import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/Button';
import { Customer, Product, Category } from '../../types';
import { Toast } from '../../components/ui/Toast';
import { generateAIImage, editAIImage, improveAIPrompt } from '../../services/aiService';
import { supabase } from '../../services/supabaseClient';
import { Lock, Save, Layout, Layers, Image as ImageIcon, Link2, Shield, Settings, Trash2, Plus, Edit2, Upload, Video, Share2, Eye, Music, Briefcase, ShoppingBag, MapPin, Search, Maximize2, X, RefreshCw, Activity, ArrowRight, UserPlus, CreditCard, Box, Zap, ShoppingCart, Loader2, GripVertical, CheckCircle2, ChevronDown, ChevronUp, Globe, Type, Palette, MessageCircle, PlayCircle, Monitor, Key, Database, Code, Bug, GitBranch, Terminal, Smartphone, Tablet, Clock, TrendingUp, Users, DollarSign, Package, Tag, ArrowLeft, History, Filter, PenTool, Radio, Sprout, Wind, Sun, Moon, CloudLightning, Droplets, Leaf, Scissors, Wand2, Star, Heart, Link as LinkIcon, Youtube, LayoutDashboard, Menu, AlertTriangle, Terminal as ActivityIcon, Trash, File, Folder, HardDrive, Download, MinusCircle, PaintBucket, Aperture, FolderOpen, Grid, List, List as ListIcon, Instagram, Sparkles, Edit, Check, Camera, LogOut, Mic, Copy, ChevronRight, Home as HomeIcon, Calendar } from 'lucide-react';
import { Home } from '../Home';
import { CalendarioDisponibilidad } from '../../components/CalendarioDisponibilidad';


// Maintainer: josferestudio@gmail.com
// Ref: 111111

interface AdminDashboardProps {
    session: any;
}

// Helper para convertir File a Base64
const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
    });
};

// Helper para convertir URL a Base64 (Para usar imágenes de galería en IA)
const urlToBase64 = async (url: string): Promise<string> => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        console.error("Error converting url to base64", e);
        throw new Error("No se pudo cargar la imagen desde la URL (posible bloqueo CORS).");
    }
};

// Helper para convertir Base64 a Blob para subir a Supabase
const base64ToBlob = async (base64: string) => {
    const res = await fetch(base64);
    return await res.blob();
};

interface GalleryImage {
    name: string;
    url: string;
    size: number; // bytes
    created_at: string;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ session }) => {
    // Usar la anon key para Edge Functions (evita 401 Invalid JWT con sesiones expiradas)
    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
    const fnHeaders = { Authorization: `Bearer ${anonKey}` };

    const [activeView, setActiveView] = useState<'overview' | 'monitor' | 'stripe' | 'theme' | 'media' | 'graphic-studio' | 'la-chacra' | 'products' | 'orders' | 'customers' | 'api' | 'tunantada-calendar'>('overview');
    const [dispSlots, setDispSlots] = useState<any[]>([]);
    const [dispLoading, setDispLoading] = useState(false);
    const [dispError, setDispError] = useState<string | null>(null);
    const dispFetchedRef = React.useRef(false);
    const [editingSlot, setEditingSlot] = useState<any>(null);
    const [slotForm, setSlotForm] = useState({ hora_inicio: '18:00', hora_fin: '22:00', estado: 'disponible', nota: '', cliente_nombre: '' });
    const [themeTab, setThemeTab] = useState<'global' | 'hero' | 'marquee' | 'bento' | 'gallery' | 'tour' | 'artist' | 'dual_cards' | 'quote' | 'featured' | 'art' | 'reviews' | 'handmade' | 'footer' | 'banner' | 'hero_weaver' | 'materials' | 'encargos'>('global');
    const [themeContext, setThemeContext] = useState<'singer' | 'weaver'>('singer');

    // DATA
    const [siteConfig, setSiteConfig] = useState<any>({});
    // Auto-seed missing config keys on load to avoid empty Theme Studio
    useEffect(() => {
        const seedDefaults = async () => {
            if (Object.keys(siteConfig).length === 0) return;
            const missing: { key: string, value: any }[] = [];
            const addIfMissing = (key: string, value: any) => {
                if (siteConfig[key] === undefined || siteConfig[key] === null) {
                    missing.push({ key, value });
                }
            };

            // LA CANTANTE defaults
            addIfMissing('hero_quote', '"La distancia no borra la identidad, la fortalece con cada ritmo que cruza el océano."');
            addIfMissing('section_title', 'El Fenómeno');
            addIfMissing('section_subtitle', 'La voz que une dos continentes. Tradición andina con alma universal.');

            // LA TEJEDORA defaults
            addIfMissing('tej_hero_title', 'Shopping con Mabelita');
            addIfMissing('tej_hero_subtitle', 'Diseño andino exclusivo tejido a mano. Tradición y moda lenta.');
            addIfMissing('tej_hero_quote', 'Llevando el calor y los colores de los Andes a cada rincón del mundo a través del fino arte textil.');
            addIfMissing('tej_hero_cta_primary_text', 'Ver Colecciones');
            addIfMissing('tej_hero_cta_primary_link', 'tienda#colecciones');
            addIfMissing('tej_hero_cta_secondary_text', 'Pedido Personalizado');
            addIfMissing('tej_hero_cta_secondary_link', 'encargos');
            addIfMissing('tej_section_title', 'El Arte');
            addIfMissing('tej_section_subtitle', 'Cada fibra hilada a mano cuenta una historia. Nuestros tejidos son mucho más que ropa: son la herencia ancestral de las comunidades andinas que ahora te abrigan.');
            addIfMissing('tej_dual_singer_title', 'Catálogo de Lujo');
            addIfMissing('tej_dual_singer_desc', 'Explora nuestra colección selecta de prendas atemporales. Sudaderas, ponchos y chalinas hiladas con la mejor lana del mundo.');
            addIfMissing('tej_dual_singer_image', 'https://images.unsplash.com/photo-1584308972272-9e4e7685e80f?auto=format&fit=crop&w=800&q=80');
            addIfMissing('tej_dual_weaver_title', 'Diseño a tu Medida');
            addIfMissing('tej_dual_weaver_desc', '¿Tienes un color, patrón o talla en mente? Cuéntame tu idea y la tejeré exclusivamente para ti.');
            addIfMissing('tej_theme_layout', 'classic');
            addIfMissing('tej_color_primary', '#A0522D');
            addIfMissing('tej_color_secondary', '#CD853F');
            addIfMissing('tej_color_accent', '#D2691E');
            // New Tejedora tabs
            addIfMissing('tej_quote_text', '"Cada prenda es un abrazo de la Pachamama, tejido con la sabiduría de generaciones."');
            addIfMissing('tej_quote_subtitle', 'Un arte que viaja desde los Andes hasta tu hogar.');
            addIfMissing('tej_featured_title', 'Obras que Cuentan Historias');
            addIfMissing('tej_featured_subtitle', 'Cada pieza es única, tejida a mano con técnicas ancestrales.');
            addIfMissing('tej_featured_cta_text', 'Ver Catálogo Completo');
            addIfMissing('tej_art_title', 'El Arte de Mabelita');
            addIfMissing('tej_art_text', 'Cada prenda que sale de mis manos lleva consigo la historia de mi pueblo, el calor de los Andes y el amor por un arte que se transmite de generación en generación.');
            // Banner defaults
            addIfMissing('banner_enabled', true);
            addIfMissing('tej_banner_enabled', true);
            addIfMissing('marquee_items', ['🎵 ESCUCHA MI ÚLTIMO SINGLE EN SPOTIFY', '✨ ENVÍOS GRATIS A ESPAÑA EN PEDIDOS +100€', '🧶 NUEVA COLECCIÓN DE INVIERNO DISPONIBLE', '🛍️ 10% DTO PRIMERA COMPRA CON CÓDIGO: MABELITA10']);
            addIfMissing('tej_marquee_items', ['🧶 NUEVA COLECCIÓN DE INVIERNO DISPONIBLE', '✨ ENVÍOS GRATIS A ESPAÑA EN PEDIDOS +100€', '🛍️ 10% DTO PRIMERA COMPRA CON CÓDIGO: MABELITA10', '🎨 TEJIDOS 100% ARTESANALES EN TELAR ANDINO']);

            if (missing.length > 0) {
                console.log(`Seeding ${missing.length} missing config defaults...`);
                await supabase.from('site_config').upsert(missing, { onConflict: 'key' });
                const { data } = await supabase.from('site_config').select('*');
                if (data) {
                    const configObj = data.reduce((acc: any, item: any) => ({ ...acc, [item.key]: item.value }), {});
                    setSiteConfig(configObj);
                }
            }
        };
        seedDefaults();
    }, [siteConfig]);

    const getThemeVal = (key: string) => {
        if (themeContext === 'weaver') {
            return siteConfig[`tej_${key}`];
        }
        return siteConfig[key];
    };

    const setThemeVal = (key: string, value: any) => {
        const targetKey = themeContext === 'weaver' ? `tej_${key}` : key;
        handleConfigChange(targetKey, value);
    };
    const [loading, setLoading] = useState(true);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    // UI STATES
    const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' | 'info' } | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isCreatingFolders, setIsCreatingFolders] = useState(false);

    // --- STRIPE DASHBOARD STATE ---
    const [stripeData, setStripeData] = useState<any>(null);
    const [isLoadingStripe, setIsLoadingStripe] = useState(false);

    // STORE MANAGEMENT STATES
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [storeFilter, setStoreFilter] = useState<'all' | 'tejidos' | 'shopping' | 'tunantada'>('all');
    const [storeSearch, setStoreSearch] = useState('');
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);

    // ORDERS STATE
    const [pedidos, setPedidos] = useState<any[]>([]);
    const [isLoadingPedidos, setIsLoadingPedidos] = useState(false);

    // CUSTOMERS STATE
    const [clientes, setClientes] = useState<any[]>([]);
    const [isLoadingClientes, setIsLoadingClientes] = useState(false);

    // CATEGORY MANAGEMENT STATES
    const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<Partial<Category> | null>(null);

    // MEDIA GALLERY STATE
    const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
    const [showGalleryModal, setShowGalleryModal] = useState(false);

    // WORKFLOW STATE (Retorno desde herramientas IA)
    const [returnToProduct, setReturnToProduct] = useState(false);

    // STATE MODIFICADO PARA GESTIONAR QUIÉN PIDE LA GALERÍA
    const [gallerySelectionMode, setGallerySelectionMode] = useState<{ mode: 'product' | 'product_main' | 'product_extra' | 'pachamanca' | 'lachacra' | 'config' | 'remover', configKey?: string } | null>(null);
    const [mediaUploadType, setMediaUploadType] = useState<'files' | 'url'>('files');
    const [socialLinks, setSocialLinks] = useState<any[]>([]);
    const [newSocialLink, setNewSocialLink] = useState({ platform: 'youtube', url: '', title: '' });

    // --- PRODUCT STATE ---
    const [previewImage, setPreviewImage] = useState<GalleryImage | null>(null);
    const [totalGallerySize, setTotalGallerySize] = useState(0);
    const [isOptimizingGalleryItem, setIsOptimizingGalleryItem] = useState<string | null>(null);

    // BUCKET MANAGEMENT
    const [activeBucket, setActiveBucket] = useState<'web-assets' | 'tejiendo' | 'shopping' | 'tunantada'>('web-assets');
    const [activeFolder, setActiveFolder] = useState<string>(''); // '' es la raíz

    // ADVANCED GALLERY STATES
    const [galleryGridSize, setGalleryGridSize] = useState<'small' | 'medium' | 'large' | 'details'>('details');
    const [subFolders, setSubFolders] = useState<any[]>([]);
    const [breadcrumb, setBreadcrumb] = useState<{ name: string, path: string }[]>([]);
    const [isDragging, setIsDragging] = useState<string | null>(null);
    const [renamingFile, setRenamingFile] = useState<string | null>(null);
    const [newFileName, setNewFileName] = useState('');
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    // WATERMARK CONFIG STATE
    const [showWmConfig, setShowWmConfig] = useState(false);
    const [wmConfig, setWmConfig] = useState(() => {
        const saved = localStorage.getItem('mabelita_wm_config');
        if (saved) try { return JSON.parse(saved); } catch { }
        return {
            mabelita: { size: 80, opacity: 25, position: 'center', logoHor: '/watermark_mabelita_hor.webp', logoVer: '/watermark_mabelita_ver.png' },
            tejidos: { size: 80, opacity: 25, position: 'center', logoHor: '/watermark_tejidos_hor.png', logoVer: '/watermark_tejidos_ver.png' }
        };
    });
    const updateWmConfig = (brand: 'mabelita' | 'tejidos', key: string, value: any) => {
        setWmConfig((prev: any) => {
            const next = { ...prev, [brand]: { ...prev[brand], [key]: value } };
            localStorage.setItem('mabelita_wm_config', JSON.stringify(next));
            return next;
        });
    };

    // Diccionario de estructura de carpetas forzadas por el administrador
    const FOLDERS_BY_BUCKET: Record<string, { id: string, label: string }[]> = {
        'web-assets': [
            { id: '', label: 'Raíz (Media General)' },
            { id: 'theme-studio', label: 'Theme Studio (Fondos, UI)' },
            { id: 'ai-generations', label: 'Generaciones IA (La Chacra)' },
            { id: 'logos', label: 'Logotipos & Branding' }
        ],
        'tejiendo': [
            { id: '', label: 'Raíz' },
            { id: 'productos', label: 'Catálogo Productos' },
            { id: 'encargos', label: 'Fotos de Encargos' }
        ],
        'shopping': [
            { id: '', label: 'Raíz' },
            { id: 'merch', label: 'Merchandising' },
            { id: 'albumes', label: 'Covers de Discos' }
        ],
        'tunantada': [
            { id: '', label: 'Raíz' },
            { id: 'productos', label: 'Productos Especiales' },
            { id: 'galeria', label: 'Galería de Eventos' }
        ]
    };

    // Cambiar de Bucket resetea la carpeta
    const handleActiveBucketChange = (bucket: 'web-assets' | 'tejiendo' | 'shopping' | 'tunantada') => {
        setActiveBucket(bucket);
        setActiveFolder('');
        setBreadcrumb([]);
    };

    // --- GRAPHIC STUDIO STATES (PACHAMANCA) ---
    const [gsAssetType, setGsAssetType] = useState<'logo' | 'banner' | 'merch' | 'social' | 'remover'>('logo');
    const [gsContext, setGsContext] = useState<'cantante' | 'tejiendo' | 'shopping'>('cantante');
    const [gsPrompt, setGsPrompt] = useState('');
    const [gsReferenceImage, setGsReferenceImage] = useState<string | null>(null); // Base64
    const [gsGeneratedImage, setGsGeneratedImage] = useState<string | null>(null);
    const [isGsGenerating, setIsGsGenerating] = useState(false);

    // --- PACHAMANCA: SILUETEADOR STATE ---
    const [bgRemoverBlob, setBgRemoverBlob] = useState<Blob | null>(null);
    const [bgRemoverUrl, setBgRemoverUrl] = useState<string | null>(null);
    const [isRemovingBg, setIsRemovingBg] = useState(false);
    const [bgRemovalStatus, setBgRemovalStatus] = useState('');

    // --- LA CHACRA (MABELITA IA STUDIO) STATES ---
    const [psWorkMode, setPsWorkMode] = useState<'seed' | 'prune'>('seed'); // seed (crear), prune (editar)
    const [psSourceImage, setPsSourceImage] = useState<string | null>(null);
    const [psMagicTool, setPsMagicTool] = useState<string>('optimizer');
    const [psAspectRatio, setPsAspectRatio] = useState<'1:1' | '16:9' | '9:16' | '4:5'>('1:1');
    const [psPrompt, setPsPrompt] = useState('');
    const [psResultImage, setPsResultImage] = useState<string | null>(null);
    const [isPsProcessing, setIsPsProcessing] = useState(false);
    const [psStats, setPsStats] = useState<{ original: number, optimized: number } | null>(null);

    // --- API MONITOR STATE ---
    const [apiLogs, setApiLogs] = useState<any[]>([]);
    const [apiStats, setApiStats] = useState({ totalRequests: 0, errorRate: 0, avgLatency: 0, totalCost: 0 });

    // --- EXPERIMENTAL: HISTORIAL IA ---
    const [creationsHistory, setCreationsHistory] = useState<any[]>([]);
    const [isImprovingPrompt, setIsImprovingPrompt] = useState(false);
    // EXPERIMENTAL: LA CHACRA MAGIC PROMPTS EDITABLES

    // EXPERIMENTAL: LA CHACRA MAGIC PROMPTS EDITABLES
    const defaultMagicPrompts: { [key: string]: string } = {
        optimizer: "Maximum web optimization. 1. Correct lighting and focus perfectly. 2. Ensure studio quality definition. 3. Reduce file size artifacts while maintaining visual fidelity. Output highly compressed but HD looking image.",
        restoration: "Fix this image for a professional website. Correct focus, sharpen details, improve lighting to studio quality, remove noise. Keep original subject exactly as is. Output HD.",
        product: "Professional product photography. Clean background, perfect studio lighting, high detail on the texture of the product. Make it look like a high-end e-commerce photo.",
        background: "Change background to: a clean, soft, neutral studio background suitable for e-commerce. Ensure perfect edge detection.",
        creative: "Creative remix. Artistic style, keeping the main subject recognizable but enhancing the atmosphere.",
        andean: "Apply a warm, earthy color grading inspired by the Peruvian Andes, terracotta tones, soft sunlight, vibrant cultural feeling. Maintain high definition.",
        fashion: "High-end editorial fashion photography style. Dramatic lighting, high contrast, sharp focus, elegant composition.",
        social: "Optimized for social media engagement. Bright, vibrant colors, centered subject, clear visual hierarchy.",
        stage: "Concert lighting atmosphere, dramatic spotlight, professional performance photography look."
    };
    const [magicPrompts, setMagicPrompts] = useState<Record<string, string>>(defaultMagicPrompts);


    useEffect(() => {
        fetchAllData();
    }, []);

    // Fetch Products and Categories when view changes to products or orders
    useEffect(() => {
        if (activeView === 'products') {
            fetchStoreData();
        } else if (activeView === 'orders') {
            fetchPedidos();
        } else if (activeView === 'customers') {
            fetchClientes();
        }
    }, [activeView]);

    // Monitor Realtime Subscription
    useEffect(() => {
        if (activeView === 'monitor') {
            fetchApiLogs();
            const channel = supabase
                .channel('api_logs_monitor')
                .on(
                    'postgres_changes',
                    { event: 'INSERT', schema: 'public', table: 'api_logs' },
                    (payload) => {
                        setApiLogs(prev => [payload.new, ...prev].slice(0, 50));
                        fetchApiLogs(); // Recalculate stats
                    }
                )
                .subscribe();
            return () => { supabase.removeChannel(channel); };
        } else if (activeView === 'stripe' && !stripeData) {
            fetchStripeDashboard();
        }
    }, [activeView]);

    const fetchApiLogs = async () => {
        const { data, error } = await supabase
            .from('api_logs')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(50);

        if (data) {
            setApiLogs(data);

            // Calc Stats (Last 50)
            const total = data.length;
            const errors = data.filter(l => l.status === 'error').length;
            const latencySum = data.reduce((acc, l) => acc + (l.duration_ms || 0), 0);
            const costSum = data.reduce((acc, l) => acc + (l.cost_estimated || 0), 0);

            setApiStats({
                totalRequests: total,
                errorRate: total > 0 ? (errors / total) * 100 : 0,
                avgLatency: total > 0 ? Math.round(latencySum / total) : 0,
                totalCost: costSum
            });
        }
    };

    const fetchAllData = async () => {
        setLoading(true);
        await Promise.all([fetchConfig(), fetchGallery(), fetchSocialLinks(), fetchCreationsHistory()]);
        setLoading(false);
    };

    const fetchCreationsHistory = async () => {
        try {
            const { data, error } = await supabase
                .from('ai_creations')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(20);

            if (error) {
                if (error.code !== '42P01') {
                    console.error("Error fetching creations history:", error);
                }
            } else {
                setCreationsHistory(data || []);
            }
        } catch (e) {
            console.error("Fetch creations history failed:", e);
        }
    };

    const fetchSocialLinks = async () => {
        const { data } = await supabase.from('social_links').select('*').order('created_at', { ascending: false });
        if (data) setSocialLinks(data);
    };

    const fetchStoreData = async () => {
        setLoading(true);
        try {
            const [cats, prods] = await Promise.all([
                supabase.from('categorias').select('*'),
                supabase.from('productos').select('*')
            ]);

            if (cats.data) {
                // Normalizar datos de categorías
                // tipo puede venir como 'Tejidos', 'tejidos', 'Shopping', 'shopping' -> normalizar a lowercase
                const mappedCats: Category[] = cats.data.map((c: any) => ({
                    id: c.id,
                    name: c.nombre,
                    slug: c.slug,
                    type: (c.tipo || '').toLowerCase().includes('shop') ? 'shopping'
                        : (c.tipo || '').toLowerCase().includes('tunantada') ? 'tunantada'
                            : 'tejidos'
                }));
                setCategories(mappedCats);
            }

            if (prods.data) {
                const mappedProducts = prods.data.map(p => ({
                    id: p.id,
                    name: p.nombre,
                    slug: p.slug,
                    description: p.descripcion,
                    price: p.precio,
                    category_id: p.categoria_id,
                    image_url: p.imagen_url || p.imagen_principal,
                    days_to_make: p.dias_elaboracion,
                    stock: p.stock,
                    is_featured: p.destacado,
                    imagenes_adicionales: p.imagenes_adicionales,
                    shipping_cost_national: p.shipping_cost_national,
                    shipping_cost_international: p.shipping_cost_international,
                    video_url: p.video_url
                }));
                setProducts(mappedProducts);
            }
        } catch (e) {
            console.error("Error fetching store data", e);
        } finally {
            setLoading(false);
        }
    };

    const fetchPedidos = async () => {
        setIsLoadingPedidos(true);
        try {
            const { data, error } = await supabase
                .from('pedidos')
                .select('*, pedido_items(*, productos(nombre, imagen_url))')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setPedidos(data || []);
        } catch (e: any) {
            console.error("Error fetching orders:", e);
            setToast({ message: 'Error cargando pedidos: ' + e.message, type: 'error' });
        } finally {
            setIsLoadingPedidos(false);
        }
    };

    const fetchClientes = async () => {
        setIsLoadingClientes(true);
        try {
            const { data, error } = await supabase
                .from('users_profiles')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) throw error;
            setClientes(data || []);
        } catch (e: any) {
            console.error("Error fetching customers:", e);
            setToast({ message: 'Error cargando clientes', type: 'error' });
        } finally {
            setIsLoadingClientes(false);
        }
    };

    // Fetch Gallery depends on activeBucket
    useEffect(() => {
        fetchGallery();
    }, [activeBucket, activeFolder]);

    const fetchGallery = async () => {
        // Limpiar estado antes de cargar
        setGalleryImages([]);
        setSubFolders([]);
        setTotalGallerySize(0);
        setSelectedItems([]);

        // Update breadcrumb visually based on activeFolder path
        if (!activeFolder) {
            setBreadcrumb([]);
        } else {
            const parts = activeFolder.split('/');
            const newBreadcrumb = parts.map((part, index) => {
                const path = parts.slice(0, index + 1).join('/');
                return { name: part, path };
            });
            setBreadcrumb(newBreadcrumb);
        }

        // Pasamos activeFolder como prefix para listar. Si es '', lista la raíz.
        const { data, error } = await supabase.storage.from(activeBucket).list(activeFolder);

        if (error) {
            console.error(`Error fetching bucket ${activeBucket}:`, error);
            if ((error as any).message?.includes("Bucket not found")) {
                setToast({ message: `Bucket '${activeBucket}' no existe. Ejecuta el SQL de configuración.`, type: 'error' });
            }
            return;
        }

        if (data) {
            // Filtrar carpetas (id === null o metadata nula) vs archivos
            const folders = data.filter(f => f.id === null);
            setSubFolders(folders.map(f => f.name));

            // Filtrar solo archivos, e ignorar archivos puramente técnicos como .keep
            const files = data.filter(f => f.id !== null && f.name !== '.keep');

            let totalSize = 0;
            const images = files.map((file: any) => {
                totalSize += (file.metadata?.size || 0);
                const filePath = activeFolder ? `${activeFolder}/${file.name}` : file.name;
                const { data: urlData } = supabase.storage.from(activeBucket).getPublicUrl(filePath);
                return {
                    name: file.name, // Supabase list(prefix) returns names without prefix inside the object
                    fullPath: filePath,
                    url: urlData.publicUrl,
                    size: file.metadata?.size || 0,
                    created_at: file.created_at
                };
            });

            // Ordenar por fecha (más reciente primero)
            images.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            setGalleryImages(images);
            setTotalGallerySize(totalSize);
        }
    };

    const fetchConfig = async () => {
        const { data } = await supabase.from('site_config').select('*');
        if (data) {
            const map: any = {};
            data.forEach(item => map[item.key] = item.value);
            setSiteConfig(map);
        }
    };

    const handleConfigChange = (key: string, value: any) => {
        setSiteConfig({ ...siteConfig, [key]: value });
    };

    const saveConfig = async (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        setIsSaving(true);
        try {
            const updates = Object.keys(siteConfig).map(key => ({
                key: key,
                value: siteConfig[key],
            }));

            const { error } = await supabase.from('site_config').upsert(updates, { onConflict: 'key' });
            if (error) throw error;

            setToast({ message: '¡Cambios guardados! Recarga la web pública para verlos.', type: 'success' });

        } catch (e: any) {
            console.error(e);
            setToast({ message: 'Error al guardar: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSyncDefaults = async () => {
        if (!window.confirm("¿Estás seguro de sincronizar los valores por defecto? Esto sobreescribirá los campos vacíos con el contenido inicial de la web.")) return;
        setIsSaving(true);
        try {
            const defaults = {
                // MabelitaLanding
                hero_title: "Mabelita de la Cruz",
                hero_subtitle: "",
                hero_quote: '"La distancia no borra la identidad. Cada hilo que tejo y cada nota que canto es un puente hacia nuestras raíces."',
                section_subtitle: "Artista Peruana en Europa",
                section_title: "La Voz del Valle del Mantaro",
                singer_bio_text: "Mabelita de la Cruz difunde el arte del Valle del Mantaro en Europa, brillando con la Tunantada, el Huaylash y el Santiago.\nDesde serenatas íntimas para pedidas de mano hasta festivales multitudinarios como las Fiestas Patrias.",
                contactTitle: "Contrata tu Show",
                contactText: "Llevo el folklore peruano a tu evento. Fiestas, bodas, festivales y más.",
                performances_title: "Mabelita en Acción",
                performances_subtitle: "Videos y fotos de actuaciones reales en festivales, fiestas y eventos por toda Europa.",
                testimonials_title: "Testimonios de mis Clientes",
                testimonials_subtitle: "Lo que dicen quienes ya han confiado en mis manos",
                repertorio: JSON.stringify([
                    "Levántate Corazón", "Mi Ángel Guardián", "Planta del Maguey",
                    "Amor de Dos Soles", "Canciones tradicionales de Huancayo", "Huaylash moderno"
                ]),
                // TejiendoFullLanding
                materials_title: "Nuestros Materiales",
                materials_subtitle: "Trabajamos con las hilanderías más prestigiosas de la región andina para garantizar prendas duraderas, suaves y espectaculares.",
                material_1_title: "Baby Alpaca 100%",
                material_1_desc: "Conocida por su extrema suavidad y ligereza. Es una fibra natural excepcional que mantiene la temperatura corporal sin abultar. Ideal para pieles sensibles.",
                material_2_title: "Oveja Merino Corriedale",
                material_2_desc: "Lana rústica pero de alta fineza, hilada a mano o en huso tradicional. Proporciona una resiliencia inigualable para abrigos gruesos y mantas pesadas.",
                material_3_title: "Teñido Natural",
                material_3_desc: "Colores extraídos de plantas, raíces e insectos andinos (como la cochinilla). Un proceso ecológico que asegura que no destiña y respeta el medio ambiente.",
                encargos_title: "Encargos a Medida",
                encargos_subtitle: "¿Tienes un diseño en mente o necesitas una talla especial? Cuéntame tu idea y tejeré una prenda exclusiva para ti.",
                gallery_subtitle: "Del telar a tu armario. Cada prenda es una obra de arte que requiere días de trabajo."
            };

            const updates: any[] = [];

            Object.entries(defaults).forEach(([key, value]) => {
                if (!siteConfig[key]) {
                    updates.push({ key, value });
                    setSiteConfig((prev: any) => ({ ...prev, [key]: value }));
                }
            });

            if (updates.length > 0) {
                const { error } = await supabase.from('site_config').upsert(updates, { onConflict: 'key' });
                if (error) throw error;
                setToast({ message: `¡Se sincronizaron ${updates.length} valores por defecto!`, type: 'success' });
            } else {
                setToast({ message: 'Todo está sincronizado, no faltan valores por defecto.', type: 'info' });
            }

        } catch (e: any) {
            console.error(e);
            setToast({ message: 'Error al sincronizar: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    // --- STORE MANAGEMENT ACTIONS ---

    const handleSaveCategory = async () => {
        if (!editingCategory || !editingCategory.name) return;
        setIsSaving(true);
        try {
            const slug = editingCategory.slug || editingCategory.name.toLowerCase().replace(/ /g, '-').replace(/[^a-z0-9-]/g, '');
            const payload = {
                nombre: editingCategory.name,
                slug: slug,
                tipo: editingCategory.type || 'tejidos'
            };

            if (editingCategory.id) {
                const { error } = await supabase.from('categorias').update(payload).eq('id', editingCategory.id);
                if (error) throw error;
            } else {
                const { error } = await supabase.from('categorias').insert(payload);
                if (error) throw error;
            }
            setToast({ message: 'Categoría guardada', type: 'success' });
            setEditingCategory(null);
            fetchStoreData(); // Refresh lists
        } catch (e: any) {
            setToast({ message: 'Error: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteCategory = async (id: number) => {
        if (!confirm('¿Eliminar categoría? Esto podría afectar a los productos asociados.')) return;
        try {
            const { error } = await supabase.from('categorias').delete().eq('id', id);
            if (error) throw error;
            setToast({ message: 'Categoría eliminada', type: 'success' });
            fetchStoreData();
        } catch (e: any) {
            setToast({ message: 'Error: ' + e.message, type: 'error' });
        }
    };

    const fetchStripeDashboard = async () => {
        setIsLoadingStripe(true);
        try {
            const { data, error } = await supabase.functions.invoke('stripe-sync', {
                body: { action: 'get_dashboard' },
                headers: fnHeaders
            });
            if (error) throw error;
            setStripeData(data);
        } catch (err: any) {
            console.error("Error fetching Stripe dashboard:", err);
            setToast({ message: `Error Stripe: ${err.message}`, type: 'error' });
        } finally {
            setIsLoadingStripe(false);
        }
    };

    const handleProductSave = async () => {
        if (!editingProduct || !editingProduct.name || !editingProduct.price) {
            setToast({ message: 'Nombre y Precio son obligatorios', type: 'error' });
            return;
        }

        setIsSaving(true);
        try {
            let finalImageUrl = editingProduct.image_url;

            const payload = {
                nombre: editingProduct.name,
                slug: editingProduct.slug || editingProduct.name?.toLowerCase().replace(/ /g, '-').replace(/[^a-z0-9-]/g, ''),
                descripcion: editingProduct.description,
                precio: editingProduct.price,
                categoria_id: editingProduct.category_id,
                imagen_url: finalImageUrl,
                stock: editingProduct.stock || 0,
                dias_elaboracion: editingProduct.days_to_make,
                destacado: editingProduct.is_featured,
                imagenes_adicionales: editingProduct.imagenes_adicionales || [],
                shipping_cost_national: editingProduct.shipping_cost_national || 0,
                shipping_cost_international: editingProduct.shipping_cost_international || 0,
                video_url: editingProduct.video_url || null
            };

            let savedProduct: Product;

            if (editingProduct.id) {
                const { data, error } = await supabase.from('productos').update(payload).eq('id', editingProduct.id).select().single();
                if (error) throw error;
                savedProduct = data;
                setToast({ message: 'Producto actualizado correctamente', type: 'success' });
            } else {
                const { data, error } = await supabase.from('productos').insert(payload).select().single();
                if (error) throw error;
                savedProduct = data;
                setToast({ message: 'Producto creado correctamente', type: 'success' });
            }

            // Sync with Stripe
            try {
                setToast({ message: 'Sincronizando con Stripe...', type: 'info' });
                const { data: stripeResponse, error: stripeError } = await supabase.functions.invoke('stripe-sync', {
                    body: {
                        action: 'sync_product',
                        payload: savedProduct
                    },
                    headers: fnHeaders
                });

                if (stripeError) {
                    console.error("Stripe sync error details:", stripeError);
                    setToast({ message: `Producto guardado, pero falló sync Stripe: ${stripeError.message || 'Error'}`, type: 'error' });
                } else if (stripeResponse?.error) {
                    setToast({ message: `Guardado. Stripe devolvió error: ${stripeResponse.error}`, type: 'error' });
                } else {
                    // ACTUALIZAR BD LOCAL CON LOS IDS DE STRIPE
                    if (stripeResponse?.stripe_product_id && stripeResponse?.stripe_price_id) {
                        await supabase.from('productos').update({
                            stripe_product_id: stripeResponse.stripe_product_id,
                            stripe_price_id: stripeResponse.stripe_price_id
                        }).eq('id', savedProduct.id);
                    }
                    setToast({ message: 'Producto guardado y sincronizado con Stripe exitosamente', type: 'success' });
                }

            } catch (e) {
                console.error("Failed to call edge function", e);
                setToast({ message: 'Producto guardado. Sincronización Stripe falló por red.', type: 'error' });
            }

            await fetchStoreData();
            setIsProductModalOpen(false);
            setEditingProduct(null);

        } catch (e: any) {
            console.error(e);
            setToast({ message: 'Error al guardar producto: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSyncAllProducts = async () => {
        setIsSaving(true);
        setToast({ message: 'Sincronizando catálogo completo con Stripe...', type: 'info' });
        try {
            const { data: stripeResults, error: stripeError } = await supabase.functions.invoke('stripe-sync', {
                body: { action: 'sync_all' },
                headers: fnHeaders
            });

            if (stripeError) throw stripeError;
            if (stripeResults?.error) throw new Error(stripeResults.error);

            setToast({ message: 'Sincronización masiva completada con éxito', type: 'success' });
            await fetchStoreData();
        } catch (e: any) {
            console.error("Bulk sync error:", e);
            setToast({ message: 'Fallo en sincronización masiva: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleProductDelete = async (id: number) => {
        if (!confirm('¿Estás seguro de eliminar este producto? Esta acción no se puede deshacer.')) return;

        try {
            const { error } = await supabase.from('productos').delete().eq('id', id);
            if (error) throw error;

            setToast({ message: 'Producto eliminado', type: 'success' });
            fetchStoreData();
        } catch (e: any) {
            setToast({ message: 'Error al eliminar: ' + e.message, type: 'error' });
        }
    };

    const removeExtraImage = (index: number) => {
        setEditingProduct(prev => {
            if (!prev) return null;
            const newImages = [...(prev.imagenes_adicionales || [])];
            newImages.splice(index, 1);
            return { ...prev, imagenes_adicionales: newImages };
        });
    };

    // --- WORKFLOW REDIRECTION (Product -> IA -> Gallery -> Product) ---

    const handleNavigateToTool = (view: 'la-chacra' | 'graphic-studio') => {
        setReturnToProduct(true);
        setIsProductModalOpen(false);
        setActiveView(view);
    };

    // --- GALLERY ACTIONS ---

    const handleCreateStorageStructure = async () => {
        setIsCreatingFolders(true);
        try {
            const emptyBlob = new Blob([''], { type: 'text/plain' });
            for (const bucket of Object.keys(FOLDERS_BY_BUCKET)) {
                for (const folder of FOLDERS_BY_BUCKET[bucket]) {
                    const filePath = `${folder.id}/.keep`;
                    await supabase.storage.from(bucket).upload(filePath, emptyBlob, { upsert: true });
                }
            }
            alert("¡Carpetas inicializadas correctamente!");
            if (activeView === 'media') fetchGallery();
        } catch (error) {
            console.error(error);
            alert("Hubo un error inicializando las carpetas.");
        } finally {
            setIsCreatingFolders(false);
        }
    };

    const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        setIsSaving(true);
        const files = Array.from(e.target.files) as File[];
        let successCount = 0;
        let errorCount = 0;

        for (const file of files) {
            try {
                const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
                const fileName = `${Date.now()}-${cleanName}`;
                const uploadPath = activeFolder ? `${activeFolder}/${fileName}` : fileName;
                const { error } = await supabase.storage.from(activeBucket).upload(uploadPath, file);
                if (error) throw error;
                successCount++;
            } catch (err) {
                console.error("Error subiendo archivo:", file.name, err);
                errorCount++;
            }
        }

        setIsSaving(false);
        if (successCount > 0) {
            setToast({ message: `${successCount} archivos subidos exitosamente.`, type: 'success' });
            fetchGallery();
        }
        if (errorCount > 0) {
            setToast({ message: `${errorCount} archivos fallaron al subir.`, type: 'error' });
        }
    };

    const handleDeleteImage = async (imageNames: string | string[]) => {
        const names = Array.isArray(imageNames) ? imageNames : [imageNames];
        const isMulti = names.length > 1;

        if (!window.confirm(`¿Estás seguro de eliminar ${isMulti ? `las ${names.length} imágenes seleccionadas` : `la imagen "${names[0]}"`} permanentemente?`)) return;

        try {
            const pathsToRemove = names.map(n => activeFolder ? `${activeFolder}/${n}` : n);
            const { error } = await supabase.storage.from(activeBucket).remove(pathsToRemove);

            if (error) {
                console.error('Error deleting:', error);
                throw error;
            }

            setToast({ message: isMulti ? `${names.length} imágenes eliminadas correctamente` : 'Imagen eliminada correctamente', type: 'success' });
            setGalleryImages(prev => prev.filter(img => !names.includes(img.name)));
            setSelectedItems([]);

            if (previewImage && names.includes(previewImage.name)) {
                setPreviewImage(null);
            }

            setTimeout(() => {
                fetchGallery();
            }, 500);

        } catch (e: any) {
            console.error("Delete error:", e);
            setToast({ message: 'Error al eliminar: ' + (e.message || e.error_description || "Revisa los permisos SQL"), type: 'error' });
        }
    };

    const handleCreateFolder = async () => {
        const folderName = prompt('Introduce el nombre de la nueva carpeta:');
        if (!folderName || folderName.trim() === '') return;

        // Sanitize
        const safeName = folderName.trim().replace(/[^a-zA-Z0-9_-]/g, '_');

        // setIsCreatingFolder(true); // This state variable is not defined in the provided context, commenting out
        try {
            // Un archivo vacío subido dentro del path crea físicamente el prefijo (carpeta) en Supabase
            const path = activeFolder ? `${activeFolder}/${safeName}/.keep` : `${safeName}/.keep`;
            const emptyBlob = new Blob([''], { type: 'text/plain' });
            const { error } = await supabase.storage.from(activeBucket).upload(path, emptyBlob);

            if (error) throw error;

            setToast({ message: `Carpeta '${safeName}' creada`, type: 'success' });
            fetchGallery();
        } catch (e: any) {
            setToast({ message: 'Error creando la carpeta: ' + e.message, type: 'error' });
        } finally {
            // setIsCreatingFolder(false); // This state variable is not defined in the provided context, commenting out
        }
    };

    const handleDeleteFolder = async (folderName: string) => {
        if (!window.confirm(`¿Seguro que quieres borrar la carpeta "${folderName}"? TODO LO QUE ESTÉ DENTRO SE PERDERÁ.`)) return;

        try {
            const folderPath = activeFolder ? `${activeFolder}/${folderName}` : folderName;

            // Supabase requires listing and deleting all files inside the folder first
            const { data: files } = await supabase.storage.from(activeBucket).list(folderPath, { limit: 100 });

            if (files && files.length > 0) {
                const filePaths = files.map(f => `${folderPath}/${f.name}`);
                const { error: deleteFilesError } = await supabase.storage.from(activeBucket).remove(filePaths);
                if (deleteFilesError) throw deleteFilesError;
            }

            // After files are deleted (or if it was empty, especially the .keep), just refresh
            // Sometimes it leaves an empty prefix until the cache clears, but the files are gone.
            setToast({ message: 'Carpeta borrada', type: 'success' });
            fetchGallery();
        } catch (e: any) {
            setToast({ message: 'Error borrando carpeta: ' + e.message, type: 'error' });
        }
    };

    const handleRenameFile = async (oldName: string, newName: string) => {
        // Assuming setRenamingFile is a state setter, it's not defined in the provided context, commenting out
        // if (!newName || newName.trim() === '' || oldName === newName) {
        //     setRenamingFile(null);
        //     return;
        // }

        try {
            // Keep extension if the user removed it by accident
            const oldExt = oldName.split('.').pop() || '';
            const newHasExt = newName.includes('.');
            const finalNewName = newHasExt ? newName : `${newName}.${oldExt}`;

            const oldPath = activeFolder ? `${activeFolder}/${oldName}` : oldName;
            const newPath = activeFolder ? `${activeFolder}/${finalNewName}` : finalNewName;

            const { error } = await supabase.storage.from(activeBucket).move(oldPath, newPath);
            if (error) throw error;
            setToast({ message: 'Archivo renombrado', type: 'success' });
            // setRenamingFile(null); // Commented out as setRenamingFile is not defined
            fetchGallery();
        } catch (e: any) {
            setToast({ message: 'Error renombrando: ' + e.message, type: 'error' });
        }
    };

    const handleMoveFiles = async (fileNames: string[], targetFolderPath: string) => {
        // Drop destination resolving
        let destFolder = '';
        if (targetFolderPath === 'root') {
            destFolder = '';
        } else {
            destFolder = activeFolder ? `${activeFolder}/${targetFolderPath}` : targetFolderPath;
        }

        let movedCount = 0;
        setIsSaving(true);
        try {
            for (const fileName of fileNames) {
                const sourcePath = activeFolder ? `${activeFolder}/${fileName}` : fileName;
                const destPath = destFolder ? `${destFolder}/${fileName}` : fileName;

                if (sourcePath === destPath) continue; // Dropped on same folder

                const { error } = await supabase.storage.from(activeBucket).move(sourcePath, destPath);
                if (!error) movedCount++;
            }

            if (movedCount > 0) {
                setToast({ message: `${movedCount} archivo(s) movido(s) a ${destFolder || 'Raíz'}`, type: 'success' });
            }
        } catch (e: any) {
            setToast({ message: 'Error moviendo archivos: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
            fetchGallery();
        }
    };

    const handleCrossBucketMove = async (e: React.DragEvent, targetBucket: string) => {
        try {
            const rawData = e.dataTransfer.getData('application/json');
            if (!rawData) return;

            const dragData = JSON.parse(rawData);
            // Si el objeto no tiene el formato nuevo, tratar de adaptar
            let fileNames: string[] = [];
            let sourceBkt = activeBucket;
            let sourceFolder = activeFolder;

            if (Array.isArray(dragData)) {
                fileNames = dragData;
            } else if (dragData.files && Array.isArray(dragData.files)) {
                fileNames = dragData.files;
                sourceBkt = dragData.sourceBucket || activeBucket;
                sourceFolder = dragData.sourcePath || '';
            }

            if (fileNames.length === 0) return;
            if (sourceBkt === targetBucket) return; // Prevent same bucket

            if (!window.confirm(`¿Mover ${fileNames.length} foto(s) al bucket "${targetBucket}"? Esto copiará y borrará cada archivo, puede tardar unos segundos.`)) return;

            setIsSaving(true);
            let movedCount = 0;

            for (const fileName of fileNames) {
                const fullSourcePath = sourceFolder ? `${sourceFolder}/${fileName}` : fileName;

                // 1. Download blob
                const { data: blob, error: dlError } = await supabase.storage.from(sourceBkt).download(fullSourcePath);
                if (dlError || !blob) {
                    console.error("Error bajando:", dlError);
                    continue;
                }

                // 2. Upload blob to target root
                const ext = fileName.split('.').pop();
                const cleanName = fileName.replace(`.${ext}`, ''); // Removing extension if it is there
                const newPath = `${cleanName}-${Date.now().toString().slice(-4)}.${ext}`; // Add timestamp to avoid native collision at root

                const { error: ulError } = await supabase.storage.from(targetBucket).upload(newPath, blob);
                if (ulError) {
                    console.error("Error subiendo:", ulError);
                    continue;
                }

                // 3. Delete from source
                await supabase.storage.from(sourceBkt).remove([fullSourcePath]);
                movedCount++;
            }

            if (movedCount > 0) {
                setToast({ message: `${movedCount} fotos transferidas a ${targetBucket}`, type: 'success' });
                setSelectedItems([]);
                fetchGallery();
            }

        } catch (err: any) {
            setToast({ message: 'Error cruzando buckets: ' + err.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDrop = async (e: React.DragEvent, targetFolderPath: string) => {
        e.preventDefault();
        try {
            const rawData = e.dataTransfer.getData('application/json');
            if (rawData) {
                const dragData = JSON.parse(rawData);
                // Extraer los archivos, ya sea directamente del Array viejo, o del objeto nuevo
                let filesToMove: string[] = [];
                if (Array.isArray(dragData)) {
                    filesToMove = dragData;
                } else if (dragData.files && Array.isArray(dragData.files)) {
                    filesToMove = dragData.files;
                }

                if (filesToMove.length > 0) {
                    // Solo permitir drop interno si el sourceBucket original es el activeBucket
                    if (!dragData.sourceBucket || dragData.sourceBucket === activeBucket) {
                        await handleMoveFiles(filesToMove, targetFolderPath);
                        setSelectedItems([]);
                    } else {
                        setToast({ message: 'No se puede arrastrar directamente entre carpetas de distintos Buckets aquí. Arrastra a las pestañas superiores.', type: 'error' });
                    }
                }
            } else {
                const draggedFile = e.dataTransfer.getData('text/plain');
                if (draggedFile) {
                    await handleMoveFiles([draggedFile], targetFolderPath);
                }
            }
        } catch (error) {
            console.error("Error drop parse", error);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault(); // Necessario para el Drop
    };

    const handleGalleryOptimize = async (image: GalleryImage, watermarkType: 'none' | 'mabelita' | 'tejidos' = 'none') => {
        setIsOptimizingGalleryItem(image.name);
        try {
            const response = await fetch(image.url);
            const blob = await response.blob();

            const optimizedBlob = await new Promise<Blob>((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const width = img.width;
                    const height = img.height;

                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');

                    if (!ctx) {
                        reject(new Error("No se pudo inicializar el contexto canvas"));
                        return;
                    }

                    ctx.drawImage(img, 0, 0, width, height);

                    const proceedWithExport = () => {
                        try {
                            canvas.toBlob((b) => {
                                if (b) resolve(b);
                                else reject(new Error("Error comprimiendo a WebP"));
                            }, 'image/webp', 0.8);
                        } catch (err: any) {
                            reject(new Error("Error de CORS (Canvas Contaminado). Por favor configure los headers CORS en Supabase Storage."));
                        }
                    };

                    if (watermarkType !== 'none') {
                        const isVertical = height > width;
                        const cfg = wmConfig[watermarkType];
                        const wmSrc = isVertical ? cfg.logoVer : cfg.logoHor;

                        const watermark = new Image();
                        watermark.onload = () => {
                            const natW = watermark.naturalWidth || 1;
                            const natH = watermark.naturalHeight || 1;
                            const sizeScale = (cfg.size || 80) / 100;

                            // Calculate watermark dimensions based on size %
                            let wmWidth = 0;
                            let wmHeight = 0;
                            if (isVertical) {
                                wmHeight = height * sizeScale;
                                wmWidth = wmHeight * (natW / natH);
                            } else {
                                wmWidth = width * sizeScale;
                                wmHeight = wmWidth * (natH / natW);
                            }

                            // Calculate position based on config
                            let x = 0;
                            let y = 0;
                            const pos = cfg.position || 'center';
                            const pad = Math.min(width, height) * 0.02; // 2% padding

                            if (pos.includes('left')) x = pad;
                            else if (pos.includes('right')) x = width - wmWidth - pad;
                            else x = (width - wmWidth) / 2; // center

                            if (pos.startsWith('top')) y = pad;
                            else if (pos.startsWith('bottom')) y = height - wmHeight - pad;
                            else y = (height - wmHeight) / 2; // center

                            const opacity = (cfg.opacity || 25) / 100;
                            ctx.globalAlpha = opacity;
                            ctx.drawImage(watermark, x, y, wmWidth, wmHeight);
                            ctx.globalAlpha = 1.0;

                            proceedWithExport();
                        };
                        watermark.onerror = () => {
                            setToast({ message: `Error al cargar la marca de agua (${wmSrc}).`, type: 'error' });
                            reject(new Error(`Fallo al cargar ${wmSrc}`));
                        };
                        watermark.src = wmSrc;
                    } else {
                        proceedWithExport();
                    }
                };
                img.onerror = (e) => reject(e);
                img.src = URL.createObjectURL(blob);
            });

            const nameWithoutExt = image.name.replace(/\.[^/.]+$/, "");
            const cleanName = nameWithoutExt.replace(/[^a-zA-Z0-9-_]/g, '_');
            let suffix = '';
            if (watermarkType === 'mabelita') suffix = '-wm-mabelita';
            else if (watermarkType === 'tejidos') suffix = '-wm-tejidos';

            const fileName = `${cleanName}${suffix}.webp`;

            const { error } = await supabase.storage.from(activeBucket).upload(fileName, optimizedBlob, {
                upsert: true,
                contentType: 'image/webp'
            });
            if (error) throw error;

            if (image.name !== fileName) {
                await supabase.storage.from(activeBucket).remove([image.name]);
            }

            setToast({ message: '¡Imagen optimizada y sobrescrita (WebP/1600px Q:0.5)!', type: 'success' });

            if (previewImage?.name === image.name) {
                setPreviewImage(null);
            }

            fetchGallery();

        } catch (e: any) {
            console.error("Optimization error:", e);
            setToast({ message: e.message || "Error al optimizar imagen. Compruebe los headers de CORS en Supabase.", type: 'error' });
        } finally {
            setIsOptimizingGalleryItem(null);
        }
    };

    // SOCIAL LINKS HANDLERS
    const handleAddSocialLink = async () => {
        if (!newSocialLink.url) return;
        setIsSaving(true);
        try {
            const { error } = await supabase.from('social_links').insert([newSocialLink]);
            if (error) throw error;
            setToast({ message: 'Enlace añadido', type: 'success' });
            setNewSocialLink({ platform: 'youtube', url: '', title: '' });
            fetchSocialLinks();
        } catch (e: any) {
            setToast({ message: 'Error: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteSocialLink = async (id: number) => {
        try {
            const { error } = await supabase.from('social_links').delete().eq('id', id);
            if (error) throw error;
            setToast({ message: 'Enlace eliminado', type: 'success' });
            fetchSocialLinks();
        } catch (e: any) {
            setToast({ message: 'Error: ' + e.message, type: 'error' });
        }
    };

    const handleToggleFeaturedSocialLink = async (id: number, currentStatus: boolean) => {
        try {
            if (!currentStatus) {
                await supabase.from('social_links').update({ is_featured: false }).neq('id', 0);
            }
            const { error } = await supabase.from('social_links').update({ is_featured: !currentStatus }).eq('id', id);
            if (error) throw error;
            fetchSocialLinks();
        } catch (e: any) {
            setToast({ message: 'Error: ' + e.message, type: 'error' });
        }
    }

    // --- NEW: LOGICA UNIFICADA DE SELECCIÓN DE GALERÍA ---

    const selectImageFromGallery = async (url: string) => {
        // No cerrar modal si estamos en modo selección múltiple
        if (gallerySelectionMode?.mode !== 'product_extra') {
            setShowGalleryModal(false);
        }

        if (gallerySelectionMode?.mode === 'config' && gallerySelectionMode.configKey) {
            handleConfigChange(gallerySelectionMode.configKey, url);
            setToast({ message: 'Imagen aplicada correctamente', type: 'success' });
        } else if (gallerySelectionMode?.mode === 'product') {
            setEditingProduct(prev => ({ ...prev, image_url: url }));
            setToast({ message: 'Imagen de producto seleccionada', type: 'success' });
        } else if (gallerySelectionMode?.mode === 'product_extra') {
            setEditingProduct(prev => ({
                ...prev,
                imagenes_adicionales: [...(prev?.imagenes_adicionales || []), url]
            }));
            setToast({ message: 'Imagen añadida a la galería del producto', type: 'success' });
        } else if (gallerySelectionMode?.mode === 'pachamanca') {
            try {
                const b64 = await urlToBase64(url);
                setGsReferenceImage(b64);
                setToast({ message: 'Referencia cargada desde galería', type: 'success' });
            } catch (e: any) {
                setToast({ message: 'Error al cargar desde galería: ' + e.message, type: 'error' });
            }
        } else if (gallerySelectionMode?.mode === 'lachacra') {
            try {
                const b64 = await urlToBase64(url);
                setPsSourceImage(b64);
                setPsWorkMode('prune');
                setPsMagicTool('optimizer');
                setToast({ message: 'Imagen lista para cultivo en La Chacra', type: 'success' });
            } catch (e: any) {
                setToast({ message: 'Error al cargar desde galería: ' + e.message, type: 'error' });
            }
        } else if (gallerySelectionMode?.mode === 'remover') {
            try {
                // Remove.bg servers sometimes fail to fetch supabase URLs due to missing User-Agents or anti-bot protections.
                // We fetch it locally into a Blob first, then upload it.
                const response = await fetch(url);
                const blob = await response.blob();
                performBgRemoval(blob);
                setToast({ message: 'Imagen de galería preparada y enviada al silueteador', type: 'success' });
            } catch (e: any) {
                setToast({ message: 'Error procesando la imagen: ' + e.message, type: 'error' });
            }
        } else {
            navigator.clipboard.writeText(url);
            setToast({ message: 'URL copiada al portapapeles', type: 'success' });
        }

        if (gallerySelectionMode?.mode !== 'product_extra') {
            setGallerySelectionMode(null);
        }
    };

    const openGalleryForConfig = (key: string) => {
        setGallerySelectionMode({ mode: 'config', configKey: key });
        setActiveBucket('web-assets');
        setShowGalleryModal(true);
        if (galleryImages.length === 0) fetchGallery();
    };

    const openGalleryForProduct = () => {
        const category = categories.find(c => c.id === editingProduct?.category_id);
        const targetBucket = category?.type === 'shopping' ? 'shopping' : 'tejiendo';

        setGallerySelectionMode({ mode: 'product' });
        setActiveBucket(targetBucket);
        setShowGalleryModal(true);
        fetchGallery(); // Fetch immediately as bucket might have changed
    };

    const openGalleryForProductExtra = () => {
        const category = categories.find(c => c.id === editingProduct?.category_id);
        const targetBucket = category?.type === 'shopping' ? 'shopping' : 'tejiendo';

        setGallerySelectionMode({ mode: 'product_extra' });
        setActiveBucket(targetBucket);
        setShowGalleryModal(true);
        fetchGallery();
    };

    const openGalleryForPachamanca = () => {
        setGallerySelectionMode({ mode: 'pachamanca' });
        setActiveBucket('web-assets');
        setShowGalleryModal(true);
        if (galleryImages.length === 0) fetchGallery();
    };

    const openGalleryForLaChacra = () => {
        setGallerySelectionMode({ mode: 'lachacra' });
        setActiveBucket('web-assets');
        setShowGalleryModal(true);
        if (galleryImages.length === 0) fetchGallery();
    };

    const openGalleryForRemover = () => {
        setGallerySelectionMode({ mode: 'remover' });
        setActiveBucket('web-assets');
        setShowGalleryModal(true);
        if (galleryImages.length === 0) fetchGallery();
    };

    // --- GRAPHIC STUDIO LOGIC (PACHAMANCA) ---

    // 1. Silueteador Logic (Bg Remover)
    const cleanBgRemoverMemory = () => {
        if (bgRemoverUrl) {
            URL.revokeObjectURL(bgRemoverUrl);
            setBgRemoverUrl(null);
        }
        setBgRemoverBlob(null);
    };

    // Refactorizado para aceptar Blob. Fetch local previene bloqueos de CORS/Anti-bot del lado de Remove.bg
    const performBgRemoval = async (input: Blob) => {
        cleanBgRemoverMemory();
        setIsRemovingBg(true);
        setBgRemovalStatus("⏳ Conectando con API de Silueteado...");

        try {
            const API_KEY = process.env.REACT_APP_REMOVE_BG_KEY || process.env.API_KEY;
            if (!API_KEY) {
                throw new Error("No hay API Key configurada para Remove.bg.");
            }

            const formData = new FormData();
            const filename = (input as File).name || 'gallery_image.png';
            formData.append('image_file', input, filename);
            formData.append('size', 'auto');

            const response = await fetch('https://api.remove.bg/v1.0/removebg', {
                method: 'POST',
                headers: { 'X-Api-Key': API_KEY },
                body: formData
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.errors?.[0]?.title || 'Error al procesar imagen en el servidor');
            }

            const blob = await response.blob();

            setBgRemoverBlob(blob);
            setBgRemoverUrl(URL.createObjectURL(blob));
            setBgRemovalStatus("✅ Fondo eliminado. Listo para descargar o guardar.");

        } catch (error: any) {
            console.error(error);
            setBgRemovalStatus("❌ Error: " + (error.message || "Fallo desconocido"));
        } finally {
            setIsRemovingBg(false);
        }
    };

    const handleBgRemovalUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            performBgRemoval(file);
        }
    };

    const downloadBgRemoved = () => {
        if (!bgRemoverBlob) return;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(bgRemoverBlob);
        link.download = 'silueta_mabelita.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const saveBgRemovedToBucket = async () => {
        if (!bgRemoverBlob) return;
        setIsSaving(true);
        try {
            const fileName = `silueta-${Date.now()}.png`;
            const { error } = await supabase.storage.from(activeBucket).upload(fileName, bgRemoverBlob);
            if (error) throw error;
            setToast({ message: `Silueta guardada en ${activeBucket}`, type: 'success' });
            fetchGallery();

            if (returnToProduct) {
                setActiveView('products');
                setIsProductModalOpen(true);
                setReturnToProduct(false);
                setTimeout(() => openGalleryForProduct(), 500);
            }

        } catch (e: any) {
            setToast({ message: 'Error guardando: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };


    const handleGsReferenceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const base64 = await fileToBase64(e.target.files[0]);
            setGsReferenceImage(base64);
        }
    };

    const handleGsGenerate = async () => {
        if (!gsPrompt) return setToast({ message: 'Describe tu diseño primero', type: 'info' });

        setIsGsGenerating(true);
        setGsGeneratedImage(null);

        try {
            let contextKeywords = "";
            let colorPalette = `Colores: ${siteConfig.primary_color}, ${siteConfig.accent_color}`;

            if (gsContext === 'cantante') {
                contextKeywords = "Estilo elegante, folklore peruano, música, escenario, andino, cultural.";
            } else if (gsContext === 'tejiendo') {
                contextKeywords = "Artesanal, lana, texturas suaves, tejido a mano, natural, orgánico.";
            } else if (gsContext === 'shopping') {
                contextKeywords = "Comercial, vibrante, moderno, merchandising, atractivo para fans, retail.";
            }

            const fullPrompt = `Diseño de ${gsAssetType}. Contexto de marca: ${contextKeywords}. ${colorPalette}. Instrucción específica: ${gsPrompt}. Alta calidad, profesional.`;

            let result: string | null = null;

            if (gsReferenceImage) {
                result = await editAIImage(gsReferenceImage, fullPrompt + " Usa la imagen de referencia para la composición o estructura pero aplica el estilo descrito.");
            } else {
                result = await generateAIImage(fullPrompt);
            }

            setGsGeneratedImage(result);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setToast({ message: '¡Diseño creado en Pachamanca!', type: 'success' });

        } catch (e: any) {
            setToast({ message: 'Error generando: ' + e.message, type: 'error' });
        } finally {
            setIsGsGenerating(false);
        }
    };

    const saveGsImage = async () => {
        if (!gsGeneratedImage || isSaving) return;
        setIsSaving(true);
        try {
            const publicUrl = await uploadBase64(gsGeneratedImage, 'web-assets', `pachamanca-${gsAssetType}`);

            // Save to History using URL
            try {
                await supabase.from('ai_creations').insert([{
                    tool: 'pachamanca',
                    prompt: gsPrompt,
                    image_url: publicUrl,
                    parameters: { type: gsAssetType, context: gsContext, hasReference: !!gsReferenceImage }
                }]);
                fetchCreationsHistory();
            } catch (e) {
                console.error("Error saving strictly to history:", e);
            }

            setToast({ message: `Diseño de Pachamanca guardado`, type: 'success' });

            if (returnToProduct) {
                setActiveView('products');
                setIsProductModalOpen(true);
                setReturnToProduct(false);
                setTimeout(() => openGalleryForProduct(), 500);
            } else {
                setActiveBucket('web-assets');
                setActiveView('media');
                fetchGallery();
            }
        } catch (e: any) {
            setToast({ message: 'Error guardando diseño: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };


    // --- EXPERIMENTAL: IMPROVE PROMPT LOGIC ---
    const handleImprovePrompt = async (tool: 'pachamanca' | 'lachacra') => {
        const currentPrompt = tool === 'pachamanca' ? gsPrompt : psPrompt;
        if (!currentPrompt) return setToast({ message: 'Escribe una idea básica primero', type: 'info' });

        setIsImprovingPrompt(true);
        try {
            const context = tool === 'pachamanca' ? `Creando un ${gsAssetType} para la marca. Contexto: ${gsContext}` : `Editando foto. Modo: ${psWorkMode}. Herramienta: ${psMagicTool}`;
            const improved = await improveAIPrompt(currentPrompt, context);

            if (tool === 'pachamanca') setGsPrompt(improved);
            else setPsPrompt(improved);

            setToast({ message: '✨ Prompt optimizado con IA', type: 'success' });
        } catch (e: any) {
            setToast({ message: 'Error optimizando prompt', type: 'error' });
        } finally {
            setIsImprovingPrompt(false);
        }
    };


    // --- LA CHACRA (MABELITA IA) LOGIC ---

    const handlePsUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const base64 = await fileToBase64(e.target.files[0]);
            setPsSourceImage(base64);
            setPsResultImage(null);
            setPsStats(null);
            setPsWorkMode('prune');
            setPsMagicTool('optimizer');
        }
    };

    const handlePsProcess = async () => {
        if (psWorkMode === 'prune' && !psSourceImage) {
            return setToast({ message: 'Debes subir una imagen para editarla', type: 'info' });
        }
        if (!psPrompt && (psMagicTool === 'creative' || psWorkMode === 'seed')) {
            return setToast({ message: 'Por favor describe lo que quieres crear', type: 'info' });
        }

        setIsPsProcessing(true);
        setPsResultImage(null);
        setPsStats(null);

        try {
            let fullPrompt = "";
            const ratioInstruction = `Aspect Ratio: ${psAspectRatio}.`;


            if (psWorkMode === 'seed') {
                fullPrompt = `${psPrompt}. Style: ${psMagicTool}. ${ratioInstruction} High quality, photorealistic.`;
                const result = await generateAIImage(fullPrompt);
                setPsResultImage(result);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                let basePrompt = magicPrompts[psMagicTool] || magicPrompts['optimizer'];
                if (psPrompt && psMagicTool !== 'background' && psMagicTool !== 'creative' && psMagicTool !== 'optimizer') {
                    basePrompt += ` Additional instruction: ${psPrompt}`;
                }

                // For tools like background/creative where prompt is injected directly
                if (psMagicTool === 'background' && psPrompt) {
                    basePrompt = basePrompt.replace('a clean, soft, neutral studio background suitable for e-commerce', psPrompt);
                } else if (psMagicTool === 'creative' && psPrompt) {
                    basePrompt = basePrompt.replace('Creative remix.', `Creative remix. ${psPrompt}.`);
                }

                fullPrompt = `${basePrompt} ${ratioInstruction}`;

                if (!psSourceImage) throw new Error("No hay imagen origen");
                const result = await editAIImage(psSourceImage, fullPrompt);
                setPsResultImage(result);
                window.scrollTo({ top: 0, behavior: 'smooth' });

                if (result) {
                    const originalSize = Math.round(psSourceImage.length * 0.75);
                    const optimizedSize = Math.round(result.length * 0.75 * (psMagicTool === 'optimizer' ? 0.4 : 0.8));

                    setPsStats({
                        original: originalSize,
                        optimized: optimizedSize
                    });
                }
            }

            setToast({ message: '¡Cosecha lista en La Chacra!', type: 'success' });

        } catch (e: any) {
            setToast({ message: 'Error en La Chacra: ' + e.message, type: 'error' });
        } finally {
            setIsPsProcessing(false);
        }
    };

    const savePsImage = async () => {
        if (!psResultImage || isSaving) return;
        setIsSaving(true);
        let targetBucket = activeBucket;
        if (psMagicTool === 'product' && activeBucket === 'web-assets') targetBucket = 'tejiendo';

        try {
            const publicUrl = await uploadBase64(psResultImage, activeBucket, `lachacra-${psMagicTool}`);

            // Save to History using URL
            try {
                await supabase.from('ai_creations').insert([{
                    tool: 'lachacra',
                    prompt: psPrompt,
                    image_url: publicUrl,
                    parameters: { mode: psWorkMode, tool: psMagicTool, ratio: psAspectRatio, hasSource: !!psSourceImage }
                }]);
                fetchCreationsHistory();
            } catch (e) {
                console.error("Error saving to history:", e);
            }

            setToast({ message: `Imagen de La Chacra guardada`, type: 'success' });

            // Si no estamos volviendo a un producto, abrimos la galería
            if (returnToProduct) {
                setActiveView('products');
                setIsProductModalOpen(true);
                setReturnToProduct(false);
                setTimeout(() => openGalleryForProduct(), 500);
            } else {
                setActiveBucket(activeBucket);
                setActiveView('media');
                fetchGallery();
            }

        } catch (e: any) {
            setToast({ message: 'Error guardando: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    // --- COMMON SAVE LOGIC ---
    const uploadBase64 = async (base64: string, bucket: string, prefix: string): Promise<string> => {
        const blob = await base64ToBlob(base64);
        const fileName = `${prefix}-${Date.now()}.png`;
        const { error } = await supabase.storage.from(bucket).upload(fileName, blob);
        if (error) throw error;

        const { data } = supabase.storage.from(bucket).getPublicUrl(fileName);
        return data.publicUrl;
    };

    const saveBase64ToBucket = async (base64: string, bucket: string, prefix: string) => {
        setIsSaving(true);
        try {
            await uploadBase64(base64, bucket, prefix);

            setToast({ message: `Guardado en ${bucket}`, type: 'success' });

            if (returnToProduct) {
                setActiveView('products');
                setIsProductModalOpen(true);
                setReturnToProduct(false);
                setTimeout(() => openGalleryForProduct(), 500);
                return;
            }

            setActiveBucket(bucket as any);
            setActiveView('media');
            await fetchGallery();
        } catch (e: any) {
            setToast({ message: 'Error guardando: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    // --- RENDERERS ---

    const handleUpdatePedidoStatus = async (id: number, newStatus: string) => {
        setIsSaving(true);
        try {
            const { error } = await supabase
                .from('pedidos')
                .update({ estado: newStatus })
                .eq('id', id);

            if (error) throw error;

            setToast({ message: 'Estado del pedido actualizado a ' + newStatus, type: 'success' });
            fetchPedidos(); // Recargar pedidos
        } catch (e: any) {
            console.error("Error updating order status:", e);
            setToast({ message: 'Error al actualizar el estado: ' + e.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const renderPedidos = () => {
        if (isLoadingPedidos) return <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>;

        return (
            <div className="animate-in fade-in space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Gestión de Pedidos</h2>
                        <p className="text-gray-500 text-sm">Registro de compras (Webhook Stripe)</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-full text-blue-600"><Briefcase size={24} /></div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                    <th className="p-4">Fecha</th>
                                    <th className="p-4">Cliente</th>
                                    <th className="p-4">Total</th>
                                    <th className="p-4">Estado</th>
                                    <th className="p-4">Items / Dirección</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {pedidos.map(pedido => (
                                    <tr key={pedido.id} className="hover:bg-gray-50 p-4 align-top">
                                        <td className="p-4 text-sm text-gray-500 whitespace-nowrap">
                                            {new Date(pedido.created_at).toLocaleDateString()}
                                            <div className="text-xs">{new Date(pedido.created_at).toLocaleTimeString()}</div>
                                        </td>
                                        <td className="p-4">
                                            <div className="font-bold text-gray-900">{pedido.cliente_nombre || 'N/A'}</div>
                                            <div className="text-xs text-gray-500">{pedido.cliente_email || 'Sin email'}</div>
                                            <div className="text-[10px] text-gray-400 mt-1">Sess: {pedido.stripe_session_id?.substring(0, 15)}...</div>
                                        </td>
                                        <td className="p-4 font-mono text-sm font-bold text-green-600">
                                            €{pedido.total?.toFixed(2)}
                                        </td>
                                        <td className="p-4">
                                            <select
                                                className={`px-2 py-1 text-xs font-bold rounded-lg border-0 cursor-pointer outline-none capitalize
                                                    ${pedido.estado === 'completado' ? 'bg-green-100 text-green-700' :
                                                        pedido.estado === 'pendiente' ? 'bg-orange-100 text-orange-700' :
                                                            pedido.estado === 'pagado' ? 'bg-blue-100 text-blue-700' :
                                                                pedido.estado === 'enviado' ? 'bg-indigo-100 text-indigo-700' :
                                                                    'bg-gray-100 text-gray-700'}`}
                                                value={pedido.estado}
                                                onChange={(e) => handleUpdatePedidoStatus(pedido.id, e.target.value)}
                                                disabled={isSaving}
                                            >
                                                <option value="pendiente">Pendiente</option>
                                                <option value="pagado">Pagado</option>
                                                <option value="procesando">Procesando</option>
                                                <option value="enviado">Enviado</option>
                                                <option value="completado">Completado</option>
                                                <option value="cancelado">Cancelado</option>
                                            </select>
                                        </td>
                                        <td className="p-4">
                                            {/* Details & Address */}
                                            {pedido.direccion_envio && (
                                                <div className="text-xs bg-gray-50 p-2 rounded border border-gray-100 text-gray-600 mb-2">
                                                    <strong>Envío:</strong> {pedido.direccion_envio?.address?.line1}, {pedido.direccion_envio?.address?.city} ({pedido.direccion_envio?.address?.country})
                                                </div>
                                            )}
                                            <div className="space-y-1">
                                                {pedido.pedido_items && pedido.pedido_items.map((item: any, i: number) => (
                                                    <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 p-2 rounded">
                                                        {item.productos?.imagen_url && <img src={item.productos.imagen_url} className="w-6 h-6 rounded object-cover" alt="" />}
                                                        <span className="font-medium text-gray-900">{item.cantidad}x</span>
                                                        <span className="text-gray-600">{item.productos?.nombre || 'Producto eliminado'}</span>
                                                        <span className="ml-auto text-gray-500 font-mono text-xs">€{(item.precio_unitario || 0).toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {pedidos.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="p-12 text-center text-gray-400">
                                            <Briefcase size={48} className="mx-auto mb-4 opacity-20" />
                                            <p>Aún no hay pedidos en el sistema.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderClientes = () => {
        if (isLoadingClientes) return <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div></div>;

        return (
            <div className="animate-in fade-in space-y-6 max-w-6xl mx-auto">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users className="text-indigo-600" /> Gestor de Clientes</h2>
                        <p className="text-gray-500 text-sm">Lista de usuarios registrados en la plataforma</p>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                    <th className="p-4">Fecha Registro</th>
                                    <th className="p-4">Cliente</th>
                                    <th className="p-4">Datos de Contacto</th>
                                    <th className="p-4">ID de Perfil</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {clientes.map(cliente => (
                                    <tr key={cliente.id} className="hover:bg-gray-50 p-4 align-top">
                                        <td className="p-4 text-sm text-gray-500 whitespace-nowrap">
                                            {new Date(cliente.created_at).toLocaleDateString()}
                                            <div className="text-xs">{new Date(cliente.created_at).toLocaleTimeString()}</div>
                                        </td>
                                        <td className="p-4">
                                            <div className="font-bold text-gray-900">{cliente.full_name || 'Sin nombre'}</div>
                                            <div className="text-xs text-indigo-600 mt-1 uppercase font-bold tracking-wider">{cliente.role || 'customer'}</div>
                                        </td>
                                        <td className="p-4">
                                            <div className="text-sm text-gray-700">{cliente.phone || 'Sin WhatsApp'}</div>
                                            {cliente.marketing_opt_in !== undefined && (
                                                <div className="text-xs mt-1">
                                                    {cliente.marketing_opt_in ? <span className="text-green-600">Suscrito a Marketing</span> : <span className="text-gray-400">No suscrito</span>}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4 font-mono text-[10px] text-gray-400">
                                            {cliente.id}
                                        </td>
                                    </tr>
                                ))}
                                {clientes.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="p-12 text-center text-gray-400">
                                            <Users size={48} className="mx-auto mb-4 opacity-20" />
                                            <p>Aún no hay clientes registrados.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderProducts = () => {
        let filteredProducts = products;

        if (storeFilter !== 'all') {
            filteredProducts = filteredProducts.filter(p => {
                const cat = categories.find(c => c.id === p.category_id);
                if (!cat) return false;
                return cat.type === storeFilter; // 'shopping' or 'tejidos'
            });
        }

        if (storeSearch) {
            const q = storeSearch.toLowerCase();
            filteredProducts = filteredProducts.filter(p =>
                p.name.toLowerCase().includes(q) ||
                p.slug.toLowerCase().includes(q)
            );
        }

        const inventoryCount = products.length;
        const lowStockCount = products.filter(p => p.stock < 5).length;
        const totalValue = products.reduce((acc, p) => acc + (p.price * p.stock), 0);

        return (
            <div className="animate-in fade-in space-y-6">
                {/* Header Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Total Inventario</p>
                            <h3 className="text-2xl font-bold text-gray-900">{inventoryCount}</h3>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-full text-blue-600"><Package size={20} /></div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Stock Bajo</p>
                            <h3 className="text-2xl font-bold text-red-600">{lowStockCount}</h3>
                        </div>
                        <div className="p-3 bg-red-50 rounded-full text-red-600"><AlertTriangle size={20} /></div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Valor Total</p>
                            <h3 className="text-2xl font-bold text-green-600">€{totalValue.toLocaleString()}</h3>
                        </div>
                        <div className="p-3 bg-green-50 rounded-full text-green-600"><DollarSign size={20} /></div>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <div className="relative w-full md:w-64">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Buscar productos..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-mabelita-primary/20"
                                value={storeSearch}
                                onChange={(e) => setStoreSearch(e.target.value)}
                            />
                        </div>
                        <div className="flex bg-gray-100 p-1 rounded-lg">
                            <button
                                onClick={() => setStoreFilter('all')}
                                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${storeFilter === 'all' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                            >Todo</button>
                            <button
                                onClick={() => setStoreFilter('tejidos')}
                                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${storeFilter === 'tejidos' ? 'bg-white shadow text-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
                            >Tejidos</button>
                            <button
                                onClick={() => setStoreFilter('shopping')}
                                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${storeFilter === 'shopping' ? 'bg-white shadow text-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
                            >Shopping</button>
                            <button
                                onClick={() => setStoreFilter('tunantada')}
                                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${storeFilter === 'tunantada' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                            >Tunantada</button>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={() => { setEditingCategory({ name: '', type: 'tejidos' }); setIsCategoryManagerOpen(true); }} className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900">
                            <List size={18} /> Categorías
                        </Button>
                        <Button onClick={() => { setEditingProduct({ stock: 1, days_to_make: 3, is_featured: false }); setIsProductModalOpen(true); }} className="flex items-center gap-2">
                            <Plus size={18} /> Nuevo Producto
                        </Button>
                    </div>
                </div>

                {/* Products Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                    <th className="p-4">Producto</th>
                                    <th className="p-4">Categoría</th>
                                    <th className="p-4">Precio</th>
                                    <th className="p-4">Stock</th>
                                    <th className="p-4">Stripe</th>
                                    <th className="p-4 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredProducts.map(product => {
                                    const cat = categories.find(c => c.id === product.category_id);
                                    const isMerch = cat?.type === 'shopping';

                                    return (
                                        <tr key={product.id} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 border border-gray-200">
                                                        {product.image_url ? (
                                                            <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon size={20} /></div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-gray-900">{product.name}</div>
                                                        <div className="text-xs text-gray-400 font-mono">{product.slug}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                {cat ? (
                                                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${isMerch ? 'bg-purple-50 text-purple-700 border border-purple-100' : 'bg-orange-50 text-orange-700 border border-orange-100'}`}>
                                                        {cat.name}
                                                    </span>
                                                ) : <span className="text-gray-400 text-xs">Sin categoría</span>}
                                            </td>
                                            <td className="p-4 font-mono text-sm">
                                                €{product.price.toFixed(2)}
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${product.stock === 0 ? 'bg-red-500' : product.stock < 5 ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
                                                    <span className={`text-sm font-bold ${product.stock === 0 ? 'text-red-600' : 'text-gray-700'}`}>{product.stock} unid.</span>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                {product.stripe_product_id ? (
                                                    <a href={`https://dashboard.stripe.com/products/${product.stripe_product_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded hover:bg-indigo-100 transition-colors" title="Ver en Stripe">
                                                        <DollarSign size={10} /> Enlazado
                                                    </a>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 text-gray-400 text-[10px] font-bold rounded" title="Aún no sincronizado con Stripe">
                                                        Pendiente
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => { setEditingProduct(product); setIsProductModalOpen(true); }}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Editar"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleProductDelete(product.id)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredProducts.length === 0 && (
                            <div className="p-12 text-center text-gray-400">
                                <Package size={48} className="mx-auto mb-4 opacity-20" />
                                <p>No se encontraron productos.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* CATEGORY MANAGER MODAL */}
                {isCategoryManagerOpen && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsCategoryManagerOpen(false)}></div>
                        <div className="relative bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                <h3 className="font-serif font-bold text-xl text-gray-800">Gestión de Categorías</h3>
                                <button onClick={() => setIsCategoryManagerOpen(false)}><X size={24} /></button>
                            </div>
                            <div className="p-6 space-y-6 flex-1 overflow-y-auto">
                                {/* Form */}
                                <div className="bg-gray-50 p-4 rounded-xl space-y-4 border border-gray-200">
                                    <h4 className="font-bold text-sm uppercase text-gray-500">{editingCategory?.id ? 'Editar' : 'Crear'} Categoría</h4>
                                    <div className="space-y-3">
                                        <input
                                            type="text" placeholder="Nombre de categoría"
                                            className="w-full p-3 border rounded-lg text-sm"
                                            value={editingCategory?.name || ''}
                                            onChange={e => {
                                                setEditingCategory(prev => ({
                                                    ...(prev || { type: 'tejidos', name: '', slug: '', id: 0 }),
                                                    name: e.target.value
                                                }));
                                            }}
                                        />
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Tipo de categoría</label>
                                            <div className="grid grid-cols-2 gap-2">
                                                {[
                                                    { value: 'shopping', label: 'Shopping', color: 'bg-purple-500' },
                                                    { value: 'tunantada', label: 'Tunantada', color: 'bg-indigo-500' },
                                                    { value: 'eventos', label: 'Contratación', color: 'bg-emerald-500' },
                                                    { value: 'tejidos', label: 'Tejidos', color: 'bg-orange-500' },
                                                ].map(opt => (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => setEditingCategory(prev => ({
                                                            ...(prev || { name: '', slug: '', id: 0 }),
                                                            type: opt.value as any
                                                        }))}
                                                        className={`flex items-center gap-2 p-2.5 rounded-lg border-2 text-sm font-bold transition-all ${(editingCategory?.type || 'tejidos') === opt.value ? 'border-gray-800 bg-white shadow-sm' : 'border-gray-200 bg-white/50 text-gray-500 hover:border-gray-300'}`}
                                                    >
                                                        <span className={`w-3 h-3 rounded-full ${opt.color}`}></span>
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <Button onClick={handleSaveCategory} disabled={isSaving} className="w-full">
                                            {editingCategory?.id ? 'Actualizar' : 'Crear'}
                                        </Button>
                                    </div>
                                </div>

                                {/* List - Grouped by Type */}
                                {[
                                    { type: 'shopping', label: 'Shopping con Mabelita', color: 'text-purple-500' },
                                    { type: 'tunantada', label: 'Tunantada', color: 'text-indigo-500' },
                                    { type: 'eventos', label: 'Contratación Eventos', color: 'text-emerald-500' },
                                    { type: 'tejidos', label: 'Tejidos (Artesanía)', color: 'text-orange-500' }
                                ].map(group => {
                                    const groupCats = categories.filter(cat => cat.type === group.type);
                                    if (groupCats.length === 0) return null;
                                    return (
                                        <div key={group.type} className="space-y-1">
                                            <h4 className={`text-xs font-bold uppercase tracking-wider ${group.color} mt-2 px-1`}>{group.label} ({groupCats.length})</h4>
                                            {groupCats.map(cat => (
                                                <div key={cat.id} className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-lg hover:shadow-sm">
                                                    <div>
                                                        <div className="font-bold text-sm">{cat.name}</div>
                                                        <div className="text-xs text-gray-400">{cat.slug}</div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button onClick={() => setEditingCategory(cat)} className="text-blue-500 hover:bg-blue-50 p-1 rounded"><Edit size={14} /></button>
                                                        <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={14} /></button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* PRODUCT EDIT MODAL */}
                {isProductModalOpen && editingProduct && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsProductModalOpen(false)}></div>
                        <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                <h3 className="font-serif font-bold text-xl text-gray-800">
                                    {editingProduct.id ? 'Editar Producto' : 'Nuevo Producto'}
                                </h3>
                                <button onClick={() => setIsProductModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                {/* Basic Info */}
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="col-span-2">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre del Producto</label>
                                        <input
                                            type="text"
                                            className="w-full p-3 border border-gray-200 rounded-lg font-medium focus:ring-2 focus:ring-mabelita-primary/20 outline-none"
                                            placeholder="Ej: Chompa de Alpaca"
                                            value={editingProduct.name || ''}
                                            onChange={e => setEditingProduct({ ...editingProduct, name: e.target.value })}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Categoría</label>
                                        <select
                                            className="w-full p-3 border border-gray-200 rounded-lg bg-white outline-none"
                                            value={editingProduct.category_id || ''}
                                            onChange={e => setEditingProduct({ ...editingProduct, category_id: Number(e.target.value) })}
                                        >
                                            <option value="">Seleccionar...</option>
                                            <optgroup label="Tejiendo">
                                                {categories.filter(c => c.type === 'tejidos').map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="Shopping (Merch)">
                                                {categories.filter(c => c.type === 'shopping').map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="Tunantada">
                                                {categories.filter(c => c.type === 'tunantada').map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="Contratación Eventos">
                                                {categories.filter(c => c.type === 'eventos').map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </optgroup>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Precio (€)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full p-3 border border-gray-200 rounded-lg font-mono outline-none"
                                            value={editingProduct.price || ''}
                                            onChange={e => setEditingProduct({ ...editingProduct, price: parseFloat(e.target.value) })}
                                        />
                                    </div>
                                </div>

                                {/* Image Upload - MODIFIED FOR GALLERY ONLY + AI TOOLS */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Imagen Principal</label>
                                    <div className="flex flex-col md:flex-row items-start gap-4">
                                        <div
                                            onClick={openGalleryForProduct}
                                            className="w-32 h-32 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden relative group cursor-pointer hover:border-blue-400 transition-colors"
                                        >
                                            {editingProduct.image_url ? (
                                                <img src={editingProduct.image_url} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="text-center text-gray-400">
                                                    <ImageIcon className="mx-auto mb-1" size={24} />
                                                    <span className="text-[10px]">Click para Galería</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 space-y-3">
                                            <p className="text-xs text-gray-500">Selecciona una imagen existente o crea una nueva usando nuestras herramientas de IA.</p>

                                            <div className="flex flex-wrap gap-2">
                                                <Button type="button" onClick={openGalleryForProduct} variant="outline" size="sm" className="flex items-center gap-2">
                                                    <Grid size={14} /> Abrir Galería
                                                </Button>
                                            </div>

                                            <div className="border-t border-gray-100 pt-2 mt-2">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase block mb-2">Generar con IA (Guardar y Volver)</label>
                                                <div className="flex gap-2">
                                                    <Button type="button" onClick={() => handleNavigateToTool('la-chacra')} size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs flex items-center gap-1">
                                                        <Sprout size={14} /> La Chacra
                                                    </Button>
                                                    <Button type="button" onClick={() => handleNavigateToTool('graphic-studio')} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white text-xs flex items-center gap-1">
                                                        <PenTool size={14} /> Pachamanca
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* ADDITIONAL IMAGES GALLERY */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2 flex justify-between items-center">
                                        <span>Galería de Imágenes (Adicionales)</span>
                                        <Button type="button" onClick={openGalleryForProductExtra} size="sm" variant="outline" className="text-xs h-6 px-2">
                                            <Plus size={12} className="mr-1" /> Añadir
                                        </Button>
                                    </label>

                                    {(!editingProduct.imagenes_adicionales || editingProduct.imagenes_adicionales.length === 0) ? (
                                        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg p-4 text-center text-xs text-gray-400">
                                            No hay imágenes adicionales
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-4 gap-2">
                                            {editingProduct.imagenes_adicionales.map((img, idx) => (
                                                <div key={idx} className="relative aspect-square rounded-lg overflow-hidden group border border-gray-200">
                                                    <img src={img} className="w-full h-full object-cover" />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeExtraImage(idx)}
                                                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                                        title="Eliminar imagen"
                                                    >
                                                        <MinusCircle size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Video URL for events */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">🎬 URL del Vídeo (embed TikTok/YouTube)</label>
                                    <input
                                        type="text"
                                        className="w-full p-3 border border-gray-200 rounded-lg outline-none font-mono text-sm"
                                        placeholder="https://www.tiktok.com/embed/v2/..."
                                        value={editingProduct.video_url || ''}
                                        onChange={e => setEditingProduct({ ...editingProduct, video_url: e.target.value })}
                                    />
                                    <p className="text-[10px] text-gray-400 mt-1">Deja vacío para mostrar imagen. Usa URL de embed para que se muestre en la tarjeta y modal del producto.</p>
                                </div>

                                {/* Details */}
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Stock</label>
                                        <input
                                            type="number"
                                            className="w-full p-3 border border-gray-200 rounded-lg outline-none"
                                            value={editingProduct.stock || 0}
                                            onChange={e => setEditingProduct({ ...editingProduct, stock: parseInt(e.target.value) })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Días Elab.</label>
                                        <input
                                            type="number"
                                            className="w-full p-3 border border-gray-200 rounded-lg outline-none"
                                            value={editingProduct.days_to_make || 3}
                                            onChange={e => setEditingProduct({ ...editingProduct, days_to_make: parseInt(e.target.value) })}
                                        />
                                    </div>
                                    <div className="flex items-center pt-6">
                                        <label className="flex items-center gap-2 cursor-pointer select-none">
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${editingProduct.is_featured ? 'bg-mabelita-primary border-mabelita-primary text-white' : 'border-gray-300 bg-white'}`}>
                                                {editingProduct.is_featured && <Check size={14} />}
                                            </div>
                                            <input
                                                type="checkbox"
                                                className="hidden"
                                                checked={editingProduct.is_featured || false}
                                                onChange={e => setEditingProduct({ ...editingProduct, is_featured: e.target.checked })}
                                            />
                                            <span className="text-sm font-medium text-gray-700">Destacado</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Opciones de Envío */}
                                <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4 mt-2">
                                    <div className="col-span-2">
                                        <h4 className="text-xs font-bold text-gray-800 uppercase flex items-center gap-2">
                                            <Package size={14} className="text-blue-500" />
                                            Opciones y Costes de Envío
                                        </h4>
                                        <p className="text-[10px] text-gray-500 mb-2">Se sincronizarán con los Shipping Rates de Stripe.</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nacional (€)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full p-3 border border-gray-200 rounded-lg outline-none"
                                            placeholder="Ej: 5.00 (0 para gratis)"
                                            value={editingProduct.shipping_cost_national ?? ''}
                                            onChange={e => setEditingProduct({ ...editingProduct, shipping_cost_national: parseFloat(e.target.value) || 0 })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Internacional (€)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full p-3 border border-gray-200 rounded-lg outline-none"
                                            placeholder="Ej: 15.00"
                                            value={editingProduct.shipping_cost_international ?? ''}
                                            onChange={e => setEditingProduct({ ...editingProduct, shipping_cost_international: parseFloat(e.target.value) || 0 })}
                                        />
                                    </div>
                                </div>

                                <div className="border-t border-gray-100 pt-4">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descripción</label>
                                    <textarea
                                        rows={4}
                                        className="w-full p-3 border border-gray-200 rounded-lg outline-none resize-none"
                                        value={editingProduct.description || ''}
                                        onChange={e => setEditingProduct({ ...editingProduct, description: e.target.value })}
                                    ></textarea>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Slug (URL)</label>
                                    <input
                                        type="text"
                                        className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm font-mono"
                                        placeholder="autogenerado-si-vacio"
                                        value={editingProduct.slug || ''}
                                        onChange={e => setEditingProduct({ ...editingProduct, slug: e.target.value })}
                                    />
                                </div>
                                {editingProduct.stripe_product_id && (
                                    <div className="col-span-2 pt-4 border-t border-gray-100">
                                        <h4 className="text-xs font-bold text-indigo-600 uppercase mb-3 flex items-center gap-1">
                                            <DollarSign size={14} /> Sincronización automática con Stripe Activa
                                        </h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Stripe Product ID</label>
                                                <input type="text" readOnly disabled className="w-full p-2 border border-dashed border-gray-200 rounded bg-gray-50 text-xs font-mono text-gray-500" value={editingProduct.stripe_product_id} />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Stripe Price ID</label>
                                                <input type="text" readOnly disabled className="w-full p-2 border border-dashed border-gray-200 rounded bg-gray-50 text-xs font-mono text-gray-500" value={editingProduct.stripe_price_id || 'N/A'} />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                                <Button variant="ghost" onClick={() => setIsProductModalOpen(false)}>Cancelar</Button>
                                <Button onClick={handleProductSave} disabled={isSaving}>
                                    {isSaving ? 'Guardando...' : 'Guardar Producto'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderStripeDashboard = () => (
        <div className="animate-in fade-in max-w-6xl mx-auto">
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        <DollarSign className="text-indigo-600" /> Stripe Dashboard
                    </h2>
                    <p className="text-gray-500 mt-1">Gestión financiera y de pagos conectada directamente con Stripe.</p>
                </div>
                <Button variant="outline" onClick={fetchStripeDashboard} disabled={isLoadingStripe} className="flex items-center gap-2">
                    <RefreshCw size={14} className={isLoadingStripe ? 'animate-spin' : ''} />
                    {isLoadingStripe ? 'Actualizando...' : 'Refrescar'}
                </Button>
            </div>

            {isLoadingStripe && !stripeData ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            ) : stripeData ? (
                <>
                    {/* KPI CARDS */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Saldo Disponible</p>
                                    <h3 className="text-4xl font-bold text-gray-900 mt-1">
                                        €{((stripeData.balance?.available?.[0]?.amount || 0) / 100).toFixed(2)}
                                    </h3>
                                </div>
                                <DollarSign className="text-green-500 opacity-20" size={40} />
                            </div>
                            <div className="text-xs font-medium text-green-600">Listo para transferir</div>
                        </div>

                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Saldo Pendiente</p>
                                    <h3 className="text-4xl font-bold text-gray-900 mt-1">
                                        €{((stripeData.balance?.pending?.[0]?.amount || 0) / 100).toFixed(2)}
                                    </h3>
                                </div>
                                <Clock className="text-orange-500 opacity-20" size={40} />
                            </div>
                            <div className="text-xs font-medium text-gray-500">En proceso de liquidación</div>
                        </div>

                        <div className="bg-gradient-to-br from-indigo-900 to-indigo-700 p-6 rounded-2xl shadow-sm text-white relative overflow-hidden flex flex-col justify-center">
                            <div className="absolute -right-4 -bottom-4 opacity-10">
                                <DollarSign size={120} />
                            </div>
                            <h3 className="text-xl font-bold mb-2">Portal de Stripe</h3>
                            <p className="text-sm text-indigo-200 mb-4 opacity-90">Accede directamente al panel completo de Stripe para configurar transferencias y disputas.</p>
                            <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="bg-white text-indigo-900 text-sm font-bold py-2 px-4 rounded-lg inline-block text-center hover:bg-indigo-50 transition-colors w-max">
                                Ir a Stripe.com
                            </a>
                        </div>
                    </div>

                    {/* RECENT CHARGES TABLE */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h3 className="font-bold text-gray-800">Transacciones Recientes</h3>
                            <span className="text-xs text-gray-400">Últimas cargas</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-500">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3">Estado</th>
                                        <th className="px-6 py-3">Monto</th>
                                        <th className="px-6 py-3">Descripción</th>
                                        <th className="px-6 py-3">Cliente (Email)</th>
                                        <th className="px-6 py-3">Fecha</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stripeData.charges?.data?.map((charge: any) => (
                                        <tr key={charge.id} className="bg-white border-b hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                {charge.status === 'succeeded' ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        Pagado
                                                    </span>
                                                ) : charge.status === 'pending' ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                        Pendiente
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        Fallido
                                                    </span>
                                                )}
                                                {charge.refunded && (
                                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800">Reembolso</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 font-bold text-gray-900">
                                                {charge.currency.toUpperCase()} {(charge.amount / 100).toFixed(2)}
                                            </td>
                                            <td className="px-6 py-4">{charge.description || 'Sin descripción'}</td>
                                            <td className="px-6 py-4">{charge.billing_details?.email || charge.receipt_email || 'No email'}</td>
                                            <td className="px-6 py-4 text-gray-400">
                                                {new Date(charge.created * 1000).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                    {(!stripeData.charges?.data || stripeData.charges.data.length === 0) && (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-12 text-center text-gray-400">
                                                No hay transacciones recientes en Stripe.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* STRIPE PRODUCTS TABLE */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mt-8">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h3 className="font-bold text-gray-800">Productos Sincronizados en Stripe</h3>
                            <span className="text-xs text-gray-400">Total: {stripeData.products?.length || 0}</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-500">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3">Producto</th>
                                        <th className="px-6 py-3">Precio Activo</th>
                                        <th className="px-6 py-3">Stripe ID</th>
                                        <th className="px-6 py-3">Estado / Extras</th>
                                        <th className="px-6 py-3">Fecha Creación</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stripeData.products?.map((product: any) => {
                                        const price = stripeData.prices?.find((p: any) => p.product === product.id);
                                        return (
                                            <tr key={product.id} className="bg-white border-b hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 flex items-center gap-3">
                                                    {product.images && product.images.length > 0 ? (
                                                        <img src={product.images[0]} alt={product.name} className="w-10 h-10 rounded object-cover" />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-gray-400">
                                                            <Package size={20} />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="font-bold text-gray-900">{product.name}</p>
                                                        {product.description && <p className="text-xs text-gray-500 truncate max-w-[200px]">{product.description}</p>}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 font-bold text-gray-900">
                                                    {price ? `${price.currency.toUpperCase()} ${(price.unit_amount / 100).toFixed(2)}` : <span className="text-gray-400 text-xs text-normal italic">Sin precio</span>}
                                                </td>
                                                <td className="px-6 py-4 font-mono text-[10px] text-gray-400">
                                                    <div>{product.id}</div>
                                                    {price && <div>{price.id}</div>}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col gap-1 items-start">
                                                        {product.active ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800">Activo</span>
                                                        ) : (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600">Inactivo</span>
                                                        )}
                                                        {product.metadata?.es_encargo === 'true' && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800" title={`Días de Elaboración: ${product.metadata?.dias_elaboracion || 'N/A'}`}>
                                                                Por Encargo ({product.metadata?.dias_elaboracion || '?'}d)
                                                            </span>
                                                        )}
                                                        {product.metadata?.stock !== undefined && (
                                                            <span className="text-[10px] font-medium text-gray-500">Stock: {product.metadata?.stock}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-gray-400 text-xs">
                                                    {new Date(product.created * 1000).toLocaleDateString()}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {(!stripeData.products || stripeData.products.length === 0) && (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-12 text-center text-gray-400">
                                                No hay productos de la tienda sincronizados en Stripe.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : (
                <div className="bg-red-50 text-red-600 p-6 rounded-2xl border border-red-200">
                    <p className="font-bold mb-2">Error al cargar datos de Stripe o No Disponible</p>
                    <p className="text-sm">Inicia el servidor local de Supabase Edge Functions para probar en local, o asegúrate de que esté desplegado correctamente y contenga la Variable de Entorno de Stripe.</p>
                </div>
            )}
        </div>
    );


    // ===== TUNANTADA CALENDAR MANAGEMENT =====
    const [selectedDayDate, setSelectedDayDate] = useState<string | null>(null);
    const [selectedDaySlots, setSelectedDaySlots] = useState<any[]>([]);
    const [bulkReleaseWeeks, setBulkReleaseWeeks] = useState(4);
    const [bulkReleasing, setBulkReleasing] = useState(false);

    const fetchDispSlots = async () => {
        setDispLoading(true);
        setDispError(null);
        const { data, error } = await supabase
            .from('disponibilidad_tunantada')
            .select('*')
            .order('fecha', { ascending: true });
        if (error) {
            console.error('Error fetching disponibilidad_tunantada:', error);
            setDispError(error.message || 'Error al cargar el calendario');
        } else if (data) {
            setDispSlots(data);
        }
        setDispLoading(false);
    };

    const handleSlotClick = async (dateStr: string, existingSlot?: any) => {
        if (existingSlot && existingSlot.id > 0) {
            setEditingSlot(existingSlot);
            setSlotForm({
                hora_inicio: existingSlot.hora_inicio || '18:00',
                hora_fin: existingSlot.hora_fin || '22:00',
                estado: existingSlot.estado || 'disponible',
                nota: existingSlot.nota || '',
                cliente_nombre: existingSlot.cliente_nombre || ''
            });
        } else {
            // No existing slot or it's a VIRTUAL slot (negative ID)
            // Open "add slot" for this date, prefilled with the virtual slot's time if provided, and set to 'disponible' (Liberar)
            setEditingSlot({ fecha: dateStr, _isNew: true });
            setSlotForm({
                hora_inicio: existingSlot?.hora_inicio || '18:00',
                hora_fin: existingSlot?.hora_fin || '20:00',
                estado: 'disponible', // Default to freeing the slot
                nota: '',
                cliente_nombre: ''
            });
        }
    };

    /** Called when clicking a day cell on the calendar — shows all slots for that day */
    const handleDayClick = (dateStr: string, daySlots: any[]) => {
        setSelectedDayDate(dateStr);
        setSelectedDaySlots(daySlots);
        setEditingSlot(null);
    };

    const handleSaveSlot = async () => {
        if (!editingSlot) return;
        if (editingSlot._isNew) {
            // Insert new slot
            const { error } = await supabase.from('disponibilidad_tunantada').insert({
                fecha: editingSlot.fecha, ...slotForm
            });
            if (!error) {
                setToast({ message: `✅ Hora ${slotForm.hora_inicio}-${slotForm.hora_fin} añadida`, type: 'success' });
                setEditingSlot(null);
                await fetchDispSlots();
                // Refresh the day view
                if (selectedDayDate) {
                    const updated = dispSlots.filter(s => s.fecha === selectedDayDate);
                    setSelectedDaySlots(updated);
                }
            } else {
                setToast({ message: `❌ Error: ${error.message}`, type: 'error' });
            }
        } else {
            const { error } = await supabase.from('disponibilidad_tunantada').update(slotForm).eq('id', editingSlot.id);
            if (!error) {
                setToast({ message: '✅ Slot actualizado', type: 'success' });
                setEditingSlot(null);
                fetchDispSlots();
            } else {
                setToast({ message: `❌ Error: ${error.message}`, type: 'error' });
            }
        }
    };

    const handleDeleteSlot = async (id: number) => {
        const { error } = await supabase.from('disponibilidad_tunantada').delete().eq('id', id);
        if (!error) {
            setToast({ message: '🗑️ Slot eliminado', type: 'success' });
            if (editingSlot?.id === id) setEditingSlot(null);
            await fetchDispSlots();
            if (selectedDayDate) {
                setSelectedDaySlots(prev => prev.filter(s => s.id !== id));
            }
        }
    };

    /** Bulk release: generate 2h slots for the next N weekends */
    const handleBulkRelease = async () => {
        setBulkReleasing(true);
        const today = new Date();
        const slotsToInsert: any[] = [];

        // Find nearest Friday (including today if it's Friday)
        let cursor = new Date(today);
        const dayOfWeek = cursor.getDay(); // 0=Sun, 5=Fri
        const daysUntilFriday = (5 - dayOfWeek + 7) % 7; // 0 if today is Friday
        cursor.setDate(cursor.getDate() + daysUntilFriday);

        // Query DB directly for existing slots (not stale React state)
        const { data: existingRows } = await supabase
            .from('disponibilidad_tunantada')
            .select('fecha, hora_inicio');
        const existingSet = new Set((existingRows || []).map((s: any) => `${s.fecha}_${s.hora_inicio}`));

        for (let week = 0; week < bulkReleaseWeeks; week++) {
            const friday = new Date(cursor);
            friday.setDate(cursor.getDate() + (week * 7));
            const saturday = new Date(friday);
            saturday.setDate(friday.getDate() + 1);
            const sunday = new Date(friday);
            sunday.setDate(friday.getDate() + 2);

            const fmt = (d: Date) => {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                return `${y}-${m}-${dd}`;
            };

            // Friday: 18:00 → 00:00 (blocks: 18-20, 20-22, 22-00)
            const friBlocks = [['18:00', '20:00'], ['20:00', '22:00'], ['22:00', '00:00']];
            for (const [start, end] of friBlocks) {
                const key = `${fmt(friday)}_${start}`;
                if (!existingSet.has(key)) {
                    slotsToInsert.push({ fecha: fmt(friday), hora_inicio: start, hora_fin: end, estado: 'disponible', nota: '', cliente_nombre: '' });
                    existingSet.add(key); // prevent duplicates within this batch
                }
            }

            // Saturday & Sunday: 08:00 → 02:00
            const dayBlocks = [['08:00', '10:00'], ['10:00', '12:00'], ['12:00', '14:00'], ['14:00', '16:00'], ['16:00', '18:00'], ['18:00', '20:00'], ['20:00', '22:00'], ['22:00', '00:00'], ['00:00', '02:00']];
            for (const day of [saturday, sunday]) {
                for (const [start, end] of dayBlocks) {
                    const key = `${fmt(day)}_${start}`;
                    if (!existingSet.has(key)) {
                        slotsToInsert.push({ fecha: fmt(day), hora_inicio: start, hora_fin: end, estado: 'disponible', nota: '', cliente_nombre: '' });
                        existingSet.add(key);
                    }
                }
            }
        }

        if (slotsToInsert.length === 0) {
            setToast({ message: 'ℹ️ Todos los huecos ya existen', type: 'success' });
            setBulkReleasing(false);
            return;
        }

        const { error } = await supabase.from('disponibilidad_tunantada').upsert(slotsToInsert, { onConflict: 'fecha,hora_inicio', ignoreDuplicates: true });
        if (!error) {
            setToast({ message: `✅ ${slotsToInsert.length} huecos liberados para ${bulkReleaseWeeks} fines de semana`, type: 'success' });
            fetchDispSlots();
        } else {
            setToast({ message: `❌ Error: ${error.message}`, type: 'error' });
        }
        setBulkReleasing(false);
    };

    const renderTunantadaCalendar = () => {
        if (!dispFetchedRef.current && dispSlots.length === 0 && !dispLoading) {
            dispFetchedRef.current = true;
            fetchDispSlots();
        }

        // Group day slots for selected day — sort with after-midnight (00-05) at the end
        const slotSortKey = (t: string) => { const h = parseInt(t || '99', 10); return h < 6 ? String(h + 24).padStart(2, '0') + t.slice(2) : t; };
        const dayViewSlots = selectedDayDate
            ? dispSlots.filter(s => s.fecha === selectedDayDate).sort((a: any, b: any) => slotSortKey(a.hora_inicio || '').localeCompare(slotSortKey(b.hora_inicio || '')))
            : [];

        return (
            <div className="animate-in fade-in max-w-6xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Calendar className="text-mabelita-primary" size={32} />
                        Calendario — Gestión de Disponibilidad
                    </h1>
                    <p className="text-gray-500 mt-2">Haz click en un día para ver y gestionar los <span className="text-mabelita-primary font-bold">bloques de horas</span>. Usa "Liberar Horas" para crear automáticamente bloques de 2h para fines de semana.</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
                        {dispLoading ? (
                            <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-indigo-500" size={32} /></div>
                        ) : dispError ? (
                            <div className="p-6 space-y-4">
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                    <p className="text-red-700 font-bold text-sm mb-1">⚠️ Error al cargar el calendario</p>
                                    <p className="text-red-600 text-xs">{dispError}</p>
                                </div>
                                <div className="bg-gray-900 rounded-xl p-4 text-xs font-mono text-green-400 overflow-x-auto">
                                    <p className="text-gray-500 mb-2">-- Copia y pega en Supabase Dashboard → SQL Editor:</p>
                                    <pre className="whitespace-pre-wrap">{`CREATE TABLE IF NOT EXISTS disponibilidad_tunantada (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  fecha date NOT NULL,
  hora_inicio text DEFAULT '18:00',
  hora_fin text DEFAULT '22:00',
  estado text DEFAULT 'disponible',
  nota text,
  cliente_nombre text,
  created_at timestamptz DEFAULT now()
);

ALTER TABLE disponibilidad_tunantada ENABLE ROW LEVEL SECURITY;
CREATE POLICY "public_read" ON disponibilidad_tunantada FOR SELECT USING (true);
CREATE POLICY "auth_all" ON disponibilidad_tunantada FOR ALL USING (true);`}</pre>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => { navigator.clipboard.writeText(`CREATE TABLE IF NOT EXISTS disponibilidad_tunantada (id uuid DEFAULT gen_random_uuid() PRIMARY KEY, fecha date NOT NULL, hora_inicio text DEFAULT '18:00', hora_fin text DEFAULT '22:00', estado text DEFAULT 'disponible', nota text, cliente_nombre text, created_at timestamptz DEFAULT now()); ALTER TABLE disponibilidad_tunantada ENABLE ROW LEVEL SECURITY; CREATE POLICY "public_read" ON disponibilidad_tunantada FOR SELECT USING (true); CREATE POLICY "auth_all" ON disponibilidad_tunantada FOR ALL USING (true);`); setToast({ message: 'SQL copiado al portapapeles', type: 'success' }); }}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 flex items-center gap-2"><Copy size={14} /> Copiar SQL</button>
                                    <button onClick={fetchDispSlots}
                                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-200 flex items-center gap-2"><RefreshCw size={14} /> Reintentar</button>
                                    <a href="https://supabase.com/dashboard/project/lhjirzehoqgajsfagamw/sql" target="_blank" rel="noopener noreferrer"
                                        className="px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-bold hover:bg-emerald-100 flex items-center gap-2"><Database size={14} /> Abrir SQL Editor</a>
                                </div>
                            </div>
                        ) : (
                            <CalendarioDisponibilidad slots={dispSlots} readOnly={false} onDayClick={handleDayClick} onSlotClick={handleSlotClick} />
                        )}
                        <div className="mt-4 flex justify-end">
                            <Button onClick={fetchDispSlots} variant="ghost" className="text-indigo-600"><RefreshCw size={14} className="mr-1" /> Actualizar</Button>
                        </div>
                    </div>
                    <div className="space-y-6">
                        {/* BULK RELEASE PANEL */}
                        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-200 p-5">
                            <h4 className="font-bold text-sm text-indigo-800 mb-3 flex items-center gap-2">
                                <Clock size={16} className="text-indigo-600" /> Liberar Horas (Fines de Semana)
                            </h4>
                            <p className="text-xs text-indigo-600 mb-3">Crea bloques de 2h automáticamente:<br />
                                <strong>Viernes</strong> 18:00–00:00 · <strong>Sáb/Dom</strong> 08:00–02:00
                            </p>
                            <div className="flex items-center gap-2 mb-3">
                                <label className="text-xs font-bold text-indigo-700">Semanas:</label>
                                <select value={bulkReleaseWeeks} onChange={e => setBulkReleaseWeeks(Number(e.target.value))}
                                    className="flex-1 p-2 border border-indigo-200 rounded-lg text-sm font-bold bg-white">
                                    <option value={1}>1 fin de semana</option>
                                    <option value={2}>2 fines de semana</option>
                                    <option value={4}>4 fines de semana</option>
                                    <option value={8}>8 fines de semana</option>
                                </select>
                            </div>
                            <button onClick={handleBulkRelease} disabled={bulkReleasing}
                                className="w-full py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                                {bulkReleasing ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                {bulkReleasing ? 'Liberando...' : 'Liberar Horas'}
                            </button>
                        </div>

                        {/* DAY SLOTS VIEWER — shows when clicking a day */}
                        {selectedDayDate && (
                            <div className="bg-white rounded-2xl shadow-lg border border-indigo-200 p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-bold text-sm text-gray-900 flex items-center gap-2">
                                        <Clock size={14} className="text-indigo-500" />
                                        {new Date(selectedDayDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })}
                                    </h4>
                                    <button onClick={() => { setSelectedDayDate(null); setSelectedDaySlots([]); }} className="p-1 text-gray-400 hover:text-gray-600"><X size={14} /></button>
                                </div>
                                {dayViewSlots.length === 0 ? (
                                    <p className="text-xs text-gray-400 text-center py-4">Sin huecos. Usa "Añadir hora" o "Liberar Horas".</p>
                                ) : (
                                    <div className="space-y-1.5 max-h-64 overflow-y-auto">
                                        {dayViewSlots.map((s: any) => (
                                            <div key={s.id} className={`flex items-center gap-2 p-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${editingSlot?.id === s.id ? 'ring-2 ring-indigo-400 bg-indigo-50' :
                                                s.estado === 'disponible' ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' :
                                                    s.estado === 'reservado' ? 'bg-red-50 text-red-700 hover:bg-red-100' :
                                                        'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                                }`} onClick={() => handleSlotClick(s.fecha, s)}>
                                                <Clock size={12} />
                                                <span className="font-mono">{s.hora_inicio?.slice(0, 5)}–{s.hora_fin?.slice(0, 5)}</span>
                                                <span className="flex-1 truncate text-[10px] opacity-70">
                                                    {s.estado === 'reservado' ? s.cliente_nombre || 'Reserv.' : s.estado === 'bloqueado' ? 'Bloq.' : ''}
                                                </span>
                                                <button onClick={(e) => { e.stopPropagation(); handleDeleteSlot(s.id); }}
                                                    className="p-0.5 text-gray-400 hover:text-red-600 rounded"><Trash2 size={11} /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <button onClick={() => { setEditingSlot({ fecha: selectedDayDate, _isNew: true }); setSlotForm({ hora_inicio: '19:00', hora_fin: '21:00', estado: 'disponible', nota: '', cliente_nombre: '' }); }}
                                    className="w-full mt-2 py-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 flex items-center justify-center gap-1">
                                    <Plus size={12} /> Añadir hora
                                </button>
                            </div>
                        )}

                        {/* SLOT EDITOR */}
                        {editingSlot && (
                            <div className="bg-white rounded-2xl shadow-lg border border-indigo-200 p-6">
                                <h3 className="font-bold text-lg text-gray-900 mb-1 flex items-center gap-2"><Edit2 size={16} className="text-indigo-500" /> {editingSlot._isNew ? 'Nuevo Hueco' : 'Editar Slot'}</h3>
                                <p className="text-sm text-gray-400 mb-4 font-mono">{editingSlot.fecha}</p>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><label className="text-[10px] font-bold uppercase text-gray-500">Hora Inicio</label><input type="time" className="w-full p-2 border rounded text-sm font-mono" value={slotForm.hora_inicio} onChange={e => setSlotForm(p => ({ ...p, hora_inicio: e.target.value }))} /></div>
                                        <div><label className="text-[10px] font-bold uppercase text-gray-500">Hora Fin</label><input type="time" className="w-full p-2 border rounded text-sm font-mono" value={slotForm.hora_fin} onChange={e => setSlotForm(p => ({ ...p, hora_fin: e.target.value }))} /></div>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase text-gray-500">Estado</label>
                                        <select className={`w-full p-2 border rounded text-sm font-bold ${slotForm.estado === 'disponible' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : slotForm.estado === 'reservado' ? 'bg-red-50 text-red-700 border-red-300' : 'bg-gray-100 text-gray-600 border-gray-300'}`} value={slotForm.estado} onChange={e => setSlotForm(p => ({ ...p, estado: e.target.value }))}>
                                            <option value="disponible">✅ Disponible</option>
                                            <option value="reservado">🔴 Reservado</option>
                                            <option value="bloqueado">⬜ Bloqueado</option>
                                        </select>
                                    </div>
                                    {slotForm.estado === 'reservado' && (<div><label className="text-[10px] font-bold uppercase text-gray-500">Cliente</label><input type="text" className="w-full p-2 border rounded text-sm" placeholder="Nombre del cliente" value={slotForm.cliente_nombre} onChange={e => setSlotForm(p => ({ ...p, cliente_nombre: e.target.value }))} /></div>)}
                                    <div><label className="text-[10px] font-bold uppercase text-gray-500">Nota interna</label><textarea className="w-full p-2 border rounded text-sm resize-none" rows={2} placeholder="Ej: Boda en Madrid" value={slotForm.nota} onChange={e => setSlotForm(p => ({ ...p, nota: e.target.value }))} /></div>
                                    <div className="flex gap-2 pt-2">
                                        <Button onClick={handleSaveSlot} className="flex-1 bg-indigo-600"><Save size={14} className="mr-1" /> {editingSlot._isNew ? 'Crear' : 'Guardar'}</Button>
                                        {!editingSlot._isNew && <Button onClick={() => handleDeleteSlot(editingSlot.id)} variant="ghost" className="text-red-500 hover:bg-red-50"><Trash2 size={14} /></Button>}
                                        <Button onClick={() => setEditingSlot(null)} variant="ghost" className="text-gray-400"><X size={14} /></Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* SUMMARY */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <h4 className="font-bold text-sm text-gray-700 mb-4 uppercase tracking-wider">Resumen</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center"><span className="text-sm text-gray-500">Huecos disponibles</span><span className="font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full text-sm">{dispSlots.filter(s => s.estado === 'disponible').length}</span></div>
                                <div className="flex justify-between items-center"><span className="text-sm text-gray-500">Reservados</span><span className="font-bold text-red-600 bg-red-50 px-3 py-1 rounded-full text-sm">{dispSlots.filter(s => s.estado === 'reservado').length}</span></div>
                                <div className="flex justify-between items-center"><span className="text-sm text-gray-500">Bloqueados</span><span className="font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full text-sm">{dispSlots.filter(s => s.estado === 'bloqueado').length}</span></div>
                            </div>
                        </div>
                        {dispSlots.filter(s => s.estado === 'reservado').length > 0 && (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                <h4 className="font-bold text-sm text-gray-700 mb-4 uppercase tracking-wider">Próximas Reservas</h4>
                                <div className="space-y-2">
                                    {dispSlots.filter(s => s.estado === 'reservado').slice(0, 5).map(s => (
                                        <button key={s.id} onClick={() => handleSlotClick(s.fecha, s)} className="w-full text-left p-3 rounded-xl bg-red-50 hover:bg-red-100 transition-colors border border-red-100">
                                            <p className="font-bold text-sm text-red-700">{s.fecha}</p>
                                            <p className="text-xs text-red-500">{s.cliente_nombre || 'Sin nombre'} • {s.hora_inicio?.slice(0, 5)}-{s.hora_fin?.slice(0, 5)}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderApiMonitor = () => (
        <div className="animate-in fade-in max-w-6xl mx-auto">
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        <Activity className="text-green-600" /> API Monitor (Google Cloud)
                    </h2>
                    <p className="text-gray-500 mt-1">Visualización en tiempo real del consumo de Gemini AI a través de logs internos.</p>
                </div>
                <Button variant="outline" onClick={fetchApiLogs} className="flex items-center gap-2"><RefreshCw size={14} /> Refrescar</Button>
            </div>

            {/* KPI CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Requests (Ult. 50)</p>
                            <h3 className="text-4xl font-bold text-gray-900 mt-1">{apiStats.totalRequests}</h3>
                        </div>
                        <Globe className="text-blue-500 opacity-20" size={40} />
                    </div>
                    <div className="text-xs font-medium text-green-600">Actividad Reciente</div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Tasa de Error</p>
                            <h3 className={`text-4xl font-bold mt-1 ${apiStats.errorRate > 0 ? 'text-red-500' : 'text-gray-900'}`}>{apiStats.errorRate.toFixed(1)}%</h3>
                        </div>
                        <AlertTriangle className={`${apiStats.errorRate > 0 ? 'text-red-500' : 'text-green-500'} opacity-20`} size={40} />
                    </div>
                    <div className="text-xs font-medium text-gray-500">Objetivo: 0%</div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Latencia Media</p>
                            <h3 className="text-4xl font-bold text-gray-900 mt-1">{apiStats.avgLatency}ms</h3>
                        </div>
                        <Clock className="text-orange-500 opacity-20" size={40} />
                    </div>
                    <div className="text-xs font-medium text-gray-500">Respuesta API</div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Coste Estimado</p>
                            <h3 className="text-4xl font-bold text-gray-900 mt-1">${apiStats.totalCost.toFixed(4)}</h3>
                        </div>
                        <DollarSign className="text-green-600 opacity-20" size={40} />
                    </div>
                    <div className="text-xs font-medium text-gray-500">Gemini Flash Pricing</div>
                </div>
            </div>

            {/* LOGS TABLE */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h3 className="font-bold text-gray-800">Historial de Llamadas</h3>
                    <span className="text-xs text-gray-400">Mostrando últimas 50</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                            <tr>
                                <th className="px-6 py-3">Estado</th>
                                <th className="px-6 py-3">Endpoint</th>
                                <th className="px-6 py-3">Modelo</th>
                                <th className="px-6 py-3">Latencia</th>
                                <th className="px-6 py-3">Coste</th>
                                <th className="px-6 py-3">Fecha</th>
                            </tr>
                        </thead>
                        <tbody>
                            {apiLogs.map((log, idx) => (
                                <tr key={log.id || idx} className="bg-white border-b hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        {log.status === 'success' ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                Success
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" title={log.error_message}>
                                                Error
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 font-medium text-gray-900">{log.endpoint}</td>
                                    <td className="px-6 py-4 font-mono text-xs">{log.model}</td>
                                    <td className="px-6 py-4">{log.duration_ms}ms</td>
                                    <td className="px-6 py-4">${log.cost_estimated?.toFixed(5)}</td>
                                    <td className="px-6 py-4 text-gray-400">
                                        {new Date(log.created_at).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                            {apiLogs.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                                        No hay registros de actividad aún. Usa La Chacra para generar tráfico.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const getConfigKey = (key: string) => themeContext === 'weaver' ? `tej_${key}` : key;

    const renderImagePicker = (configKey: string, currentUrl: string) => (
        <div className="mb-4">
            <label className="block text-sm font-bold mb-2 text-gray-700">Imagen ({configKey})</label>
            <div className="flex gap-4 items-start">
                <div className="w-32 h-24 bg-gray-100 rounded-lg border border-gray-200 overflow-hidden relative group shrink-0 shadow-inner">
                    {currentUrl ? (
                        <img src={currentUrl} className="w-full h-full object-cover" alt="Preview" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400"><ImageIcon size={24} /></div>
                    )}
                </div>
                <div className="flex flex-col gap-2 justify-center h-24">
                    <Button size="sm" variant="primary" onClick={() => openGalleryForConfig(getConfigKey(configKey))} className="flex items-center gap-2 justify-start shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all">
                        <Grid size={16} /> Seleccionar de Galería
                    </Button>
                </div>
            </div>
        </div>
    );

    const getGridClass = () => {
        switch (galleryGridSize) {
            case 'small': return 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2';
            case 'large': return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6';
            case 'details': return 'grid-cols-1 gap-0'; // list view
            case 'medium':
            default: return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4';
        }
    };

    const renderBucketSelector = () => (
        <div className="flex flex-col gap-4 mb-6">
            <div className="flex gap-2 overflow-x-auto pb-2">
                {[
                    { id: 'web-assets', label: 'Web Assets', icon: <Globe size={16} /> },
                    { id: 'tejiendo', label: 'Tejiendo con Mabelita', icon: <Briefcase size={16} /> },
                    { id: 'shopping', label: 'Shopping con Mabelita', icon: <ShoppingBag size={16} /> },
                    { id: 'tunantada', label: 'Tunantada (Artista)', icon: <Mic size={16} /> }
                ].map((b) => (
                    <button
                        key={b.id}
                        onClick={() => handleActiveBucketChange(b.id as any)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (b.id !== activeBucket) {
                                handleCrossBucketMove(e, b.id);
                            }
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap border ${activeBucket === b.id
                            ? 'bg-gray-900 text-white border-gray-900 shadow-md'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                            }`}
                    >
                        {b.icon} {b.label}
                    </button>
                ))}
            </div>

            {/* BARRA DE NAVEGACION Y ACCIONES (BREADCRUMBS & TOOLS) */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-3 rounded-xl border border-gray-200 shadow-sm animate-in fade-in">

                {/* BREADCRUMBS */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 w-full sm:w-auto">
                    <button
                        onClick={() => setActiveFolder('')}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, 'root')}
                        className={`p-1.5 rounded-lg transition-colors flex items-center gap-1 font-bold text-sm ${!activeFolder ? 'text-blue-600 bg-blue-50' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`}
                    >
                        <HardDrive size={16} /> Raíz
                    </button>

                    {breadcrumb.map((crumb, idx) => (
                        <React.Fragment key={crumb.path}>
                            <span className="text-gray-300 mx-1">/</span>
                            <button
                                onClick={() => setActiveFolder(crumb.path)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, crumb.path)}
                                className={`px-2 py-1 rounded-lg transition-colors font-semibold text-sm whitespace-nowrap ${idx === breadcrumb.length - 1 ? 'text-gray-900 bg-gray-100 cursor-default pointer-events-none' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`}
                            >
                                {crumb.name}
                            </button>
                        </React.Fragment>
                    ))}
                </div>

                {/* ACCIONES Y TAMAÑOS */}
                <div className="flex items-center gap-3 w-full sm:w-auto justify-end border-t sm:border-t-0 pt-3 sm:pt-0">
                    <button onClick={handleCreateFolder} className="text-xs font-bold text-gray-600 hover:text-blue-600 flex items-center gap-1 px-3 py-1.5 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors">
                        <FolderOpen size={14} /> Crear Carpeta
                    </button>

                    <div className="h-6 w-px bg-gray-200 mx-1"></div>

                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                        {(['details', 'small', 'medium', 'large'] as const).map(size => (
                            <button
                                key={size}
                                onClick={() => setGalleryGridSize(size)}
                                className={`p-1.5 rounded-md transition-all ${galleryGridSize === size ? 'bg-white shadow-sm text-mabelita-primary ring-1 ring-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                                title={size === 'details' ? 'Vista de Detalles' : size === 'small' ? 'Iconos Pequeños' : size === 'medium' ? 'Iconos Medianos' : 'Iconos Grandes'}
                            >
                                {size === 'details' && <ListIcon size={14} />}
                                {size === 'small' && <Grid size={14} />}
                                {size === 'medium' && <Grid size={16} />}
                                {size === 'large' && <Maximize2 size={14} />}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const formatBytes = (bytes: number, decimals = 1) => {
        if (!+bytes) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const renderMediaGallery = () => (
        <div className="animate-in fade-in h-[calc(100vh-6rem)]  flex flex-col">
            {/* ===== WINDOWS EXPLORER RIBBON / TOOLBAR ===== */}
            <div className="bg-white border border-gray-200 rounded-t-xl shadow-sm">
                {/* Row 1: Actions only (buckets are in the sidebar) */}
                <div className="flex items-center justify-end px-3 py-2 border-b border-gray-100 gap-1.5">
                    <label className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200">
                        <Upload size={13} /> Subir
                        <input type="file" multiple className="hidden" onChange={handleBulkUpload} disabled={isSaving} accept="image/*" />
                    </label>
                    <button onClick={handleCreateFolder} className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-100 border border-gray-200">
                        <FolderOpen size={13} /> Nueva Carpeta
                    </button>
                    <Button onClick={() => setActiveView('la-chacra')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 rounded-lg border">
                        <Sprout size={13} /> La Chacra
                    </Button>
                </div>

                {/* Row 2: Address bar + view controls */}
                <div className="flex items-center px-3 py-1.5 gap-3">
                    {/* Back button */}
                    <div className="flex items-center gap-0.5">
                        <button onClick={() => { if (breadcrumb.length > 1) setActiveFolder(breadcrumb[breadcrumb.length - 2].path); else setActiveFolder(''); }}
                            disabled={!activeFolder} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 text-gray-500"><ArrowLeft size={14} /></button>
                    </div>

                    {/* Address bar (breadcrumb as path) */}
                    <div className="flex-1 flex items-center bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 gap-1 overflow-x-auto min-w-0">
                        <button onClick={() => setActiveFolder('')} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, 'root')}
                            className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-semibold transition-colors ${!activeFolder ? 'text-blue-600 bg-blue-50' : 'text-gray-500 hover:bg-gray-200'}`}>
                            <HardDrive size={12} className="inline mr-1" />{activeBucket}
                        </button>
                        {breadcrumb.map((crumb, idx) => (
                            <React.Fragment key={crumb.path}>
                                <ChevronRight size={10} className="text-gray-300 shrink-0" />
                                <button onClick={() => setActiveFolder(crumb.path)} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, crumb.path)}
                                    className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${idx === breadcrumb.length - 1 ? 'text-gray-900 bg-gray-100' : 'text-gray-500 hover:bg-gray-200'}`}>
                                    {crumb.name}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>

                    {/* View modes */}
                    <div className="flex items-center bg-gray-100 rounded-lg p-0.5 shrink-0">
                        {(['details', 'small', 'medium', 'large'] as const).map(size => (
                            <button key={size} onClick={() => setGalleryGridSize(size)}
                                className={`p-1.5 rounded-md transition-all ${galleryGridSize === size ? 'bg-white shadow-sm text-blue-600 ring-1 ring-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                                title={size === 'details' ? 'Detalles' : size === 'small' ? 'Iconos' : size === 'medium' ? 'Mosaico' : 'Grande'}>
                                {size === 'details' && <ListIcon size={14} />}
                                {size === 'small' && <Grid size={12} />}
                                {size === 'medium' && <Grid size={16} />}
                                {size === 'large' && <Maximize2 size={14} />}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ===== MAIN CONTENT: SIDEBAR + FILES ===== */}
            <div className="flex flex-1 border-x border-b border-gray-200 rounded-b-xl overflow-hidden bg-white min-h-0">
                {/* LEFT SIDEBAR: Folder tree */}
                <div className="w-48 shrink-0 border-r border-gray-100 bg-gray-50/50 overflow-y-auto hidden md:block">
                    <div className="p-2">
                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider px-2 py-2">Acceso Rápido</p>
                        {[
                            { id: 'web-assets', label: 'Web Assets', icon: <Globe size={13} /> },
                            { id: 'tejiendo', label: 'Tejiendo', icon: <Briefcase size={13} /> },
                            { id: 'shopping', label: 'Shopping', icon: <ShoppingBag size={13} /> },
                            { id: 'tunantada', label: 'Tunantada', icon: <Mic size={13} /> }
                        ].map(b => (
                            <button key={b.id} onClick={() => { handleActiveBucketChange(b.id as any); setActiveFolder(''); }}
                                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs transition-all mb-0.5 ${activeBucket === b.id ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-600 hover:bg-white font-medium'}`}>
                                {b.icon} {b.label}
                            </button>
                        ))}
                        {subFolders.length > 0 && (
                            <>
                                <div className="h-px bg-gray-200 my-2"></div>
                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider px-2 py-1">Carpetas</p>
                                {subFolders.map((f, i) => (
                                    <button key={i} onClick={() => setActiveFolder(activeFolder ? `${activeFolder}/${f}` : f)}
                                        onDragOver={handleDragOver} onDrop={(e) => { e.stopPropagation(); handleDrop(e, f); }}
                                        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-gray-600 hover:bg-white hover:text-blue-600 font-medium transition-all mb-0.5">
                                        <Folder size={13} className="text-yellow-500 fill-yellow-100" /> <span className="truncate">{f}</span>
                                    </button>
                                ))}
                            </>
                        )}
                        {/* Storage info */}
                        <div className="mt-4 px-2 py-3 bg-white rounded-lg border border-gray-100">
                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Almacenamiento</p>
                            <p className="text-lg font-black text-gray-800">{formatBytes(totalGallerySize)}</p>
                            <p className="text-[10px] text-gray-400">{galleryImages.length} archivos</p>
                            <div className="w-full bg-gray-100 h-1.5 rounded-full mt-2 overflow-hidden">
                                <div className="bg-blue-500 h-full rounded-full" style={{ width: '15%' }}></div>
                            </div>
                        </div>

                        {/* WATERMARK CONFIG PANEL */}
                        <div className="mt-3">
                            <button onClick={() => setShowWmConfig(!showWmConfig)}
                                className="w-full flex items-center justify-between px-2 py-2 rounded-lg hover:bg-white text-xs font-bold text-gray-500 transition-colors">
                                <span className="flex items-center gap-1.5"><Droplets size={12} /> Marcas de Agua</span>
                                <ChevronRight size={12} className={`transition-transform ${showWmConfig ? 'rotate-90' : ''}`} />
                            </button>
                            {showWmConfig && (
                                <div className="space-y-3 mt-1">
                                    {(['mabelita', 'tejidos'] as const).map(brand => (
                                        <div key={brand} className="bg-white rounded-lg border border-gray-100 p-2.5">
                                            <p className={`text-[10px] font-black uppercase tracking-wider mb-2 ${brand === 'mabelita' ? 'text-pink-500' : 'text-orange-500'}`}>
                                                {brand === 'mabelita' ? '🌸 Mabelita' : '🧶 Tejidos'}
                                            </p>
                                            {/* Opacity */}
                                            <label className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                                                <span>Opacidad</span><span className="font-mono font-bold">{wmConfig[brand].opacity}%</span>
                                            </label>
                                            <input type="range" min={5} max={100} step={5} value={wmConfig[brand].opacity}
                                                onChange={e => updateWmConfig(brand, 'opacity', Number(e.target.value))}
                                                className="w-full h-1 rounded-full appearance-none bg-gray-200 accent-blue-500 cursor-pointer mb-2" />
                                            {/* Size */}
                                            <label className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                                                <span>Tamaño</span><span className="font-mono font-bold">{wmConfig[brand].size}%</span>
                                            </label>
                                            <input type="range" min={10} max={100} step={5} value={wmConfig[brand].size}
                                                onChange={e => updateWmConfig(brand, 'size', Number(e.target.value))}
                                                className="w-full h-1 rounded-full appearance-none bg-gray-200 accent-blue-500 cursor-pointer mb-2" />
                                            {/* Position */}
                                            <label className="text-[10px] text-gray-500 mb-1 block">Posición</label>
                                            <div className="grid grid-cols-3 gap-0.5 mb-2">
                                                {['top-left', 'top-center', 'top-right', 'center-left', 'center', 'center-right', 'bottom-left', 'bottom-center', 'bottom-right'].map(pos => (
                                                    <button key={pos} onClick={() => updateWmConfig(brand, 'position', pos)}
                                                        className={`h-5 rounded text-[7px] font-bold transition-all ${wmConfig[brand].position === pos
                                                            ? (brand === 'mabelita' ? 'bg-pink-500 text-white' : 'bg-orange-500 text-white')
                                                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
                                                        {pos === 'center' ? '●' : '·'}
                                                    </button>
                                                ))}
                                            </div>
                                            {/* Logo URL */}
                                            <label className="text-[10px] text-gray-500 mb-1 block">Logo (horizontal)</label>
                                            <input type="text" value={wmConfig[brand].logoHor}
                                                onChange={e => updateWmConfig(brand, 'logoHor', e.target.value)}
                                                className="w-full text-[9px] p-1 border border-gray-200 rounded mb-1 font-mono" placeholder="/watermark.png" />
                                            <label className="text-[10px] text-gray-500 mb-1 block">Logo (vertical)</label>
                                            <input type="text" value={wmConfig[brand].logoVer}
                                                onChange={e => updateWmConfig(brand, 'logoVer', e.target.value)}
                                                className="w-full text-[9px] p-1 border border-gray-200 rounded font-mono" placeholder="/watermark_ver.png" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT: Content area */}
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                    {/* Multi select toolbar */}
                    {selectedItems.length > 0 && (
                        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-3 text-sm shrink-0">
                            <span className="font-bold text-blue-700 flex items-center gap-2">
                                <div className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-black">{selectedItems.length}</div>
                                seleccionados
                            </span>
                            <button onClick={() => handleDeleteImage(selectedItems)} className="text-red-600 hover:bg-red-100 font-bold text-xs px-2.5 py-1 rounded flex items-center gap-1"><Trash2 size={13} /> Eliminar</button>
                            <button onClick={() => setSelectedItems([])} className="text-gray-400 hover:text-gray-600 ml-auto"><X size={16} /></button>
                        </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-0" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, activeFolder || 'root')}>
                        {galleryImages.length === 0 && subFolders.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center p-8">
                                <FolderOpen className="h-16 w-16 text-gray-200 mb-4" />
                                <p className="text-gray-400 font-medium">Esta carpeta está vacía</p>
                                <p className="text-xs text-gray-300 mt-1">Sube archivos o crea una subcarpeta</p>
                                <label className="mt-4 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-bold cursor-pointer hover:bg-blue-100 border border-blue-200">
                                    <Upload size={14} className="inline mr-1" /> Subir archivos
                                    <input type="file" multiple className="hidden" onChange={handleBulkUpload} disabled={isSaving} accept="image/*" />
                                </label>
                            </div>
                        ) : (
                            <>
                                {/* Folders row (only when there are subfolders, shown inline at top for grid views) */}
                                {subFolders.length > 0 && galleryGridSize !== 'details' && (
                                    <div className="px-4 pt-3 pb-1">
                                        <div className="flex flex-wrap gap-2">
                                            {subFolders.map((folder, idx) => (
                                                <div key={`f-${idx}`} className="group flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 hover:bg-blue-50 hover:border-blue-200 cursor-pointer transition-all"
                                                    onClick={() => setActiveFolder(activeFolder ? `${activeFolder}/${folder}` : folder)}
                                                    onDragOver={handleDragOver} onDrop={(e) => { e.stopPropagation(); handleDrop(e, folder); }}>
                                                    <Folder size={16} className="text-yellow-500 fill-yellow-100" />
                                                    <span className="text-xs font-semibold text-gray-700 group-hover:text-blue-700">{folder}</span>
                                                    <button onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}
                                                        className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 rounded"><Trash2 size={10} /></button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* DETAILS VIEW — Windows-style table */}
                                {galleryGridSize === 'details' && (
                                    <div className="text-sm">
                                        {/* Table header */}
                                        <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-4 py-1.5 grid grid-cols-[20px_1fr_80px_60px_100px] gap-3 items-center text-[10px] font-bold uppercase tracking-wider text-gray-400 select-none">
                                            <div></div><div>Nombre</div><div className="text-right">Tamaño</div><div className="text-center">Tipo</div><div className="text-center">Acciones</div>
                                        </div>
                                        {/* Folder rows */}
                                        {subFolders.map((folder, idx) => (
                                            <div key={`df-${idx}`} className="px-4 py-1.5 grid grid-cols-[20px_1fr_80px_60px_100px] gap-3 items-center border-b border-gray-50 hover:bg-blue-50 cursor-pointer group transition-colors"
                                                onClick={() => setActiveFolder(activeFolder ? `${activeFolder}/${folder}` : folder)}
                                                onDragOver={handleDragOver} onDrop={(e) => { e.stopPropagation(); handleDrop(e, folder); }}>
                                                <div></div>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Folder size={16} className="text-yellow-500 fill-yellow-100 shrink-0" />
                                                    <span className="text-xs font-semibold text-gray-700 truncate group-hover:text-blue-700">{folder}</span>
                                                </div>
                                                <div className="text-[10px] text-gray-400 text-right">—</div>
                                                <div className="text-center"><span className="text-[9px] font-bold uppercase bg-yellow-50 text-yellow-600 px-1.5 py-0.5 rounded">Carpeta</span></div>
                                                <div className="flex items-center justify-center opacity-0 group-hover:opacity-100" onClick={e => e.stopPropagation()}>
                                                    <button onClick={() => handleDeleteFolder(folder)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={12} /></button>
                                                </div>
                                            </div>
                                        ))}
                                        {/* File rows */}
                                        {galleryImages.map((img, idx) => {
                                            const isSelected = selectedItems.includes(img.name);
                                            const ext = img.name.split('.').pop()?.toUpperCase() || '?';
                                            return (
                                                <div key={idx} className={`px-4 py-1 grid grid-cols-[20px_1fr_80px_60px_100px] gap-3 items-center border-b border-gray-50 cursor-pointer group transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                                    onClick={() => { if (renamingFile !== null) return; const ns = isSelected ? selectedItems.filter(i => i !== img.name) : [...selectedItems, img.name]; setSelectedItems(ns); }}
                                                    draggable="true"
                                                    onDragStart={(e) => { setIsDragging(img.name); const items = isSelected ? selectedItems : [img.name]; e.dataTransfer.setData('application/json', JSON.stringify({ sourceBucket: activeBucket, sourcePath: activeFolder, files: items })); }}
                                                    onDragEnd={() => setIsDragging(null)}>
                                                    {/* Checkbox */}
                                                    <label className="cursor-pointer flex items-center" onClick={e => e.stopPropagation()}>
                                                        <div className="relative w-4 h-4">
                                                            <input type="checkbox" checked={isSelected}
                                                                onChange={() => { const ns = isSelected ? selectedItems.filter(i => i !== img.name) : [...selectedItems, img.name]; setSelectedItems(ns); }}
                                                                className="peer appearance-none w-4 h-4 border border-gray-300 rounded checked:bg-blue-500 checked:border-blue-500 cursor-pointer" />
                                                            <Check size={10} className="absolute inset-0 m-auto text-white opacity-0 peer-checked:opacity-100 pointer-events-none" strokeWidth={3} />
                                                        </div>
                                                    </label>
                                                    {/* Name + thumbnail */}
                                                    <div className="flex items-center gap-2.5 min-w-0" onClick={e => e.stopPropagation()}>
                                                        <div className="w-7 h-7 rounded bg-gray-100 overflow-hidden shrink-0 cursor-zoom-in border border-gray-200" onClick={() => setPreviewImage(img)}>
                                                            <img src={img.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                                                        </div>
                                                        {renamingFile === img.name ? (
                                                            <div className="flex items-center gap-1 flex-1 min-w-0">
                                                                <input type="text" className="flex-1 min-w-0 text-xs p-1 border border-blue-400 rounded outline-none" value={newFileName}
                                                                    onChange={e => setNewFileName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleRenameFile(img.name, newFileName); if (e.key === 'Escape') setRenamingFile(null); }} autoFocus />
                                                                <button onClick={() => handleRenameFile(img.name, newFileName)} className="p-0.5 text-green-600"><Check size={12} /></button>
                                                                <button onClick={() => setRenamingFile(null)} className="p-0.5 text-red-500"><X size={12} /></button>
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-gray-700 truncate hover:text-blue-600 cursor-pointer" onClick={() => { setRenamingFile(img.name); setNewFileName(img.name); }}>{img.name}</span>
                                                        )}
                                                    </div>
                                                    {/* Size */}
                                                    <span className="text-[10px] text-gray-400 font-mono text-right">{formatBytes(img.size, 0)}</span>
                                                    {/* Type */}
                                                    <div className="flex justify-center">
                                                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${ext === 'WEBP' ? 'bg-emerald-50 text-emerald-600' : ext === 'PNG' ? 'bg-blue-50 text-blue-600' : ext === 'JPG' || ext === 'JPEG' ? 'bg-orange-50 text-orange-600' : 'bg-gray-50 text-gray-500'}`}>{ext}</span>
                                                    </div>
                                                    {/* Actions */}
                                                    <div className="flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                                                        <button onClick={() => handleGalleryOptimize(img, 'mabelita')} disabled={isOptimizingGalleryItem === img.name}
                                                            className="p-1 text-pink-400 hover:text-pink-600 hover:bg-pink-50 rounded" title="Sello Mabelita"><span className="text-[9px] font-black">M</span></button>
                                                        <button onClick={() => handleGalleryOptimize(img, 'tejidos')} disabled={isOptimizingGalleryItem === img.name}
                                                            className="p-1 text-orange-400 hover:text-orange-600 hover:bg-orange-50 rounded" title="Sello Tejidos"><span className="text-[9px] font-black">T</span></button>
                                                        <button onClick={() => handleGalleryOptimize(img, 'none')} disabled={isOptimizingGalleryItem === img.name}
                                                            className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="WebP 80%"><ImageIcon size={12} /></button>
                                                        <button onClick={() => { navigator.clipboard.writeText(img.url); setToast({ message: 'URL copiada', type: 'success' }); }}
                                                            className="p-1 text-gray-400 hover:text-blue-600 rounded" title="Copiar URL"><Copy size={12} /></button>
                                                        <button onClick={(e) => { e.preventDefault(); handleDeleteImage(img.name); }}
                                                            className="p-1 text-gray-400 hover:text-red-600 rounded" title="Eliminar"><Trash2 size={12} /></button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* GRID VIEWS (small / medium / large) */}
                                {galleryGridSize !== 'details' && galleryImages.length > 0 && (
                                    <div className="p-4">
                                        <div className={`grid ${getGridClass()}`}>
                                            {galleryImages.map((img, idx) => {
                                                const isSelected = selectedItems.includes(img.name);
                                                return (
                                                    <div key={idx} className={`group bg-white rounded-lg overflow-hidden border ${isSelected ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-gray-200'} shadow-sm hover:shadow-md transition-all flex flex-col relative`}
                                                        draggable="true"
                                                        onDragStart={(e) => { setIsDragging(img.name); const items = isSelected ? selectedItems : [img.name]; e.dataTransfer.setData('application/json', JSON.stringify({ sourceBucket: activeBucket, sourcePath: activeFolder, files: items })); e.dataTransfer.setData('text/plain', img.name); }}
                                                        onDragEnd={() => setIsDragging(null)}
                                                        style={{ opacity: isDragging === img.name ? 0.5 : 1 }}
                                                        onClick={() => { if (renamingFile !== null) return; const ns = isSelected ? selectedItems.filter(i => i !== img.name) : [...selectedItems, img.name]; setSelectedItems(ns); }}>
                                                        <div className="relative aspect-square bg-gray-50 flex items-center justify-center overflow-hidden cursor-zoom-in" onClick={(e) => { e.stopPropagation(); setPreviewImage(img); }}>
                                                            <img src={img.url} alt={img.name} loading="lazy" className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110" />
                                                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                                            <label className="absolute top-1.5 left-1.5 z-10 cursor-pointer" onClick={e => e.stopPropagation()}>
                                                                <div className="relative w-5 h-5">
                                                                    <input type="checkbox" checked={isSelected} onChange={() => { const ns = isSelected ? selectedItems.filter(i => i !== img.name) : [...selectedItems, img.name]; setSelectedItems(ns); }}
                                                                        className="peer appearance-none w-4 h-4 border-2 border-white rounded bg-black/20 checked:bg-blue-500 checked:border-blue-500 cursor-pointer shadow-sm" />
                                                                    <Check size={10} className="absolute inset-0 m-auto text-white opacity-0 peer-checked:opacity-100 pointer-events-none" strokeWidth={3} />
                                                                </div>
                                                            </label>
                                                            <div className="absolute bottom-2 left-2 right-2 flex justify-between gap-1 opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0">
                                                                <div className="flex gap-1">
                                                                    <button onClick={(e) => { e.stopPropagation(); handleGalleryOptimize(img, 'mabelita'); }} disabled={isOptimizingGalleryItem === img.name}
                                                                        className="w-6 h-6 rounded-full bg-pink-500/90 hover:bg-pink-600 text-white flex items-center justify-center shadow transition-transform hover:scale-110" title="Sello Mabelita"><span className="text-[9px] font-black">M</span></button>
                                                                    <button onClick={(e) => { e.stopPropagation(); handleGalleryOptimize(img, 'tejidos'); }} disabled={isOptimizingGalleryItem === img.name}
                                                                        className="w-6 h-6 rounded-full bg-orange-500/90 hover:bg-orange-600 text-white flex items-center justify-center shadow transition-transform hover:scale-110" title="Sello Tejidos"><span className="text-[9px] font-black">T</span></button>
                                                                    <button onClick={(e) => { e.stopPropagation(); handleGalleryOptimize(img, 'none'); }} disabled={isOptimizingGalleryItem === img.name}
                                                                        className="w-6 h-6 rounded-full bg-blue-500/90 hover:bg-blue-600 text-white flex items-center justify-center shadow transition-transform hover:scale-110" title="WebP 80%"><ImageIcon size={11} /></button>
                                                                </div>
                                                                <div className="flex gap-1">
                                                                    <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(img.url); setToast({ message: 'URL copiada', type: 'success' }); }}
                                                                        className="w-6 h-6 rounded-full bg-white/90 text-gray-600 flex items-center justify-center shadow hover:bg-blue-500 hover:text-white transition-colors"><Copy size={11} /></button>
                                                                    <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDeleteImage(img.name); }}
                                                                        className="w-6 h-6 rounded-full bg-white/90 text-gray-600 flex items-center justify-center shadow hover:bg-red-500 hover:text-white transition-colors"><Trash2 size={11} /></button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="px-2 py-1.5 flex items-center justify-between border-t border-gray-100" onClick={e => e.stopPropagation()}>
                                                            {renamingFile === img.name ? (
                                                                <div className="flex items-center gap-1 w-full">
                                                                    <input type="text" className="flex-1 min-w-0 text-[10px] p-1 border border-blue-400 rounded outline-none" value={newFileName}
                                                                        onChange={e => setNewFileName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleRenameFile(img.name, newFileName); if (e.key === 'Escape') setRenamingFile(null); }} autoFocus />
                                                                    <button onClick={() => handleRenameFile(img.name, newFileName)} className="p-0.5 text-green-600"><Check size={10} /></button>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <h5 className="text-[10px] font-medium text-gray-600 truncate cursor-pointer hover:text-blue-600 flex-1" onClick={() => { setRenamingFile(img.name); setNewFileName(img.name); }}>{img.name}</h5>
                                                                    <span className="text-[9px] text-gray-400 font-mono ml-1 shrink-0">{formatBytes(img.size, 0)}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Status bar at bottom like Windows Explorer */}
                    <div className="shrink-0 px-4 py-1.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400 font-medium">
                        <span>{galleryImages.length} archivo{galleryImages.length !== 1 ? 's' : ''}{subFolders.length > 0 ? `, ${subFolders.length} carpeta${subFolders.length !== 1 ? 's' : ''}` : ''}</span>
                        <span>{formatBytes(totalGallerySize)}</span>
                    </div>
                </div>
            </div>

            {/* Social links section */}
            {socialLinks.length > 0 && (
                <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-3 bg-gray-50 border-b flex items-center justify-between">
                        <h4 className="text-sm font-bold flex items-center gap-2"><LinkIcon size={16} className="text-red-500" /> Enlaces ({socialLinks.length})</h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4">
                        {socialLinks.map(link => (
                            <div key={link.id} className={`border rounded-lg p-3 flex flex-col justify-between ${link.is_featured ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${link.platform === 'youtube' ? 'bg-red-100 text-red-700' : link.platform === 'tiktok' ? 'bg-black text-white' : 'bg-pink-100 text-pink-700'}`}>{link.platform}</span>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleToggleFeaturedSocialLink(link.id, link.is_featured)} className={`text-xs ${link.is_featured ? 'text-red-600' : 'text-gray-400 hover:text-red-400'}`}><Star size={14} fill={link.is_featured ? 'currentColor' : 'none'} /></button>
                                        <button onClick={() => handleDeleteSocialLink(link.id)} className="text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                                    </div>
                                </div>
                                <h5 className="font-bold text-sm text-gray-800 line-clamp-1">{link.title || link.platform}</h5>
                                <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline truncate font-mono mt-1">{link.url}</a>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const renderGraphicStudio = () => (
        <div className="animate-in fade-in max-w-7xl mx-auto">
            <div className="mb-8 text-center bg-gray-900 text-white rounded-[2rem] p-8 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-500/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>

                <h2 className="relative z-10 text-4xl font-black flex items-center justify-center gap-3">
                    <span className="p-3 bg-orange-500 text-white rounded-2xl shadow-lg border border-orange-400">
                        <PenTool size={28} />
                    </span>
                    Estudio Gráfico "Pachamanca"
                </h2>
                <p className="relative z-10 text-gray-300 mt-4 max-w-2xl mx-auto text-lg">Diseñando con Mabelita Inteligencia Artificial. Crea logos, banners y assets gráficos en segundos.</p>
            </div>

            <div className="flex flex-col lg:grid lg:grid-cols-4 gap-8">
                {/* LA HISTORIA DE CREACIONES PACHAMANCA (NUEVO SIDEBAR IZQUIERDO) */}
                <div className="lg:col-span-1 hidden lg:block space-y-4">
                    <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider flex items-center gap-2">
                        <History size={16} /> Historial
                    </h3>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 max-h-[800px] overflow-y-auto space-y-3 custom-scrollbar">
                        {creationsHistory.filter(c => c.tool === 'pachamanca').map((creation) => (
                            <div key={creation.id} className="group relative rounded-lg overflow-hidden border border-gray-100 hover:border-orange-300 transition-colors">
                                <img src={creation.image_url} alt="Creation" className="w-full aspect-square object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                                    <p className="text-[10px] text-white line-clamp-2 mb-2 font-medium">{creation.prompt}</p>
                                    <div className="flex gap-1 justify-between">
                                        <button
                                            onClick={() => {
                                                setGsPrompt(creation.prompt || '');
                                                if (creation.parameters?.type) setGsAssetType(creation.parameters.type);
                                                if (creation.parameters?.context) setGsContext(creation.parameters.context);
                                                // Resucita prompt e info, y pone la imagen referencial (opcional)
                                                setToast({ message: 'Parámetros cargados', type: 'info' });
                                            }}
                                            className="bg-white text-orange-600 p-1.5 rounded-md text-xs font-bold hover:bg-orange-50 flex-1 flex items-center justify-center gap-1"
                                            title="Usar estos parámetros"
                                        >
                                            <RefreshCw size={12} /> Rescatar
                                        </button>
                                        <button
                                            onClick={async () => {
                                                await supabase.from('ai_creations').delete().eq('id', creation.id);
                                                fetchCreationsHistory();
                                                setToast({ message: 'Historial eliminado', type: 'success' });
                                            }}
                                            className="bg-red-500 text-white p-1.5 rounded-md hover:bg-red-600"
                                            title="Borrar del historial"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {creationsHistory.filter(c => c.tool === 'pachamanca').length === 0 && (
                            <div className="text-center p-6 text-gray-400">
                                <PenTool size={32} className="mx-auto mb-2 opacity-50" />
                                <p className="text-xs">Sin historial aún</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* CONTROLES PRINCIPALES */}
                <div className="lg:col-span-3">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6 flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Pachamanca</h2>
                            <p className="text-gray-500 text-sm">Estudio de Diseño Gráfico & Silueteador Profesional</p>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-full text-orange-600"><PenTool size={24} /></div>
                    </div>

                    <div className="flex flex-col lg:grid lg:grid-cols-3 gap-8">
                        <div className="order-2 lg:order-1 lg:col-span-1 space-y-6 bg-white rounded-2xl p-6 shadow-sm border border-gray-100 h-fit">
                            <h3 className="font-bold text-gray-800 mb-4 text-sm uppercase tracking-wider">1. Configuración</h3>

                            <div className="flex flex-wrap gap-2 mb-4">
                                {[
                                    { id: 'logo', label: 'Logo' },
                                    { id: 'banner', label: 'Banner' },
                                    { id: 'merch', label: 'Merch' },
                                    { id: 'social', label: 'Social' },
                                    { id: 'remover', label: 'Silueteador', icon: <Scissors size={12} /> }
                                ].map(t => (
                                    <button
                                        key={t.id}
                                        onClick={() => setGsAssetType(t.id as any)}
                                        className={`flex items-center gap-1 px-3 py-2 rounded-lg border text-xs font-bold uppercase ${gsAssetType === t.id ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-500'}`}
                                    >
                                        {t.icon}{t.label}
                                    </button>
                                ))}
                            </div>

                            {gsAssetType === 'remover' ? (
                                /* INTERFAZ SILUETEADOR */
                                <div className="animate-in slide-in-from-left-2">
                                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 mb-4">
                                        <p className="text-xs text-gray-600 mb-2">
                                            Elimina el fondo de tus fotos usando la API profesional de Remove.bg.
                                        </p>
                                        <div className="flex items-center gap-2 text-xs font-bold text-green-600">
                                            <Zap size={14} /> API Conectada
                                        </div>
                                    </div>

                                    <h3 className="font-bold text-gray-800 mb-2 text-sm uppercase tracking-wider">2. Subir Imagen</h3>
                                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-white hover:bg-gray-50 transition-colors group relative mb-4">
                                        {isRemovingBg ? (
                                            <div className="flex flex-col items-center text-center px-2">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mb-2"></div>
                                                <p className="text-xs text-gray-500 font-bold animate-pulse">{bgRemovalStatus}</p>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <Upload className="w-8 h-8 mb-2 text-gray-400 group-hover:text-orange-500 transition-colors" />
                                                <p className="text-xs text-gray-500">Click o arrastrar imagen</p>
                                            </div>
                                        )}
                                        <input type="file" className="hidden" accept="image/*" onChange={handleBgRemovalUpload} disabled={isRemovingBg} />
                                    </label>

                                    {/* NUEVO BOTÓN GALERÍA PARA SILUETEADOR */}
                                    <button onClick={openGalleryForRemover} className="w-full py-2 text-xs font-bold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 mb-4 transition-colors border border-blue-200">
                                        📂 O buscar en Galería
                                    </button>

                                    {bgRemoverUrl && (
                                        <div className="space-y-2">
                                            <Button onClick={downloadBgRemoved} className="w-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center gap-2 text-xs">
                                                <Download size={16} /> Descargar PNG
                                            </Button>
                                            <Button onClick={saveBgRemovedToBucket} disabled={isSaving} className="w-full bg-black hover:bg-gray-800 flex items-center justify-center gap-2 text-xs">
                                                <Save size={16} /> Guardar en {activeBucket}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* INTERFAZ GENERADOR IA (STANDARD) */
                                <div className="animate-in slide-in-from-left-2">
                                    {/* CONTEXTO */}
                                    <h3 className="font-bold text-gray-800 mb-2 text-sm uppercase tracking-wider mt-6">2. Contexto de Marca</h3>
                                    <div className="flex flex-col gap-2 mb-4">
                                        <button onClick={() => setGsContext('cantante')} className={`flex items-center gap-2 p-2 rounded-lg border ${gsContext === 'cantante' ? 'border-mabelita-primary bg-red-50' : 'border-gray-100'}`}>
                                            <MusicIcon size={16} /> <span className="text-sm font-medium text-gray-800">Cantante (Folklore)</span>
                                        </button>
                                        <button onClick={() => setGsContext('tejiendo')} className={`flex items-center gap-2 p-2 rounded-lg border ${gsContext === 'tejiendo' ? 'border-mabelita-accent bg-orange-50' : 'border-gray-100'}`}>
                                            <Briefcase size={16} /> <span className="text-sm font-medium text-gray-800">Tejiendo con Mabelita</span>
                                        </button>
                                        <button onClick={() => setGsContext('shopping')} className={`flex items-center gap-2 p-2 rounded-lg border ${gsContext === 'shopping' ? 'border-purple-500 bg-purple-50' : 'border-gray-100'}`}>
                                            <ShoppingBag size={16} /> <span className="text-sm font-medium text-gray-800">Shopping con Mabelita</span>
                                        </button>
                                    </div>

                                    {/* REFERENCIA */}
                                    <h3 className="font-bold text-gray-800 mb-2 text-sm uppercase tracking-wider mt-6">3. Referencia (Opcional)</h3>
                                    <label className="block w-full p-3 border-2 border-dashed border-gray-300 rounded-xl text-center cursor-pointer hover:bg-gray-50 transition-colors mb-2 bg-white">
                                        {gsReferenceImage ? <span className="text-green-600 font-bold text-sm flex items-center justify-center gap-2"><Check size={14} /> Imagen cargada</span> : <span className="text-gray-400 text-sm flex items-center justify-center gap-2"><Upload size={14} /> Subir imagen guía</span>}
                                        <input type="file" hidden accept="image/*" onChange={handleGsReferenceUpload} />
                                    </label>
                                    <button onClick={openGalleryForPachamanca} className="w-full py-2 text-xs font-bold text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100 mb-4 transition-colors border border-orange-200">
                                        📂 O buscar en Galería
                                    </button>

                                    {/* PROMPT */}
                                    <div className="flex justify-between items-center mt-6 mb-2">
                                        <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider">4. Instrucciones</h3>
                                        <button
                                            onClick={() => handleImprovePrompt('pachamanca')}
                                            disabled={isImprovingPrompt || !gsPrompt}
                                            className="text-xs flex items-center gap-1 bg-purple-50 text-purple-700 hover:bg-purple-100 px-2 py-1 rounded-md font-bold transition-colors disabled:opacity-50"
                                        >
                                            <Wand2 size={12} /> {isImprovingPrompt ? 'Optimizando...' : '🪄 Mejorar Prompt'}
                                        </button>
                                    </div>
                                    <textarea
                                        className="w-full p-3 border rounded-xl bg-white text-gray-900 text-sm min-h-[100px] border-gray-300 placeholder-gray-500 focus:border-orange-500 outline-none"
                                        placeholder="Ej: Un logo circular con una llama minimalista..."
                                        value={gsPrompt}
                                        onChange={(e) => setGsPrompt(e.target.value)}
                                    ></textarea>

                                    <Button
                                        onClick={handleGsGenerate}
                                        disabled={isGsGenerating}
                                        className="w-full mt-6 bg-orange-600 hover:bg-orange-700 text-white shadow-lg py-3"
                                    >
                                        {isGsGenerating ? 'Diseñando...' : <span className="flex items-center gap-2 justify-center"><Wand2 size={18} /> Generar Diseño</span>}
                                    </Button>
                                </div>
                            )}
                        </div>
                        {/* PREVIEW AREA */}
                        <div className="order-1 lg:order-2 lg:col-span-2">
                            <div
                                className="h-full rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center relative p-8 min-h-[500px] overflow-hidden bg-gray-50"
                                style={{
                                    backgroundImage: gsAssetType === 'remover' ? `
                            linear-gradient(45deg, #e0e0e0 25%, transparent 25%), 
                            linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), 
                            linear-gradient(45deg, transparent 75%, #e0e0e0 75%), 
                            linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
                        ` : 'none',
                                    backgroundSize: '20px 20px',
                                    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                                }}
                            >
                                {gsAssetType === 'remover' ? (
                                    bgRemoverUrl ? (
                                        <div className="relative group w-full h-full flex items-center justify-center">
                                            <img src={bgRemoverUrl} className="max-w-full max-h-[500px] object-contain drop-shadow-2xl animate-in zoom-in duration-300" alt="Sin fondo" />
                                            <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-xs font-mono">
                                                Fondo eliminado
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center text-gray-400 opacity-50">
                                            <Scissors size={64} className="mx-auto mb-4" />
                                            <p className="text-xl font-serif">Silueteador Pro (Remove.bg)</p>
                                        </div>
                                    )
                                ) : (
                                    gsGeneratedImage ? (
                                        <div className="relative group animate-in zoom-in duration-300 w-full h-full flex items-center justify-center">
                                            <img src={gsGeneratedImage} className="max-w-full max-h-[500px] object-contain shadow-2xl" alt="Generated" />
                                            <div className="absolute bottom-4 flex gap-4 bg-white p-2 rounded-full shadow-xl border border-gray-100">
                                                <a href={gsGeneratedImage} download={`pachamanca-${gsAssetType}.png`} className="p-3 hover:bg-gray-100 rounded-full text-gray-700" title="Descargar"><Download size={20} /></a>
                                                <button onClick={saveGsImage} className="px-6 py-2 bg-black text-white rounded-full font-bold text-sm hover:bg-gray-800 flex items-center gap-2">
                                                    <Check size={16} /> Guardar en {activeBucket}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center text-gray-400 opacity-50">
                                            <PenTool size={64} className="mx-auto mb-4" />
                                            <p className="text-xl font-serif">Estudio de Diseño Gráfico</p>
                                            <p className="text-sm">Pachamanca</p>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderLaChacra = () => (
        <div className="animate-in fade-in max-w-6xl mx-auto">
            <div className="mb-8 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-green-100 text-green-800 text-sm font-bold mb-2 border border-green-200 uppercase tracking-wider">
                    <Sprout size={14} /> Mabelita IA Photo Studio
                </div>
                <h2 className="text-4xl font-serif font-bold text-gray-900 flex items-center justify-center gap-2">
                    La Chacra
                </h2>
                <p className="text-gray-500 mt-2">Centro de Cultivo Creativo: Siembra ideas nuevas o optimiza tus fotos existentes.</p>
            </div>

            <div className="flex flex-col lg:grid lg:grid-cols-12 gap-8">
                {/* LA HISTORIA DE CREACIONES LA CHACRA (NUEVO SIDEBAR IZQUIERDO) */}
                <div className="order-3 lg:order-1 lg:col-span-2 hidden lg:block space-y-4">
                    <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider flex items-center gap-2">
                        <History size={16} /> Cosechas
                    </h3>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 max-h-[800px] overflow-y-auto space-y-2 custom-scrollbar">
                        {creationsHistory.filter(c => c.tool === 'lachacra').map((creation) => (
                            <div key={creation.id} className="group relative rounded-lg overflow-hidden border border-gray-100 hover:border-green-300 transition-colors">
                                <img src={creation.image_url} alt="Creation" className="w-full aspect-square object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                                    <p className="text-[10px] text-white line-clamp-2 mb-2 font-medium">{creation.prompt}</p>
                                    <div className="flex gap-1 justify-between">
                                        <button
                                            onClick={() => {
                                                setPsPrompt(creation.prompt || '');
                                                if (creation.parameters?.mode) setPsWorkMode(creation.parameters.mode);
                                                if (creation.parameters?.tool) setPsMagicTool(creation.parameters.tool);
                                                if (creation.parameters?.ratio) setPsAspectRatio(creation.parameters.ratio);
                                                setToast({ message: 'Parámetros cargados', type: 'info' });
                                            }}
                                            className="bg-white text-green-700 p-1.5 rounded-md text-xs font-bold hover:bg-green-50 flex-1 flex items-center justify-center gap-1"
                                            title="Usar estos parámetros"
                                        >
                                            <RefreshCw size={12} /> Rescatar
                                        </button>
                                        <button
                                            onClick={async () => {
                                                await supabase.from('ai_creations').delete().eq('id', creation.id);
                                                fetchCreationsHistory();
                                                setToast({ message: 'Historial eliminado', type: 'success' });
                                            }}
                                            className="bg-red-500 text-white p-1.5 rounded-md hover:bg-red-600"
                                            title="Borrar del historial"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {creationsHistory.filter(c => c.tool === 'lachacra').length === 0 && (
                            <div className="text-center p-6 text-gray-400">
                                <Sprout size={32} className="mx-auto mb-2 opacity-50" />
                                <p className="text-xs">Sin historial aún</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* SIDEBAR TOOLS */}
                <div className="order-2 lg:order-2 lg:col-span-3 space-y-6">
                    <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">

                        {/* MODE SELECTOR */}
                        <div className="flex p-1 bg-gray-100 rounded-xl mb-6">
                            <button
                                onClick={() => { setPsWorkMode('seed'); setPsSourceImage(null); setPsResultImage(null); }}
                                className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${psWorkMode === 'seed' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <Sprout size={16} /> Sembrar (Crear)
                            </button>
                            <button
                                onClick={() => { setPsWorkMode('prune'); setPsResultImage(null); }}
                                className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${psWorkMode === 'prune' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <Scissors size={16} /> Podar (Editar)
                            </button>
                        </div>

                        {/* SOURCE UPLOAD (Only for Prune Mode) */}
                        {psWorkMode === 'prune' && (
                            <div className="mb-6 animate-in slide-in-from-top-2">
                                <h3 className="font-bold text-gray-800 mb-2 text-sm uppercase tracking-wider">1. Imagen Base</h3>
                                <label className="block w-full aspect-video bg-white rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-all overflow-hidden relative group mb-2">
                                    {psSourceImage ? (
                                        <>
                                            <img src={psSourceImage} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-40 transition-opacity" />
                                            <div className="relative z-10 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold shadow-sm flex items-center gap-1">
                                                <RefreshCw size={12} /> Cambiar
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-gray-400 text-center p-4">
                                            <Upload size={32} className="mx-auto mb-2" />
                                            <span className="text-xs font-bold">Subir foto</span>
                                        </div>
                                    )}
                                    <input type="file" hidden accept="image/*" onChange={handlePsUpload} />
                                </label>
                                <button onClick={openGalleryForLaChacra} className="w-full py-2 text-xs font-bold text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors border border-purple-200">
                                    📂 O buscar en Galería
                                </button>
                            </div>
                        )}

                        {/* ASPECT RATIO */}
                        <div className="mb-6">
                            <h3 className="font-bold text-gray-800 mb-2 text-sm uppercase tracking-wider">2. Formato de Cultivo</h3>
                            <div className="grid grid-cols-4 gap-2">
                                {[
                                    { r: '1:1', icon: <Grid size={14} />, l: 'Cuadrado' },
                                    { r: '9:16', icon: <Smartphone size={14} />, l: 'Historia' },
                                    { r: '16:9', icon: <Monitor size={14} />, l: 'Web' },
                                    { r: '4:5', icon: <Instagram size={14} />, l: 'Retrato' }
                                ].map((ar) => (
                                    <button
                                        key={ar.r}
                                        onClick={() => setPsAspectRatio(ar.r as any)}
                                        className={`flex flex-col items-center justify-center p-2 rounded-lg border transition-all ${psAspectRatio === ar.r ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 text-gray-500'}`}
                                    >
                                        {ar.icon}
                                        <span className="text-[10px] font-bold mt-1">{ar.l}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* MAGIC TOOLS */}
                        <div className="mb-6">
                            <h3 className="font-bold text-gray-800 mb-2 text-sm uppercase tracking-wider">3. Herramientas Mágicas</h3>
                            <div className="grid grid-cols-2 gap-2 max-h-[250px] overflow-y-auto pr-1 custom-scrollbar">
                                {/* OPTIMIZADOR DESTACADO */}
                                <button
                                    onClick={() => setPsMagicTool('optimizer')}
                                    className={`col-span-2 p-3 rounded-lg text-left border-2 transition-all flex items-center gap-3 ${psMagicTool === 'optimizer' ? 'border-blue-500 bg-blue-50 text-blue-900 shadow-sm' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                >
                                    <div className={`p-2 rounded-full bg-white ${psMagicTool === 'optimizer' ? 'text-blue-600' : 'text-gray-400'}`}><Zap size={20} /></div>
                                    <div>
                                        <div className="text-sm font-bold">Optimizador Web Total</div>
                                        <div className="text-[10px] opacity-70">Reduce peso, mejora foco y luz HD</div>
                                    </div>
                                </button>

                                {[
                                    { id: 'restoration', label: 'Restauración HD', desc: 'Enfoque y luz', icon: <RefreshCw size={16} /> },
                                    { id: 'product', label: 'Producto Pro', desc: 'Estudio E-commerce', icon: <ShoppingBag size={16} /> },
                                    { id: 'andean', label: 'Filtro Andino', desc: 'Tonos tierra cálidos', icon: <Sun size={16} /> },
                                    { id: 'fashion', label: 'Moda Editorial', desc: 'Alto contraste', icon: <Camera size={16} /> },
                                    { id: 'background', label: 'Cambiar Fondo', desc: 'Nuevo entorno', icon: <Scissors size={16} /> },
                                    { id: 'social', label: 'Redes Sociales', desc: 'Optimizado viral', icon: <Smartphone size={16} /> },
                                    { id: 'creative', label: 'Creativo', desc: 'Remix artístico', icon: <PaintBucket size={16} /> },
                                    { id: 'stage', label: 'Escenario', desc: 'Luces concierto', icon: <Aperture size={16} /> }
                                ].map(tool => (
                                    <button
                                        key={tool.id}
                                        onClick={() => setPsMagicTool(tool.id)}
                                        className={`p-3 rounded-lg text-left border transition-all flex items-start gap-3 ${psMagicTool === tool.id ? 'border-green-500 bg-green-50 text-green-900 shadow-sm' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        <div className={`mt-0.5 ${psMagicTool === tool.id ? 'text-green-600' : 'text-gray-400'}`}>{tool.icon}</div>
                                        <div>
                                            <div className="text-xs font-bold">{tool.label}</div>
                                            <div className="text-[10px] opacity-70">{tool.desc}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* EDITABLE MAGIC PROMPT */}
                        {psWorkMode === 'prune' && (
                            <div className="mb-6 animate-in slide-in-from-top-2">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider flex items-center gap-1">
                                        <Settings size={14} /> Prompt Base de Herramienta
                                    </h3>
                                    <button
                                        onClick={() => setMagicPrompts(prev => ({ ...prev, [psMagicTool]: defaultMagicPrompts[psMagicTool] }))}
                                        className="text-[10px] text-purple-600 hover:text-purple-800 underline"
                                        title="Restaurar prompt por defecto"
                                    >
                                        Restaurar default
                                    </button>
                                </div>
                                <textarea
                                    className="w-full p-3 border rounded-lg text-xs bg-purple-50 text-purple-900 border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none transition-all placeholder-gray-500 font-mono"
                                    rows={4}
                                    value={magicPrompts[psMagicTool] || ''}
                                    onChange={(e) => setMagicPrompts(prev => ({ ...prev, [psMagicTool]: e.target.value }))}
                                    placeholder="Instrucción principal para la IA..."
                                />
                                <p className="text-[10px] text-gray-500 mt-1">Este es el prompt base que se envía a la IA. Puedes modificarlo libremente para afinar el resultado.</p>
                            </div>
                        )}

                        {/* PROMPT */}
                        <div className="mb-6 animate-in slide-in-from-top-2">
                            <div className="flex justify-between items-center mb-2">
                                <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider">4. {psWorkMode === 'seed' ? '¿Qué quieres sembrar?' : 'Instrucciones extra (Opcional)'}</h3>
                                <button
                                    onClick={() => handleImprovePrompt('lachacra')}
                                    disabled={isImprovingPrompt || !psPrompt}
                                    className="text-xs flex items-center gap-1 bg-purple-50 text-purple-700 hover:bg-purple-100 px-2 py-1 rounded-md font-bold transition-colors disabled:opacity-50"
                                >
                                    <Wand2 size={12} /> {isImprovingPrompt ? 'Optimizando...' : '🪄 Mejorar Prompt'}
                                </button>
                            </div>
                            <input
                                type="text"
                                className="w-full p-3 border rounded-lg text-sm bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-green-500 outline-none transition-all placeholder-gray-500"
                                placeholder={psWorkMode === 'seed' ? "Ej: Una llama con gafas de sol en Machu Picchu" : "Ej: Hacer más brillante, estilo atardecer..."}
                                value={psPrompt}
                                onChange={(e) => setPsPrompt(e.target.value)}
                            />
                        </div>

                        <Button
                            onClick={handlePsProcess}
                            disabled={isPsProcessing || (psWorkMode === 'prune' && !psSourceImage)}
                            className={`w-full shadow-lg py-3 ${psWorkMode === 'seed' ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                        >
                            {isPsProcessing ? 'Cosechando...' : <span className="flex items-center gap-2 justify-center"><Sparkles size={18} /> {psWorkMode === 'seed' ? 'Generar Imagen' : 'Aplicar Magia'}</span>}
                        </Button>

                    </div>
                </div>

                {/* WORKSPACE */}
                <div className="order-1 lg:order-3 lg:col-span-7 bg-gray-900 rounded-2xl p-8 flex items-center justify-center relative min-h-[400px] lg:min-h-[600px] overflow-hidden shadow-inner border border-gray-800">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/dark-matter.png')] opacity-30"></div>

                    {psResultImage ? (
                        <div className="relative w-full h-full flex flex-col items-center justify-center animate-in zoom-in duration-500">
                            <div className="relative max-w-full max-h-[550px] group">
                                <img src={psResultImage} className="max-w-full max-h-[550px] object-contain rounded-sm shadow-[0_0_50px_rgba(0,0,0,0.5)]" alt="Resultado Mágico" />

                                {psStats && (
                                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/80 backdrop-blur-md border border-green-500/50 rounded-xl p-4 flex items-center gap-6 text-white shadow-2xl animate-in slide-in-from-bottom-4">
                                        <div className="text-center border-r border-gray-600 pr-4">
                                            <div className="text-[10px] text-gray-400 uppercase tracking-wider">Original</div>
                                            <div className="text-xl font-bold text-gray-300">{(psStats.original / 1024).toFixed(1)} KB</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-[10px] text-green-400 uppercase tracking-wider">Optimizado</div>
                                            <div className="text-2xl font-bold text-green-400">{(psStats.optimized / 1024).toFixed(1)} KB</div>
                                        </div>
                                        <div className="ml-2 bg-green-900/50 text-green-300 px-2 py-1 rounded text-xs font-bold">
                                            -{(100 - (psStats.optimized / psStats.original) * 100).toFixed(0)}% Peso
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-8 flex gap-4 z-10">
                                <button onClick={() => setPsResultImage(null)} className="px-6 py-3 bg-gray-800 text-white rounded-full hover:bg-gray-700 font-bold transition-colors border border-gray-700">
                                    Descartar
                                </button>
                                <button onClick={savePsImage} className="px-8 py-3 bg-white text-green-900 rounded-full hover:bg-green-50 font-bold shadow-lg transition-transform hover:scale-105 flex items-center gap-2">
                                    <Check size={18} /> Guardar en Galería
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center text-gray-600 select-none">
                            <Sprout size={80} className="mx-auto mb-4 opacity-20 text-green-500" />
                            <h3 className="text-3xl font-serif text-gray-500 mb-2">La Chacra</h3>
                            <p className="text-sm text-gray-500 max-w-md mx-auto">Espacio de trabajo vacío. Configura tu cultivo en el panel izquierdo y pulsa el botón mágico.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // --- PREVIEW MODAL ---

    const renderPreviewModal = () => {
        if (!previewImage) return null;

        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 animate-in fade-in duration-200">
                <div className="absolute top-4 right-4 flex gap-4 z-20">
                    <button onClick={() => setPreviewImage(null)} className="text-white/70 hover:text-white p-2 bg-white/10 rounded-full transition-colors">
                        <X size={32} />
                    </button>
                </div>

                <div className="w-full h-full flex flex-col items-center justify-center">
                    {/* Main Image */}
                    <img
                        src={previewImage.url}
                        alt={previewImage.name}
                        className="max-w-full max-h-[80vh] object-contain shadow-2xl rounded-lg"
                    />

                    {/* Stats Bar */}
                    <div className="mt-8 bg-white/10 border border-white/20 backdrop-blur-lg rounded-2xl p-6 text-white max-w-2xl w-full flex justify-between items-center">
                        <div>
                            <h3 className="font-bold text-lg truncate max-w-[200px] md:max-w-md">{previewImage.name}</h3>
                            <div className="flex gap-4 text-sm text-white/70 mt-1">
                                <span>Peso: {formatBytes(previewImage.size)}</span>
                            </div>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            <button onClick={() => { handleGalleryOptimize(previewImage, 'mabelita'); setPreviewImage(null); }} disabled={isOptimizingGalleryItem === previewImage.name}
                                className="px-3 py-2 bg-pink-600 hover:bg-pink-500 rounded-lg font-bold text-sm flex items-center gap-2 disabled:opacity-50">
                                <span className="font-black">M</span> Sello Mabelita
                            </button>
                            <button onClick={() => { handleGalleryOptimize(previewImage, 'tejidos'); setPreviewImage(null); }} disabled={isOptimizingGalleryItem === previewImage.name}
                                className="px-3 py-2 bg-orange-600 hover:bg-orange-500 rounded-lg font-bold text-sm flex items-center gap-2 disabled:opacity-50">
                                <span className="font-black">T</span> Sello Tejidos
                            </button>
                            <button onClick={() => { handleGalleryOptimize(previewImage, 'none'); setPreviewImage(null); }} disabled={isOptimizingGalleryItem === previewImage.name}
                                className="px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg font-bold text-sm flex items-center gap-2 disabled:opacity-50">
                                <ImageIcon size={14} /> WebP 80%
                            </button>
                            <button onClick={() => {
                                navigator.clipboard.writeText(previewImage.url);
                                setToast({ message: 'URL copiada', type: 'success' });
                            }} className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-bold text-sm flex items-center gap-2">
                                <Copy size={14} /> Copiar URL
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleDeleteImage(previewImage.name);
                                }}
                                className="px-3 py-2 bg-red-600/20 hover:bg-red-600 hover:text-white text-red-200 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors"
                            >
                                <Trash2 size={14} className="pointer-events-none" /> Borrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderOverview = () => {
        const totalSales = pedidos.reduce((acc, p) => acc + (p.total || 0), 0);
        const lowStock = products.filter(p => (p.stock || 0) < 5).length;

        return (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                            Dashboard <span className="text-indigo-600">Mabelita</span>
                        </h1>
                        <p className="text-gray-500 font-medium mt-1">Bienvenida al centro de control de Mabelita.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={fetchAllData} variant="outline" className="rounded-xl border-gray-200">
                            <RefreshCw size={16} className="mr-2" /> Actualizar
                        </Button>
                        <Button onClick={() => { setEditingProduct({}); setIsProductModalOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg">
                            <Plus size={16} className="mr-2" /> Nuevo
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                        { label: 'Ventas Totales', value: `€${totalSales.toFixed(2)}`, icon: <DollarSign className="text-emerald-600" />, trend: '+12%', color: 'from-emerald-50' },
                        { label: 'Pedidos Totales', value: pedidos.length.toString(), icon: <ShoppingBag className="text-indigo-600" />, trend: 'Actualizado', color: 'from-indigo-50' },
                        { label: 'Stock Bajo', value: lowStock.toString(), icon: <AlertTriangle className={lowStock > 0 ? "text-orange-600" : "text-gray-400"} />, trend: 'Alerta', color: 'from-orange-50' },
                        { label: 'Llamadas API', value: apiLogs.length.toString(), icon: <Terminal className="text-blue-600" />, trend: 'En vivo', color: 'from-blue-50' }
                    ].map((kpi, i) => (
                        <div key={i} className={`bg-gradient-to-br ${kpi.color} to-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all`}>
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-white rounded-xl shadow-sm">{kpi.icon}</div>
                                <span className="text-[10px] font-bold px-2 py-1 bg-white/50 rounded-full border">{kpi.trend}</span>
                            </div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{kpi.label}</p>
                            <h3 className="text-2xl font-black text-gray-900 mt-1">{kpi.value}</h3>
                        </div>
                    ))}
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
                            <h3 className="font-bold text-xl mb-6 text-gray-900">Actividad de Ventas</h3>
                            <div className="h-48 flex items-end justify-between gap-1 overflow-hidden">
                                {[40, 70, 45, 90, 65, 80, 55, 60, 40, 75, 50, 85].map((h, i) => (
                                    <div key={i} className="flex-1 bg-indigo-600/10 rounded-t-lg hover:bg-indigo-600/30 transition-all cursor-pointer group relative" style={{ height: `${h}%` }}>
                                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                            €{(h * 12).toFixed(0)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-between mt-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                <span>Enero</span>
                                <span>Junio</span>
                                <span>Diciembre</span>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-bold text-xl text-gray-900">Pedidos Recientes</h3>
                                <Button variant="ghost" onClick={() => setActiveView('orders')} className="text-indigo-600 font-bold">Ver Todos</Button>
                            </div>
                            <div className="space-y-4">
                                {pedidos.slice(0, 3).map(p => (
                                    <div key={p.id} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-transparent hover:border-gray-100 transition-all">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm border text-indigo-600 font-black">
                                                {p.id.toString().slice(-2)}
                                            </div>
                                            <div>
                                                <p className="font-bold text-sm text-gray-900">#{p.id.toString().substring(0, 8)}</p>
                                                <p className="text-[10px] text-gray-500 font-medium uppercase">{new Date(p.created_at).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-gray-900">€{p.total?.toFixed(2)}</p>
                                            <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${p.status === 'pagado' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                                                {p.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="bg-gradient-to-br from-indigo-900 to-indigo-800 p-8 rounded-[2rem] text-white shadow-xl relative overflow-hidden group">
                            <Zap className="absolute -right-10 -bottom-10 text-white/10 group-hover:scale-110 transition-transform duration-700" size={180} />
                            <h3 className="font-bold text-xl mb-2 relative z-10">Stripe Sync</h3>
                            <p className="text-indigo-100/80 text-sm mb-6 relative z-10">Sincroniza tu catálogo actual con la pasarela de pagos para habilitar el Checkout.</p>
                            <Button onClick={handleSyncAllProducts} className="w-full bg-white text-indigo-900 hover:bg-blue-50 font-black rounded-2xl py-6 relative z-10">
                                Sincronizar Catálogo
                            </Button>
                        </div>

                        <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
                            <h3 className="font-bold text-lg mb-6 text-gray-900 flex items-center gap-2">
                                <Activity size={18} className="text-blue-500" /> API Metrics
                            </h3>
                            <div className="space-y-4">
                                {apiLogs.slice(0, 4).map((log, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className={`w-1 h-10 rounded-full ${log.status === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-bold text-gray-900 truncate uppercase tracking-tighter">{log.endpoint}</p>
                                            <p className="text-[9px] text-gray-400 font-medium uppercase tracking-widest">{log.timestamp}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const MusicIcon = ({ size }: { size: number }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>
    );

    const renderThemeStudio = () => (
        <div className="max-w-[1600px] mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div className="flex bg-gray-100 p-1 rounded-xl w-full md:w-auto mt-4 md:mt-0">
                    <button
                        onClick={() => { setThemeContext('singer'); setThemeTab('hero'); }}
                        className={`flex-1 md:flex-none px-6 py-2 rounded-lg font-bold transition-all ${themeContext === 'singer' ? 'bg-white text-gray-900 shadow-md' : 'text-gray-500 hover:bg-gray-200'}`}
                    >🎤 La Cantante</button>
                    <button
                        onClick={() => { setThemeContext('weaver'); setThemeTab('dual_cards'); }}
                        className={`flex-1 md:flex-none px-6 py-2 rounded-lg font-bold transition-all ${themeContext === 'weaver' ? 'bg-mabelita-primary text-white shadow-md' : 'text-gray-500 hover:bg-gray-200'}`}
                    >🧶 La Tejedora</button>
                </div>
                <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Theme Studio</h2>
                    <p className="text-gray-500 text-sm">Personaliza tu web página por página.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                    <Button type="button" onClick={saveConfig} disabled={isSaving} className="flex items-center gap-2 shadow-lg w-full sm:w-auto justify-center">
                        {isSaving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save size={18} />}
                        {isSaving ? ' Guardando...' : ' Guardar Cambios'}
                    </Button>
                    <Button type="button" variant="outline" onClick={handleSyncDefaults} disabled={isSaving} className="flex items-center gap-2 shadow-sm w-full sm:w-auto justify-center bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50 hover:text-indigo-800">
                        <ArrowRight size={16} /> Sync Defaults
                    </Button>
                </div>
            </div>

            <div className="grid xl:grid-cols-12 gap-8">
                {/* PANEL ÚNICO FULL WIDTH - CONTROLES */}
                <div className="xl:col-span-12 flex flex-col gap-6 max-w-5xl mx-auto w-full">
                    <div className="flex gap-1 bg-white p-1 rounded-xl border border-gray-200 overflow-x-auto">
                        {[
                            { id: 'global', label: 'Global', icon: <Globe size={16} />, ctx: 'both' },
                            ...(themeContext === 'singer' ? [
                                { id: 'hero', label: 'Hero', icon: <ImageIcon size={16} />, ctx: 'singer' },
                                { id: 'marquee', label: 'Marquee', icon: <Layers size={16} />, ctx: 'singer' },
                                { id: 'bento', label: 'Bento Box', icon: <Globe size={16} />, ctx: 'singer' },
                                { id: 'gallery', label: 'Galería', icon: <PlayCircle size={16} />, ctx: 'singer' },
                                { id: 'tour', label: 'Tour', icon: <PlayCircle size={16} />, ctx: 'singer' },
                                { id: 'artist', label: 'Artista', icon: <Music size={16} />, ctx: 'singer' },
                                { id: 'reviews', label: 'Reseñas', icon: <Star size={16} />, ctx: 'singer' },
                            ] : [
                                { id: 'hero_weaver', label: 'Hero', icon: <ImageIcon size={16} />, ctx: 'weaver' },
                                { id: 'materials', label: 'Materiales', icon: <Layers size={16} />, ctx: 'weaver' },
                                { id: 'handmade', label: 'Galería', icon: <PlayCircle size={16} />, ctx: 'weaver' },
                                { id: 'reviews', label: 'Reseñas', icon: <Star size={16} />, ctx: 'weaver' },
                                { id: 'encargos', label: 'Encargos', icon: <Briefcase size={16} />, ctx: 'weaver' },
                            ]),
                            { id: 'banner', label: 'Banner', icon: <Radio size={16} />, ctx: 'both' },
                            { id: 'footer', label: 'Footer', icon: <Layout size={16} />, ctx: 'both' }
                        ].map(tab => (
                            <button
                                key={tab.id} onClick={() => setThemeTab(tab.id as any)}
                                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${themeTab === tab.id ? 'bg-gray-900 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}
                            >{tab.icon} {tab.label}</button>
                        ))}
                    </div>

                    <div className="min-h-[500px]">
                        {/* ============ LA CANTANTE: HERO ============ */}
                        {
                            themeTab === 'hero' && (
                                <div className="grid md:grid-cols-2 gap-6 animate-in fade-in">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Type size={18} /> Hero Cantante</h3>
                                        <div className="mb-4">
                                            <label className="block text-sm font-bold mb-2 text-gray-700">Título Principal</label>
                                            <input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900 border-gray-300" value={getThemeVal('hero_title') || ''} onChange={(e) => setThemeVal('hero_title', e.target.value)} />
                                        </div>
                                        <div className="mb-4">
                                            <label className="block text-sm font-bold mb-2 text-gray-700">Subtítulo</label>
                                            <textarea className="w-full p-3 border rounded-lg bg-white text-gray-900 border-gray-300" rows={2} value={getThemeVal('hero_subtitle') || ''} onChange={(e) => setThemeVal('hero_subtitle', e.target.value)} />
                                        </div>
                                        <div className="mb-4">
                                            <label className="block text-sm font-bold mb-2 text-gray-700">Cita del Hero</label>
                                            <textarea className="w-full p-3 border rounded-lg bg-white text-gray-900 border-gray-300" rows={3} value={getThemeVal('hero_quote') || ''} onChange={(e) => setThemeVal('hero_quote', e.target.value)} placeholder={'"La distancia no borra la identidad..."'} />
                                        </div>
                                        <h3 className="font-bold text-lg mt-6 mb-4 flex items-center gap-2"><LinkIcon size={18} /> Botones de Acción</h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div><label className="block text-xs font-bold mb-1">Texto Botón 1</label><input type="text" className="w-full p-2 border rounded text-sm bg-white text-gray-900" value={getThemeVal('hero_cta_primary_text') || ''} onChange={(e) => setThemeVal('hero_cta_primary_text', e.target.value)} /></div>
                                            <div><label className="block text-xs font-bold mb-1">Enlace Botón 1</label><input type="text" className="w-full p-2 border rounded text-sm bg-white text-gray-900" value={getThemeVal('hero_cta_primary_link') || ''} onChange={(e) => setThemeVal('hero_cta_primary_link', e.target.value)} /></div>
                                            <div><label className="block text-xs font-bold mb-1">Texto Botón 2</label><input type="text" className="w-full p-2 border rounded text-sm bg-white text-gray-900" value={getThemeVal('hero_cta_secondary_text') || ''} onChange={(e) => setThemeVal('hero_cta_secondary_text', e.target.value)} /></div>
                                            <div><label className="block text-xs font-bold mb-1">Enlace Botón 2</label><input type="text" className="w-full p-2 border rounded text-sm bg-white text-gray-900" value={getThemeVal('hero_cta_secondary_link') || ''} onChange={(e) => setThemeVal('hero_cta_secondary_link', e.target.value)} /></div>
                                        </div>
                                    </div>
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><ImageIcon size={18} /> Imagen de Fondo</h3>
                                        {renderImagePicker('hero_image', getThemeVal('hero_image'))}
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ LA CANTANTE: MARQUEE ============ */}
                        {
                            themeTab === 'marquee' && (
                                <div className="animate-in fade-in">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <div className="flex justify-between items-center mb-6">
                                            <h3 className="font-bold text-lg flex items-center gap-2"><Layers size={18} /> Textos Rotativos (Cinta Naranja)</h3>
                                            <Button type="button" size="sm" variant="outline" onClick={() => { const t = Array.isArray(getThemeVal('marquee_texts')) ? [...getThemeVal('marquee_texts')] : []; t.push("NUEVO TEXTO..."); setThemeVal('marquee_texts', t); }}><Plus size={16} className="mr-1" /> Añadir</Button>
                                        </div>
                                        <div className="space-y-2">
                                            {(Array.isArray(getThemeVal('marquee_texts')) ? getThemeVal('marquee_texts') : ["GIRA ESPAÑA 2026"]).map((txt: string, idx: number) => (
                                                <div key={idx} className="flex gap-2 items-center">
                                                    <input type="text" className="flex-1 p-2 border bg-white rounded text-sm text-gray-900 font-bold tracking-widest uppercase" value={txt} onChange={(e) => { const n = Array.isArray(getThemeVal('marquee_texts')) ? [...getThemeVal('marquee_texts')] : []; n[idx] = e.target.value; setThemeVal('marquee_texts', n); }} />
                                                    <button type="button" onClick={() => { const n = Array.isArray(getThemeVal('marquee_texts')) ? [...getThemeVal('marquee_texts')] : []; n.splice(idx, 1); setThemeVal('marquee_texts', n); }} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ LA CANTANTE: BENTO BOX ============ */}
                        {
                            themeTab === 'bento' && (
                                <div className="space-y-6 animate-in fade-in">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Globe size={18} /> El Fenómeno Mabelita</h3>
                                        <div className="grid md:grid-cols-2 gap-4 mb-6">
                                            <div><label className="block text-sm font-bold mb-2">Título Sección</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('section_title') || ''} onChange={(e) => setThemeVal('section_title', e.target.value)} placeholder="El Fenómeno" /></div>
                                            <div><label className="block text-sm font-bold mb-2">Subtítulo</label><textarea className="w-full p-3 border rounded-lg bg-white text-gray-900" rows={2} value={getThemeVal('section_subtitle') || ''} onChange={(e) => setThemeVal('section_subtitle', e.target.value)} /></div>
                                        </div>
                                    </div>
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <div className="flex justify-between items-center mb-6">
                                            <h3 className="font-bold text-lg flex items-center gap-2"><Globe size={18} /> Tarjetas Estadísticas</h3>
                                            <Button type="button" size="sm" variant="outline" onClick={() => { const s = Array.isArray(getThemeVal('social_stats')) ? [...getThemeVal('social_stats')] : []; s.push({ platform: 'youtube', count: '100K', label: 'Suscriptores', url: '' }); setThemeVal('social_stats', s); }}><Plus size={16} className="mr-1" /> Añadir</Button>
                                        </div>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {(Array.isArray(getThemeVal('social_stats')) ? getThemeVal('social_stats') : []).map((stat: any, idx: number) => (
                                                <div key={idx} className="border p-4 rounded-lg bg-gray-50 relative group">
                                                    <button type="button" onClick={() => { const n = [...getThemeVal('social_stats')]; n.splice(idx, 1); setThemeVal('social_stats', n); }} className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                                        <div><label className="text-[10px] font-bold uppercase">Plataforma</label><select className="w-full text-sm p-1.5 border rounded" value={stat.platform} onChange={e => { const n = [...getThemeVal('social_stats')]; n[idx].platform = e.target.value; setThemeVal('social_stats', n); }}><option value="youtube">YouTube</option><option value="spotify">Spotify</option><option value="tiktok">TikTok</option><option value="instagram">Instagram</option><option value="facebook">Facebook</option></select></div>
                                                        <div><label className="text-[10px] font-bold uppercase">Métrica (Ej: 15.2M)</label><input type="text" className="w-full text-sm p-1.5 border rounded font-black text-indigo-600" value={stat.count || ''} onChange={e => { const n = [...getThemeVal('social_stats')]; n[idx].count = e.target.value; setThemeVal('social_stats', n); }} /></div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div><label className="text-[10px] font-bold uppercase">Etiqueta</label><input type="text" className="w-full text-sm p-1.5 border rounded" value={stat.label || ''} onChange={e => { const n = [...getThemeVal('social_stats')]; n[idx].label = e.target.value; setThemeVal('social_stats', n); }} /></div>
                                                        <div><label className="text-[10px] font-bold uppercase">URL</label><input type="text" className="w-full text-sm p-1.5 border rounded" placeholder="https://..." value={stat.url || ''} onChange={e => { const n = [...getThemeVal('social_stats')]; n[idx].url = e.target.value; setThemeVal('social_stats', n); }} /></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ LA CANTANTE: GALERÍA ============ */}
                        {
                            themeTab === 'gallery' && (
                                <div className="animate-in fade-in space-y-6">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <div className="flex justify-between items-center mb-6">
                                            <h3 className="font-bold text-lg flex items-center gap-2"><PlayCircle size={18} /> Galería Interactiva</h3>
                                            <Button type="button" size="sm" className="bg-mabelita-primary" onClick={() => { const items = Array.isArray(getThemeVal('gallery_items')) ? [...getThemeVal('gallery_items')] : []; items.push({ id: Date.now().toString(), type: 'youtube', url: '', title: 'Nuevo Item', filter: 'youtube', date: new Date().toISOString().split('T')[0], views: 0 }); setThemeVal('gallery_items', items); }}><Plus size={16} className="mr-1" /> Añadir Item</Button>
                                        </div>
                                        <p className="text-sm text-gray-500 mb-6">Gestiona los vídeos y fotos de la galería. Categorías: YouTube, TikTok, Fotos.</p>
                                        {(!Array.isArray(getThemeVal('gallery_items')) || getThemeVal('gallery_items').length === 0) ? (
                                            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50"><PlayCircle size={48} className="mx-auto text-gray-300 mb-4" /><p className="text-gray-500 font-medium">Sin items.</p></div>
                                        ) : (
                                            <div className="space-y-4">
                                                {getThemeVal('gallery_items').map((item: any, idx: number) => (
                                                    <div key={idx} className="p-4 border border-gray-200 rounded-lg bg-gray-50 relative group">
                                                        <button type="button" onClick={() => { const n = [...getThemeVal('gallery_items')]; n.splice(idx, 1); setThemeVal('gallery_items', n); }} className="absolute top-2 right-2 p-1 bg-red-50 text-red-600 border border-red-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                            <div><label className="block text-xs font-bold mb-1">Tipo</label><select className="w-full p-2 text-sm border bg-white rounded" value={item.type || 'youtube'} onChange={(e) => { const n = [...getThemeVal('gallery_items')]; n[idx].type = e.target.value; n[idx].filter = e.target.value === 'image' ? 'images' : e.target.value; setThemeVal('gallery_items', n); }}><option value="youtube">YouTube</option><option value="tiktok">TikTok</option><option value="image">Foto</option></select></div>
                                                            <div><label className="block text-xs font-bold mb-1">Título</label><input type="text" value={item.title || ''} onChange={(e) => { const n = [...getThemeVal('gallery_items')]; n[idx].title = e.target.value; setThemeVal('gallery_items', n); }} className="w-full p-2 text-sm border bg-white rounded" /></div>
                                                            <div><label className="block text-xs font-bold mb-1">Fecha</label><input type="date" value={item.date || ''} onChange={(e) => { const n = [...getThemeVal('gallery_items')]; n[idx].date = e.target.value; setThemeVal('gallery_items', n); }} className="w-full p-2 text-sm border bg-white rounded" /></div>
                                                            <div><label className="block text-xs font-bold mb-1">Views</label><input type="number" value={item.views || 0} onChange={(e) => { const n = [...getThemeVal('gallery_items')]; n[idx].views = parseInt(e.target.value) || 0; setThemeVal('gallery_items', n); }} className="w-full p-2 text-sm border bg-white rounded" /></div>
                                                        </div>
                                                        <div className="mt-3"><label className="block text-xs font-bold mb-1">URL</label><input type="text" value={item.url || ''} onChange={(e) => { const n = [...getThemeVal('gallery_items')]; n[idx].url = e.target.value; setThemeVal('gallery_items', n); }} className="w-full p-2 text-sm border bg-white rounded" /></div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ LA CANTANTE: TOUR ============ */}
                        {
                            themeTab === 'tour' && (
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-in fade-in">
                                    <div className="flex justify-between items-center mb-6">
                                        <h3 className="font-bold text-lg flex items-center gap-2"><PlayCircle size={18} /> Calendario de Conciertos</h3>
                                        <Button type="button" size="sm" className="bg-indigo-600" onClick={() => { const t = Array.isArray(getThemeVal('concert_calendar')) ? [...getThemeVal('concert_calendar')] : []; t.push({ id: Date.now().toString(), date: new Date().toISOString().split('T')[0], location: 'Madrid, ES', venue: 'Venue', status: 'tickets_available' }); setThemeVal('concert_calendar', t); }}><Plus size={16} className="mr-1" /> Añadir Fecha</Button>
                                    </div>
                                    <div className="space-y-4">
                                        {(Array.isArray(getThemeVal('concert_calendar')) ? getThemeVal('concert_calendar') : []).map((concert: any, idx: number) => (
                                            <div key={concert.id || idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50 flex flex-col md:flex-row gap-4 items-center group relative">
                                                <div className="absolute -left-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"><button type="button" onClick={() => { const n = [...getThemeVal('concert_calendar')]; n.splice(idx, 1); setThemeVal('concert_calendar', n); }} className="bg-red-500 text-white rounded-full p-1.5 shadow-md hover:bg-red-600"><X size={14} /></button></div>
                                                <div className="w-full md:w-auto shrink-0 space-y-1"><label className="text-[10px] font-bold uppercase text-gray-500">Fecha</label><input type="date" className="p-2 border rounded text-sm w-full md:w-36 font-mono font-bold" value={concert.date || ''} onChange={e => { const n = [...getThemeVal('concert_calendar')]; n[idx].date = e.target.value; setThemeVal('concert_calendar', n); }} /></div>
                                                <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 flex-1">
                                                    <div><label className="text-[10px] font-bold uppercase text-gray-500">Ciudad / País</label><input type="text" className="w-full p-2 border rounded text-sm font-bold uppercase" value={concert.location || ''} onChange={e => { const n = [...getThemeVal('concert_calendar')]; n[idx].location = e.target.value; setThemeVal('concert_calendar', n); }} /></div>
                                                    <div><label className="text-[10px] font-bold uppercase text-gray-500">Recinto</label><input type="text" className="w-full p-2 border rounded text-sm" value={concert.venue || ''} onChange={e => { const n = [...getThemeVal('concert_calendar')]; n[idx].venue = e.target.value; setThemeVal('concert_calendar', n); }} /></div>
                                                    <div><label className="text-[10px] font-bold uppercase text-gray-500">Link Entradas</label><input type="text" className="w-full p-2 border rounded text-sm text-indigo-700 bg-indigo-50/50" value={concert.ticketLink || ''} onChange={e => { const n = [...getThemeVal('concert_calendar')]; n[idx].ticketLink = e.target.value; setThemeVal('concert_calendar', n); }} /></div>
                                                </div>
                                                <div className="w-full md:w-auto shrink-0 space-y-1"><label className="text-[10px] font-bold uppercase text-gray-500">Estado</label><select className={`w-full p-2 border rounded text-xs font-bold uppercase ${concert.status === 'tickets_available' ? 'bg-emerald-50 text-emerald-800' : concert.status === 'sold_out' ? 'bg-orange-50 text-orange-800' : 'bg-gray-200 text-gray-500'}`} value={concert.status || 'tickets_available'} onChange={e => { const n = [...getThemeVal('concert_calendar')]; n[idx].status = e.target.value; setThemeVal('concert_calendar', n); }}><option value="tickets_available">A la Venta</option><option value="sold_out">Sold Out</option><option value="cancelled">Cancelado</option></select></div>
                                            </div>
                                        ))}
                                        {(!getThemeVal('concert_calendar') || getThemeVal('concert_calendar').length === 0) && (<div className="text-center py-8 text-gray-400 font-medium">No hay fechas configuradas.</div>)}
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ LA CANTANTE: ARTISTA ============ */}
                        {
                            themeTab === 'artist' && (
                                <div className="grid md:grid-cols-2 gap-6 animate-in fade-in">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Type size={18} /> Página: La Artista</h3>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Título Hero Banner</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('singer_hero_title') || ''} onChange={(e) => setThemeVal('singer_hero_title', e.target.value)} /></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Subtítulo Hero</label><textarea className="w-full p-3 border rounded-lg bg-white text-gray-900" rows={2} value={getThemeVal('singer_hero_subtitle') || ''} onChange={(e) => setThemeVal('singer_hero_subtitle', e.target.value)} /></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Bio / Trayectoria</label><textarea className="w-full p-3 border rounded-lg bg-white text-gray-900" rows={6} value={getThemeVal('singer_bio_text') || ''} onChange={(e) => setThemeVal('singer_bio_text', e.target.value)} /></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Repertorio (JSON array)</label><textarea className="w-full p-3 border rounded-lg bg-white text-gray-900 font-mono text-xs" rows={4} value={typeof getThemeVal('repertorio') === 'string' ? getThemeVal('repertorio') : JSON.stringify(getThemeVal('repertorio') || [])} onChange={(e) => setThemeVal('repertorio', e.target.value)} placeholder='["Canción 1", "Canción 2"]' /></div>
                                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                                            <div><label className="block text-xs font-bold mb-1">Título Contratación</label><input type="text" className="w-full p-2 border rounded text-sm bg-white text-gray-900" value={getThemeVal('contactTitle') || ''} onChange={(e) => setThemeVal('contactTitle', e.target.value)} /></div>
                                            <div><label className="block text-xs font-bold mb-1">Texto Contratación</label><textarea className="w-full p-2 border rounded text-sm bg-white text-gray-900" rows={2} value={getThemeVal('contactText') || ''} onChange={(e) => setThemeVal('contactText', e.target.value)} /></div>
                                        </div>
                                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                                            <div><label className="block text-xs font-bold mb-1">Título Multimedia</label><input type="text" className="w-full p-2 border rounded text-sm bg-white text-gray-900" value={getThemeVal('performances_title') || 'Galería Multimedia'} onChange={(e) => setThemeVal('performances_title', e.target.value)} /></div>
                                            <div><label className="block text-xs font-bold mb-1">Subtítulo Multimedia</label><textarea className="w-full p-2 border rounded text-sm bg-white text-gray-900" rows={2} value={getThemeVal('performances_subtitle') || ''} onChange={(e) => setThemeVal('performances_subtitle', e.target.value)} /></div>
                                        </div>
                                    </div>
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><ImageIcon size={18} /> Imagen Hero Artista</h3>
                                        {renderImagePicker('singer_hero_image', getThemeVal('singer_hero_image'))}
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ GLOBAL ============ */}
                        {
                            themeTab === 'global' && (
                                <div className="grid md:grid-cols-2 gap-6 animate-in fade-in">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Layout size={18} /> Estructura y Diseño</h3>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Layout Principal</label><select className="w-full p-3 bg-white text-gray-900 border border-gray-200 rounded-lg" value={getThemeVal('theme_layout') || 'classic'} onChange={(e) => setThemeVal('theme_layout', e.target.value)}><option value="classic">Classic (Folklore)</option><option value="editorial">Editorial (Revista)</option><option value="urban">Urban (Neo-Brutalista)</option></select></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Posición del Logo</label><select className="w-full p-3 bg-white text-gray-900 border border-gray-200 rounded-lg" value={getThemeVal('nav_logo_position') || 'inline'} onChange={(e) => setThemeVal('nav_logo_position', e.target.value)}><option value="inline">Inline (Logo Izq)</option><option value="inline_big">Inline Grande</option><option value="centered">Centrado</option><option value="stacked">Cover (Grande Arriba)</option></select></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Tipografía</label><select className="w-full p-3 bg-white text-gray-900 border border-gray-200 rounded-lg" value={getThemeVal('font_pair') || 'classic'} onChange={(e) => setThemeVal('font_pair', e.target.value)}><option value="classic">Clásica (Playfair + Inter)</option><option value="modern">Moderna (Montserrat + Lato)</option><option value="elegant">Elegante (Merriweather)</option></select></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Paleta de Colores</label><select className="w-full p-3 bg-white text-gray-900 border border-gray-200 rounded-lg" value={getThemeVal('color_palette') || 'earth'} onChange={(e) => setThemeVal('color_palette', e.target.value)}><option value="earth">Tierra (Andino)</option><option value="minimal">Minimalista</option><option value="vibrant">Vibrante</option><option value="custom">Personalizada</option></select></div>
                                    </div>
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Globe size={18} /> Identidad de Marca</h3>
                                        {renderImagePicker('logo_url', getThemeVal('logo_url'))}
                                        {renderImagePicker('tejiendo_logo_url', getThemeVal('tejiendo_logo_url'))}
                                        {getThemeVal('color_palette') === 'custom' && (
                                            <div className="grid grid-cols-2 gap-4 mt-4">
                                                <div><label className="text-xs font-bold">Color Primario</label><div className="flex gap-2 items-center mt-1"><input type="color" value={getThemeVal('color_primary') || '#000000'} onChange={(e) => setThemeVal('color_primary', e.target.value)} className="h-10 w-10 rounded cursor-pointer" /><input type="text" value={getThemeVal('color_primary') || ''} onChange={(e) => setThemeVal('color_primary', e.target.value)} className="w-full p-2 text-sm border rounded bg-white text-gray-900" /></div></div>
                                                <div><label className="text-xs font-bold">Color Secundario</label><div className="flex gap-2 items-center mt-1"><input type="color" value={getThemeVal('color_secondary') || '#000000'} onChange={(e) => setThemeVal('color_secondary', e.target.value)} className="h-10 w-10 rounded cursor-pointer" /><input type="text" value={getThemeVal('color_secondary') || ''} onChange={(e) => setThemeVal('color_secondary', e.target.value)} className="w-full p-2 text-sm border rounded bg-white text-gray-900" /></div></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ LA TEJEDORA: HERO ============ */}
                        {
                            themeTab === 'hero_weaver' && (
                                <div className="space-y-6 animate-in fade-in">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><ImageIcon size={18} /> Hero Storefront</h3>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Título Principal</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('hero_title') || ''} onChange={(e) => setThemeVal('hero_title', e.target.value)} placeholder="Shopping con Mabelita" /></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Subtítulo</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('hero_subtitle') || ''} onChange={(e) => setThemeVal('hero_subtitle', e.target.value)} placeholder="Prendas únicas tejidas a mano..." /></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Cita / Frase</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('hero_quote') || ''} onChange={(e) => setThemeVal('hero_quote', e.target.value)} /></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Badge Superior</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('artisan_badge') || ''} onChange={(e) => setThemeVal('artisan_badge', e.target.value)} placeholder="Artesanía Textil Premium" /></div>
                                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                                            <div><label className="block text-sm font-bold mb-2">Botón Primario Texto</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('hero_cta_primary_text') || ''} onChange={(e) => setThemeVal('hero_cta_primary_text', e.target.value)} /></div>
                                            <div><label className="block text-sm font-bold mb-2">Link</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('hero_cta_primary_link') || ''} onChange={(e) => setThemeVal('hero_cta_primary_link', e.target.value)} /></div>
                                            <div><label className="block text-sm font-bold mb-2">Botón Secundario Texto</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('hero_cta_secondary_text') || ''} onChange={(e) => setThemeVal('hero_cta_secondary_text', e.target.value)} /></div>
                                            <div><label className="block text-sm font-bold mb-2">Link</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('hero_cta_secondary_link') || ''} onChange={(e) => setThemeVal('hero_cta_secondary_link', e.target.value)} /></div>
                                        </div>
                                        <div className="mb-4">{renderImagePicker('hero_image', getThemeVal('hero_image'))}</div>
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ LA TEJEDORA: MATERIALES ============ */}
                        {
                            themeTab === 'materials' && (
                                <div className="animate-in fade-in">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Layers size={18} /> Nuestros Materiales</h3>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Título Sección</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('materials_title') || ''} onChange={(e) => setThemeVal('materials_title', e.target.value)} /></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Subtítulo</label><textarea className="w-full p-3 border rounded-lg bg-white text-gray-900" rows={2} value={getThemeVal('materials_subtitle') || ''} onChange={(e) => setThemeVal('materials_subtitle', e.target.value)} /></div>

                                        <div className="mt-8 space-y-6">
                                            {[1, 2, 3].map(i => (
                                                <div key={i} className="p-4 border border-gray-200 rounded-lg relative overflow-hidden">
                                                    <div className="absolute top-0 left-0 w-1 h-full bg-orange-500"></div>
                                                    <h4 className="font-bold mb-2">Material {i}</h4>
                                                    <div className="mb-2"><label className="block text-xs font-bold mb-1 border-b pb-1">Título</label><input type="text" className="w-full p-2 border rounded bg-white text-sm" value={getThemeVal(`material_${i}_title`) || ''} onChange={(e) => setThemeVal(`material_${i}_title`, e.target.value)} /></div>
                                                    <div><label className="block text-xs font-bold mb-1 border-b pb-1">Descripción</label><textarea className="w-full p-2 border rounded bg-white text-sm" rows={2} value={getThemeVal(`material_${i}_desc`) || ''} onChange={(e) => setThemeVal(`material_${i}_desc`, e.target.value)} /></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ LA TEJEDORA: ENCARGOS ============ */}
                        {
                            themeTab === 'encargos' && (
                                <div className="animate-in fade-in">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Briefcase size={18} /> Encargos a Medida</h3>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Título Sección</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('encargos_title') || ''} onChange={(e) => setThemeVal('encargos_title', e.target.value)} /></div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Texto Descriptivo</label><textarea className="w-full p-3 border rounded-lg bg-white text-gray-900" rows={3} value={getThemeVal('encargos_subtitle') || ''} onChange={(e) => setThemeVal('encargos_subtitle', e.target.value)} /></div>
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ LA TEJEDORA: RESEÑAS ============ */}
                        {
                            themeTab === 'reviews' && (
                                <div className="animate-in fade-in">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <div className="flex justify-between items-center mb-6">
                                            <div>
                                                <h3 className="font-bold text-lg flex items-center gap-2"><Star size={18} /> Reseñas de Clientes</h3>
                                                <p className="text-xs text-gray-400 mt-1">Estilo Trustpilot / Google Reviews</p>
                                            </div>
                                            <Button type="button" size="sm" variant="outline" onClick={() => { const r = Array.isArray(getThemeVal('reviews')) ? [...getThemeVal('reviews')] : []; r.push({ stars: 5, text: 'Escribe aquí la reseña...', name: 'Cliente', location: 'España', media_url: '', media_type: 'image', profile_img: '' }); setThemeVal('reviews', r); }}><Plus size={16} className="mr-1" /> Añadir Reseña</Button>
                                        </div>
                                        <div className="mb-4"><label className="block text-sm font-bold mb-2">Título Sección</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('testimonials_title') || ''} onChange={(e) => setThemeVal('testimonials_title', e.target.value)} /></div>
                                        <div className="mb-6"><label className="block text-sm font-bold mb-2">Subtítulo</label><input type="text" className="w-full p-3 border rounded-lg bg-white text-gray-900" value={getThemeVal('testimonials_subtitle') || ''} onChange={(e) => setThemeVal('testimonials_subtitle', e.target.value)} /></div>

                                        <div className="space-y-5">
                                            {(Array.isArray(getThemeVal('reviews')) ? getThemeVal('reviews') : []).map((review: any, idx: number) => (
                                                <div key={idx} className="p-4 border border-gray-200 rounded-xl bg-gray-50 relative group overflow-hidden">
                                                    <button type="button" onClick={() => { const n = [...getThemeVal('reviews')]; n.splice(idx, 1); setThemeVal('reviews', n); }} className="absolute top-3 right-3 p-1.5 text-red-400 hover:bg-red-100 hover:text-red-600 rounded-lg opacity-0 group-hover:opacity-100 transition-all z-10"><Trash2 size={14} /></button>

                                                    {/* Review card: profile + content */}
                                                    <div className="flex gap-4 p-4">
                                                        {/* Profile image */}
                                                        <div className="shrink-0 flex flex-col items-center gap-2">
                                                            <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-gray-200 bg-white cursor-pointer group/avatar" onClick={() => openGalleryForConfig(`__review_profile_${idx}`)}>
                                                                {review.profile_img ? (
                                                                    <img src={review.profile_img} alt="" className="w-full h-full object-cover" />
                                                                ) : (
                                                                    <div className="w-full h-full bg-gradient-to-br from-amber-100 to-orange-200 flex items-center justify-center">
                                                                        <span className="text-2xl font-black text-amber-700">{(review.name || 'C')[0].toUpperCase()}</span>
                                                                    </div>
                                                                )}
                                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/avatar:opacity-100 transition-opacity flex items-center justify-center">
                                                                    <ImageIcon size={16} className="text-white" />
                                                                </div>
                                                            </div>
                                                            <input type="text" className="w-20 p-1 text-[9px] border rounded bg-white text-center font-mono" placeholder="URL foto" value={review.profile_img || ''} onChange={e => { const n = [...getThemeVal('reviews')]; n[idx].profile_img = e.target.value; setThemeVal('reviews', n); }} />
                                                        </div>

                                                        {/* Content */}
                                                        <div className="flex-1 min-w-0">
                                                            {/* Trustpilot-style stars */}
                                                            <div className="flex items-center gap-1 mb-3">
                                                                {[1, 2, 3, 4, 5].map(star => (
                                                                    <button key={star} type="button" onClick={() => { const n = [...getThemeVal('reviews')]; n[idx].stars = star; setThemeVal('reviews', n); }}
                                                                        className={`w-7 h-7 rounded-sm flex items-center justify-center transition-all hover:scale-110 ${star <= (review.stars || 5) ? 'bg-[#00b67a] text-white' : 'bg-gray-200 text-gray-400'}`}>
                                                                        <Star size={16} fill="currentColor" />
                                                                    </button>
                                                                ))}
                                                                <span className="ml-2 text-xs font-bold text-gray-500">{review.stars || 5}/5</span>
                                                            </div>

                                                            {/* Name + Location row */}
                                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                                <div><label className="block text-[10px] font-bold mb-1 text-gray-500 uppercase tracking-wide">Nombre</label><input type="text" className="w-full p-2 text-sm border rounded-lg bg-white font-semibold" value={review.name || ''} onChange={e => { const n = [...getThemeVal('reviews')]; n[idx].name = e.target.value; setThemeVal('reviews', n); }} /></div>
                                                                <div><label className="block text-[10px] font-bold mb-1 text-gray-500 uppercase tracking-wide">Ubicación</label><input type="text" className="w-full p-2 text-sm border rounded-lg bg-white" value={review.location || ''} onChange={e => { const n = [...getThemeVal('reviews')]; n[idx].location = e.target.value; setThemeVal('reviews', n); }} /></div>
                                                            </div>

                                                            {/* Review text */}
                                                            <div><label className="block text-[10px] font-bold mb-1 text-gray-500 uppercase tracking-wide">Reseña</label><textarea className="w-full p-2 text-sm border rounded-lg bg-white" rows={2} value={review.text || ''} onChange={e => { const n = [...getThemeVal('reviews')]; n[idx].text = e.target.value; setThemeVal('reviews', n); }} /></div>
                                                        </div>
                                                    </div>

                                                    {/* Media attachment bar */}
                                                    <div className="px-4 pb-4">
                                                        <div className="p-3 bg-white border border-dashed border-gray-300 rounded-lg">
                                                            <label className="block text-[10px] font-bold mb-2 text-gray-500 uppercase tracking-wide">📎 Media adjunto</label>
                                                            <div className="flex gap-2 items-center">
                                                                <select className="w-24 p-2 text-xs border rounded bg-gray-50 font-bold" value={review.media_type || 'image'} onChange={e => { const n = [...getThemeVal('reviews')]; n[idx].media_type = e.target.value; setThemeVal('reviews', n); }}>
                                                                    <option value="image">🖼️ Imagen</option>
                                                                    <option value="video">🎬 Video</option>
                                                                    <option value="embed">🔗 Embed</option>
                                                                </select>
                                                                <input type="text" className="flex-1 p-2 text-sm border rounded bg-white font-mono" placeholder={review.media_type === 'embed' ? 'https://youtube.com/embed/...' : review.media_type === 'video' ? 'URL del video...' : 'URL de la imagen...'} value={review.media_url || ''} onChange={e => { const n = [...getThemeVal('reviews')]; n[idx].media_url = e.target.value; setThemeVal('reviews', n); }} />
                                                                <Button type="button" size="sm" variant="outline" onClick={() => openGalleryForConfig(`__review_media_${idx}`)} className="shrink-0 text-xs"><Grid size={14} className="mr-1" /> Galería</Button>
                                                            </div>
                                                            {review.media_url && (
                                                                <div className="mt-2 max-w-[200px]">
                                                                    {review.media_type === 'embed' ? (
                                                                        <div className="aspect-video bg-gray-100 rounded border overflow-hidden">
                                                                            <iframe src={review.media_url} className="w-full h-full" allowFullScreen title="embed preview" />
                                                                        </div>
                                                                    ) : review.media_type === 'video' ? (
                                                                        <video src={review.media_url} className="w-full rounded border" controls muted />
                                                                    ) : (
                                                                        <img src={review.media_url} className="w-full rounded border object-cover" alt="preview" />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ LA TEJEDORA: HANDMADE ============ */}
                        {
                            themeTab === 'handmade' && (
                                <div className="animate-in fade-in">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <div className="flex justify-between items-center mb-6">
                                            <h3 className="font-bold text-lg flex items-center gap-2"><Heart size={18} /> Galería Hecho a Mano</h3>
                                            <Button type="button" size="sm" variant="outline" onClick={() => {
                                                const items = Array.isArray(getThemeVal('handmade_gallery')) ? [...getThemeVal('handmade_gallery')] : [];
                                                items.push({ url: '', type: 'image', caption: '' });
                                                setThemeVal('handmade_gallery', items);
                                            }}><Plus size={16} className="mr-1" /> Añadir Media</Button>
                                        </div>
                                        <div className="mb-6"><label className="block text-sm font-bold mb-2">Subtítulo Descriptivo</label><textarea className="w-full p-3 border rounded-lg bg-white text-gray-900" rows={2} value={getThemeVal('gallery_subtitle') || ''} onChange={(e) => setThemeVal('gallery_subtitle', e.target.value)} /></div>
                                        <p className="text-sm text-gray-500 mb-4">Imágenes, videos y embeds que muestran el proceso artesanal del tejido a mano.</p>
                                        <div className="space-y-4">
                                            {(Array.isArray(getThemeVal('handmade_gallery')) ? getThemeVal('handmade_gallery') :
                                                /* Legacy migration: convert old string[] to object[] */
                                                (Array.isArray(getThemeVal('handmade_images')) ? getThemeVal('handmade_images').map((u: string) => ({ url: u, type: 'image', caption: '' })) : [])
                                            ).map((item: any, idx: number) => {
                                                // Support legacy string items
                                                const mediaItem = typeof item === 'string' ? { url: item, type: 'image', caption: '' } : item;
                                                return (
                                                    <div key={idx} className="p-3 border border-gray-200 rounded-lg bg-gray-50 group relative">
                                                        <button type="button" onClick={() => {
                                                            const n = Array.isArray(getThemeVal('handmade_gallery')) ? [...getThemeVal('handmade_gallery')] : [];
                                                            n.splice(idx, 1); setThemeVal('handmade_gallery', n);
                                                        }} className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                                                        <div className="flex gap-2 items-start">
                                                            <select className="w-24 p-2 text-xs border rounded bg-white font-bold shrink-0" value={mediaItem.type || 'image'} onChange={e => {
                                                                const n = Array.isArray(getThemeVal('handmade_gallery')) ? [...getThemeVal('handmade_gallery')] : [];
                                                                if (!n[idx] || typeof n[idx] === 'string') n[idx] = { url: n[idx] || '', type: 'image', caption: '' };
                                                                n[idx].type = e.target.value; setThemeVal('handmade_gallery', n);
                                                            }}>
                                                                <option value="image">🖼️ Imagen</option>
                                                                <option value="video">🎬 Video</option>
                                                                <option value="embed">🔗 Embed</option>
                                                            </select>
                                                            <div className="flex-1 flex flex-col gap-2">
                                                                <div className="flex gap-2">
                                                                    <input type="text" className="flex-1 p-2 text-sm border rounded bg-white font-mono" placeholder={mediaItem.type === 'embed' ? 'https://youtube.com/embed/...' : mediaItem.type === 'video' ? 'URL del video...' : 'URL de la imagen...'} value={mediaItem.url || ''} onChange={e => {
                                                                        const n = Array.isArray(getThemeVal('handmade_gallery')) ? [...getThemeVal('handmade_gallery')] : [];
                                                                        if (!n[idx] || typeof n[idx] === 'string') n[idx] = { url: '', type: 'image', caption: '' };
                                                                        n[idx].url = e.target.value; setThemeVal('handmade_gallery', n);
                                                                    }} />
                                                                    <Button type="button" size="sm" variant="outline" onClick={() => openGalleryForConfig(`__handmade_media_${idx}`)} className="shrink-0 text-xs"><Grid size={14} className="mr-1" /> Galería</Button>
                                                                </div>
                                                                <input type="text" className="w-full p-2 text-xs border rounded bg-white" placeholder="Pie de foto (opcional)" value={mediaItem.caption || ''} onChange={e => {
                                                                    const n = Array.isArray(getThemeVal('handmade_gallery')) ? [...getThemeVal('handmade_gallery')] : [];
                                                                    if (!n[idx] || typeof n[idx] === 'string') n[idx] = { url: '', type: 'image', caption: '' };
                                                                    n[idx].caption = e.target.value; setThemeVal('handmade_gallery', n);
                                                                }} />
                                                            </div>
                                                            {mediaItem.url && (
                                                                <div className="w-20 shrink-0">
                                                                    {mediaItem.type === 'embed' ? (
                                                                        <div className="aspect-video bg-gray-100 rounded border overflow-hidden">
                                                                            <iframe src={mediaItem.url} className="w-full h-full" allowFullScreen title="preview" />
                                                                        </div>
                                                                    ) : mediaItem.type === 'video' ? (
                                                                        <video src={mediaItem.url} className="w-full rounded border" muted />
                                                                    ) : (
                                                                        <img src={mediaItem.url} className="w-full rounded border object-cover aspect-square" alt="" />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ BANNER ============ */}
                        {
                            themeTab === 'banner' && (
                                <div className="animate-in fade-in space-y-6">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                        <div className="flex justify-between items-center mb-6">
                                            <div>
                                                <h3 className="font-bold text-lg flex items-center gap-2"><Radio size={18} /> Banner Superior (Announcement Bar)</h3>
                                                <p className="text-xs text-gray-400 mt-1">La barra de anuncios que aparece arriba del todo en {themeContext === 'singer' ? 'Mabelita de la Cruz' : 'Tejiendo con Mabelita'}.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 mb-6 p-4 border border-gray-200 rounded-xl bg-gray-50">
                                            <label className="flex items-center gap-3 cursor-pointer">
                                                <div className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${getThemeVal('banner_enabled') !== false ? 'bg-emerald-500' : 'bg-gray-300'}`}
                                                    onClick={() => setThemeVal('banner_enabled', getThemeVal('banner_enabled') === false ? true : false)}>
                                                    <div className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-200 ${getThemeVal('banner_enabled') !== false ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                                </div>
                                                <span className="text-sm font-bold text-gray-700">{getThemeVal('banner_enabled') !== false ? 'Banner Activado' : 'Banner Desactivado'}</span>
                                            </label>
                                        </div>
                                        <div className={`${getThemeVal('banner_enabled') === false ? 'opacity-50 pointer-events-none' : ''}`}>
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="font-bold text-sm text-gray-600 flex items-center gap-2"><Layers size={16} /> Textos del Banner</h4>
                                                <Button type="button" size="sm" variant="outline" onClick={() => {
                                                    const t = Array.isArray(getThemeVal('marquee_items')) ? [...getThemeVal('marquee_items')] : [];
                                                    t.push('✨ NUEVO ANUNCIO...');
                                                    setThemeVal('marquee_items', t);
                                                }}><Plus size={16} className="mr-1" /> Añadir</Button>
                                            </div>
                                            <div className="space-y-2">
                                                {(Array.isArray(getThemeVal('marquee_items')) ? getThemeVal('marquee_items') : []).map((txt: string, idx: number) => (
                                                    <div key={idx} className="flex gap-2 items-center">
                                                        <input type="text" className="flex-1 p-2 border bg-white rounded text-sm text-gray-900 font-bold" value={txt} onChange={(e) => {
                                                            const n = Array.isArray(getThemeVal('marquee_items')) ? [...getThemeVal('marquee_items')] : [];
                                                            n[idx] = e.target.value;
                                                            setThemeVal('marquee_items', n);
                                                        }} />
                                                        <button type="button" onClick={() => {
                                                            const n = Array.isArray(getThemeVal('marquee_items')) ? [...getThemeVal('marquee_items')] : [];
                                                            n.splice(idx, 1);
                                                            setThemeVal('marquee_items', n);
                                                        }} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                                                    </div>
                                                ))}
                                                {(!Array.isArray(getThemeVal('marquee_items')) || getThemeVal('marquee_items').length === 0) && (
                                                    <div className="text-center py-6 text-gray-400 font-medium border-2 border-dashed border-gray-200 rounded-lg">
                                                        Sin textos de banner. Añade uno para empezar.
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mt-6 p-4 bg-gradient-to-r from-mabelita-primary to-mabelita-secondary rounded-xl overflow-hidden">
                                                <p className="text-xs text-white/60 mb-2 font-bold uppercase tracking-widest">Vista previa</p>
                                                <div className="overflow-hidden whitespace-nowrap">
                                                    <div className="inline-block animate-marquee text-white text-xs font-bold tracking-widest uppercase">
                                                        {(Array.isArray(getThemeVal('marquee_items')) ? getThemeVal('marquee_items') : ['GRATIS A ESPAÑA EN PEDIDOS +100€']).map((item: string, i: number) => (
                                                            <span key={i}><span className="mx-4">{item}</span> •</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {/* ============ FOOTER ============ */}
                        {
                            themeTab === 'footer' && (
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-in fade-in">
                                    <h3 className="font-bold text-lg mb-4">Pie de Página</h3>
                                    <div><label className="block text-sm font-medium mb-1">Texto Footer</label><textarea rows={3} className="w-full p-2 border rounded bg-white text-gray-900" value={getThemeVal('footer_text') || ''} onChange={(e) => setThemeVal('footer_text', e.target.value)} /></div>
                                </div>
                            )
                        }

                    </div>
                </div>
            </div>
        </div>
    );
    return (
        <div className="flex min-h-screen bg-gray-50 relative overflow-x-hidden text-gray-900 antialiased">
            {/* HEADER MOBILE */}
            <div className="lg:hidden fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b z-40 px-4 py-3 flex justify-between items-center shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-200">
                        <Zap className="text-white" size={16} fill="white" />
                    </div>
                    <div>
                        <h2 className="text-lg font-black text-gray-900 tracking-tighter">MABELITA</h2>
                        <p className="text-[8px] font-black tracking-widest text-indigo-600 uppercase">Mabelita Admin</p>
                    </div>
                </div>
                <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 text-gray-600">
                    {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* SIDEBAR */}
            <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white/70 backdrop-blur-2xl border-r border-gray-200/50 transform transition-all duration-500 cubic-bezier(0.4, 0, 0.2, 1) lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} shadow-2xl lg:shadow-none`}>
                <div className="p-8 h-full flex flex-col">
                    <div className="flex items-center gap-3 mb-10 px-2 transition-transform hover:scale-105">
                        <div className="w-10 h-10 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-200">
                            <Zap className="text-white" size={20} fill="white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 tracking-tighter">MABELITA</h2>
                            <p className="text-[10px] font-black tracking-[0.2em] text-indigo-600 uppercase">Control Panel</p>
                        </div>
                    </div>

                    <nav className="flex-1 space-y-1 overflow-y-auto pr-2 custom-scrollbar">
                        <div className="mb-8">
                            <p className="px-4 text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-4 opacity-50">Discovery</p>
                            <button
                                onClick={() => { setActiveView('overview'); setIsSidebarOpen(false); }}
                                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 ${activeView === 'overview' ? 'bg-indigo-600 text-white shadow-2xl shadow-indigo-200' : 'text-gray-500 hover:bg-white hover:shadow-lg hover:shadow-gray-200/50 hover:text-indigo-600'}`}
                            >
                                <LayoutDashboard size={18} /> Resumen
                            </button>
                        </div>

                        <div className="mb-8">
                            <p className="px-4 text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-4 opacity-50">Comercio</p>
                            {[
                                { id: 'products', label: 'Productos', icon: <Package size={18} /> },
                                { id: 'orders', label: 'Pedidos', icon: <ShoppingBag size={18} /> },
                                { id: 'customers', label: 'Clientes', icon: <Users size={18} /> },
                                { id: 'tunantada-calendar', label: 'Contratación Eventos', icon: <Calendar size={18} /> },
                                { id: 'stripe', label: 'Stripe Bank', icon: <DollarSign size={18} /> }
                            ].map(item => (
                                <button
                                    key={item.id}
                                    onClick={() => { setActiveView(item.id as any); setIsSidebarOpen(false); }}
                                    className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 mb-1 ${activeView === item.id ? 'bg-indigo-600 text-white shadow-2xl shadow-indigo-200' : 'text-gray-500 hover:bg-white hover:shadow-lg hover:shadow-gray-200/50 hover:text-indigo-600'}`}
                                >
                                    {item.icon} {item.label === 'Contratación Eventos' ? 'Calendario' : item.label}
                                </button>
                            ))}
                        </div>

                        <div className="mb-8">
                            <p className="px-4 text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-4 opacity-50">Estudio AI</p>
                            {[
                                { id: 'theme', label: 'Theme Studio', icon: <Palette size={18} /> },
                                { id: 'graphic-studio', label: 'Pachamanca', icon: <PenTool size={18} /> },
                                { id: 'la-chacra', label: 'La Chacra', icon: <Sprout size={18} /> },
                                { id: 'media', label: 'Galería', icon: <ImageIcon size={18} /> }
                            ].map(item => (
                                <button
                                    key={item.id}
                                    onClick={() => { setActiveView(item.id as any); setIsSidebarOpen(false); }}
                                    className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 mb-1 ${activeView === item.id ? 'bg-indigo-600 text-white shadow-2xl shadow-indigo-200' : 'text-gray-500 hover:bg-white hover:shadow-lg hover:shadow-gray-200/50 hover:text-indigo-600'}`}
                                >
                                    {item.icon} {item.label}
                                </button>
                            ))}
                        </div>

                        <div className="mb-8">
                            <p className="px-4 text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-4 opacity-50">Sistemas</p>
                            <button
                                onClick={() => { setActiveView('monitor'); setIsSidebarOpen(false); }}
                                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 ${activeView === 'monitor' || activeView === 'api' ? 'bg-black text-white shadow-2xl shadow-gray-400' : 'text-gray-500 hover:bg-white hover:text-black hover:shadow-lg'}`}
                            >
                                <Activity size={18} /> API Monitor
                            </button>
                        </div>
                    </nav>

                    <div className="mt-auto pt-6 border-t border-gray-100">
                        <div className="p-4 bg-gray-50/50 rounded-3xl border border-gray-100 mb-4">
                            <Button variant="outline" className="w-full text-indigo-600 font-bold border-indigo-200 hover:bg-indigo-50" onClick={() => window.location.href = '/'}>
                                <ArrowLeft size={16} className="mr-2" /> Volver a la Tienda
                            </Button>
                        </div>
                        <div className="p-4 bg-gray-50/50 rounded-3xl border border-gray-100">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 font-bold">M</div>
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-gray-900 truncate">Mabelita</p>
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Super Admin</p>
                                </div>
                            </div>
                            <Button variant="ghost" className="w-full text-red-500 hover:bg-red-50 hover:text-red-600 text-xs font-black rounded-xl py-3" onClick={() => supabase.auth.signOut()}>
                                <LogOut size={16} className="mr-2" /> Cerrar Sesión
                            </Button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <main className="lg:ml-64 pt-20 lg:pt-8 pb-8 px-4 lg:px-10 min-h-screen">
                {activeView === 'overview' && renderOverview()}
                {activeView === 'products' && renderProducts()}
                {activeView === 'orders' && renderPedidos()}
                {activeView === 'customers' && renderClientes()}
                {activeView === 'stripe' && renderStripeDashboard()}
                {activeView === 'theme' && renderThemeStudio()}
                {activeView === 'graphic-studio' && renderGraphicStudio()}
                {activeView === 'la-chacra' && renderLaChacra()}
                {activeView === 'media' && renderMediaGallery()}
                {activeView === 'monitor' && renderApiMonitor()}
                {activeView === 'tunantada-calendar' && renderTunantadaCalendar()}
            </main>

            {/* GALLERY MODAL FOR SELECTING IMAGES */}
            {showGalleryModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in">
                    <div className="bg-white w-full max-w-5xl h-[80vh] rounded-2xl flex flex-col overflow-hidden shadow-2xl">
                        <div className="p-4 border-b flex justify-between items-center">
                            <h3 className="font-bold text-lg">Seleccionar Imagen</h3>
                            <button onClick={() => setShowGalleryModal(false)}><X size={24} /></button>
                        </div>
                        <div className="p-4 bg-gray-50 border-b">
                            {renderBucketSelector()}
                            <div className="mt-4">
                                <label className="flex items-center justify-center w-full h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-white transition-colors">
                                    <div className="flex items-center gap-2 text-gray-500">
                                        <Upload size={20} /> <span className="text-sm font-bold">Subir nueva imagen aquí</span>
                                    </div>
                                    <input type="file" multiple className="hidden" onChange={handleBulkUpload} disabled={isSaving} accept="image/*" />
                                </label>
                            </div>
                        </div>
                        {/* Modal Breadcrumbs */}
                        {breadcrumb.length > 0 && (
                            <div className="bg-gray-100 border-b px-4 py-2 flex items-center gap-2 overflow-x-auto text-[11px] font-medium custom-scrollbar">
                                <button
                                    onClick={() => setActiveFolder('')}
                                    className="flex items-center gap-1 text-gray-500 hover:text-indigo-600 transition-colors shrink-0"
                                >
                                    <HomeIcon size={12} /> Raíz
                                </button>
                                {breadcrumb.map((crumb, idx) => (
                                    <React.Fragment key={idx}>
                                        <ChevronRight size={12} className="text-gray-400 shrink-0" />
                                        <button
                                            onClick={() => setActiveFolder(crumb.path)}
                                            className={`hover:text-indigo-600 transition-colors truncate max-w-[120px] ${idx === breadcrumb.length - 1 ? 'text-gray-900 font-bold pointer-events-none' : 'text-gray-500'}`}
                                        >
                                            {crumb.name}
                                        </button>
                                    </React.Fragment>
                                ))}
                            </div>
                        )}

                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-gray-50">
                            {/* Renderizar Subcarpetas en el Modal */}
                            {subFolders.length > 0 && (
                                <div className="mb-6">
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 ml-1">Carpetas</h4>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                        {subFolders.map((folder, idx) => (
                                            <div
                                                key={`modal-folder-${idx}`}
                                                className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow hover:bg-blue-50/50 hover:border-blue-300 transition-all flex flex-col items-center justify-center p-4 cursor-pointer"
                                                onClick={() => setActiveFolder(activeFolder ? `${activeFolder}/${folder}` : folder)}
                                            >
                                                <Folder className="text-blue-500 w-10 h-10 mb-2 fill-blue-50 group-hover:fill-blue-100 transition-colors" strokeWidth={1.5} />
                                                <h5 className="text-xs font-bold text-gray-700 text-center w-full truncate" title={folder}>{folder}</h5>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Renderizar Archivos en el Modal */}
                            {galleryImages.length > 0 && (
                                <div>
                                    {subFolders.length > 0 && <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 ml-1">Archivos</h4>}
                                    <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                        {galleryImages.map((img, idx) => (
                                            <div key={idx} onClick={() => selectImageFromGallery(img.url)} className="aspect-square relative group cursor-pointer border border-gray-200 rounded-xl overflow-hidden hover:border-blue-500 hover:ring-2 ring-blue-500/50 transition-all bg-white shadow-sm flex flex-col items-center justify-center">
                                                <img src={img.url} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" alt={img.name} />
                                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <p className="text-white text-[10px] truncate font-medium">{img.name}</p>
                                                    <p className="text-gray-300 text-[9px] font-mono">{formatBytes(img.size, 0)}</p>
                                                </div>
                                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-blue-500 text-white rounded-full p-1.5 shadow-lg shadow-blue-500/30">
                                                    <Check size={14} className="stroke-[3]" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {galleryImages.length === 0 && subFolders.length === 0 && (
                                <div className="text-center py-20 bg-white rounded-xl border-2 border-dashed border-gray-200">
                                    <FolderOpen className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                                    <p className="text-gray-500">La carpeta <strong>{activeBucket}{activeFolder ? `/${activeFolder}` : ''}</strong> está vacía.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {renderPreviewModal()}
        </div>
    );
};